import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import PersonIcon from '@mui/icons-material/Person';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Tabs,
  Tab,
  Chip,
  Grid,
  Alert,
  Stack,
  Divider,
  InputAdornment,
  CircularProgress,
  FormHelperText,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
  Avatar,
  Menu,
  useTheme,
  useMediaQuery,
  Drawer,
  Autocomplete,
  Collapse,
  alpha,
  Snackbar,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";

import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useLocation } from "react-router-dom";
import apiClient from "../services/apiClient";
import clinicalNoteService from "../services/clinicalNoteService";
import carePlanService from "../services/carePlanService";
import residentService from "../services/residentService";
import masterDataService from "../services/masterDataService";
import { MedicationRoute } from "../types/medication.types";
import vitalsService from "../services/vitalsService";
import appointmentService from "../services/appointmentService";
import OrderMedicationService from "../services/OrderMedicationService";
import { useTenant } from "../contexts/TenantContext";
import activityTimelineService, { ActivityTimelineEvent } from "../services/activityTimelineService";
import { ResidentDocumentDTO, ResidentStatus, ResidentDTO, InsuranceCard, CareType, CareLevel } from "../types/resident.types";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import DeleteIcon from "@mui/icons-material/Delete";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EditIcon from "@mui/icons-material/Edit";
import PrintIcon from "@mui/icons-material/Print";
import CloseIcon from "@mui/icons-material/Close";
import BlockIcon from "@mui/icons-material/Block";
import SettingsBackupRestoreIcon from "@mui/icons-material/SettingsBackupRestore";
import InfoIcon from "@mui/icons-material/Info";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import WarningIcon from '@mui/icons-material/Warning';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import StraightenIcon from "@mui/icons-material/Straighten";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MonitorWeightIcon from "@mui/icons-material/MonitorWeight";
import CalculateIcon from "@mui/icons-material/Calculate";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import TimelineIcon from "@mui/icons-material/Timeline";
import AssignmentIcon from "@mui/icons-material/Assignment";
import LocalPharmacyIcon from "@mui/icons-material/LocalPharmacy";
import EventIcon from "@mui/icons-material/Event";
import DescriptionIcon from "@mui/icons-material/Description";
import HistoryIcon from "@mui/icons-material/History";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import BusinessIcon from "@mui/icons-material/Business";
import LocationIcon from "@mui/icons-material/LocationOn";
import ContactPhoneIcon from "@mui/icons-material/ContactPhone";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import MedicationIcon from "@mui/icons-material/Medication";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import DailyActivitiesDropdown from "../components/DailyActivitiesDropdown";
import DietaryRestrictionsDropdown from "../components/DietaryRestrictionsDropdown";
import AmbulationDropdown from "../components/AmbulationDropdown";
import WoundCareDropdown from "../components/WoundCareDropdown";
import MailIcon from "@mui/icons-material/Mail";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ClearIcon from "@mui/icons-material/Clear";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import BloodPressureSvgIcon from "../components/icons/BloodPressureSvgIcon";
import WeightSvgIcon from "../components/icons/WeightSvgIcon";
import BmiSvgIcon from "../components/icons/BmiSvgIcon";
import BloodGroupSvgIcon from "../components/icons/BloodGroupSvgIcon";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { format, parseISO } from "date-fns";
import { formatToUIDate, formatToUIDateTime, formatToBackendDateTime, formatToUIDateTimeLong, parseApiDateTime } from "../utils/dateUtils";
import { useClinicalNotes } from "../hooks/useClinicalNotes";

import { useAuth } from "../contexts/AuthContext";
import { useSpeechToText } from "../hooks/useSpeechToText";
import {
  ClinicalNoteType,
  CreateClinicalNoteRequest,
} from "../types/clinicalNote.types";
import { MedicationOrderDTO } from "../types/medication.types";
import ResidentProgressNoteForm from "../components/ResidentProgressNoteForm";
import ProgressNotesSection from "../components/ProgressNotesSection";
import VitalsManager from "../components/VitalsManager";
import MedicationAdministrationRecord from "../components/MedicationAdministrationRecord";
import ResidentAvatar from "../components/ResidentAvatar";
import SignatureInput from "../components/SignatureInput";

const clinicalNoteSchema = yup.object({
  noteType: yup.string().required("Note type is required"),
  content: yup
    .string()
    .required("Note content is required")
    .min(10, "Note must be at least 10 characters"),
  noteDate: yup.date().required("Date and time are required").nullable(),
});

type FormData = yup.InferType<typeof clinicalNoteSchema>;

type Medication = {
  id: string;
  med: string;
  dose: string;
  route: string;
  start: string;
  end?: string | null;
  schedule: string;
  timeSlot?: string;
  timeOfDay?: string;
  time?: string;
  residentId?: string;
  createdBy?: string;
  createdOn?: string;
  createdByName?: string;
  updatedBy?: string;
  updatedOn?: string;
  updatedByName?: string;
  note?: string;
  discontinued?: boolean;
  isNarcotic?: boolean;
  isCritical?: boolean;
  narcoticStorageLocation?: string;
};

// Valid medication route values
const VALID_ROUTES = [
  "PO", "SL", "BUCCAL", "NG_GT", "JT", "PR",
  "IV", "IM", "SC", "ID", "IO", "IT", "IA", "IP",
  "TOP", "TD", "INTRADERM", "WOUND", "OTIC", "OPHTH",
  "INH", "NEB", "NASAL",
  "VAG", "URETHRAL", "RECTAL",
  "EPIDURAL", "INTRACARDIAC", "INTRAOCULAR", "INTRAPLEURAL", "INTRAVESICAL",
];

const ROUTE_MAPPING: Record<string, string> = {
  ORAL: "PO",
  "ORAL (PO)": "PO",
  PO: "PO",
  SL: "SL",
  SUBLINGUAL: "SL",
  BUCCAL: "BUCCAL",
  IV: "IV",
  INTRAVENOUS: "IV",
  IM: "IM",
  INTRAMUSCULAR: "IM",
  SC: "SC",
  SQ: "SC",
  SUBCUTANEOUS: "SC",
  TOP: "TOP",
  TOPICAL: "TOP",
  TD: "TD",
  TRANSDERMAL: "TD",
  NASAL: "NASAL",
  OTIC: "OTIC",
  OPHTH: "OPHTH",
  OPHTHALMIC: "OPHTH",
  RECTAL: "PR",
  PR: "PR",
  INH: "INH",
  INHALATION: "INH",
  NEB: "NEB",
  NEBULIZER: "NEB",
};

const normalizeMedicationRoute = (routeStr?: string): MedicationRoute | "" => {
  if (!routeStr) return "";
  const normalized = routeStr.trim().toUpperCase();
  if (!normalized) return "";

  // Prefer route code shown inside parentheses, e.g. "Oral (PO)".
  const parenMatch = normalized.match(/\(([^)]+)\)/);
  if (parenMatch?.[1]) {
    const parenCode = parenMatch[1].trim().toUpperCase();
    if (VALID_ROUTES.includes(parenCode)) return parenCode as MedicationRoute;
    if (ROUTE_MAPPING[parenCode]) return ROUTE_MAPPING[parenCode] as MedicationRoute;
  }

  if (VALID_ROUTES.includes(normalized)) return normalized as MedicationRoute;
  if (ROUTE_MAPPING[normalized]) return ROUTE_MAPPING[normalized] as MedicationRoute;

  // Fallback: split labels like "SUBCUTANEOUS / SC" and try each token.
  const tokens = normalized.split(/[^A-Z0-9_]+/).filter(Boolean);
  for (const token of tokens) {
    if (VALID_ROUTES.includes(token)) return token as MedicationRoute;
    if (ROUTE_MAPPING[token]) return ROUTE_MAPPING[token] as MedicationRoute;
  }

  return "";
};


const medicationItemSchema = yup.object({
  medicationName: yup.string().required("Medication name is required"),
  dosage: yup.string().required("Dosage is required"),
  route: yup
    .string()
    .transform((value) => normalizeMedicationRoute(value))
    .required("Route is required")
    .oneOf(VALID_ROUTES, "Invalid route selected"),
  frequency: yup.string().required("Frequency is required"),
  startDate: yup
    .date()
    .required("Medication start date is required")
    .nullable(),
  endDate: yup.date().nullable(),
  medicationExpirationDate: yup.date().nullable(),
  note: yup.string().nullable(),
});

const orderSchema = yup.object({
  residentId: yup.string().required("Resident is required"),
  prescribingPhysician: yup
    .string()
    .required("Prescribing physician is required"),
  pharmacyName: yup.string(),
  pharmacyEmail: yup
    .string()
    .email("Invalid email")
    .required("Pharmacy email is required"),
  pharmacyContactNumber: yup
    .string()
    .required("Pharmacy contact number is required")
    .length(10, "Phone number must be exactly 10 digits")
    .matches(/^[0-9]+$/, "Phone number must contain only digits"),
  prescriptionNumber: yup
    .string()
    .test('is-number', 'Only numbers are allowed', value => !value || /^[0-9]*$/.test(value))
    .max(10, "Prescription number cannot exceed 10 characters")
    .nullable(),
  medicationItems: yup
    .array()
    .of(medicationItemSchema)
    .min(1, "At least one medication is required"),
  prescriptionFile: yup
    .mixed<File>()
    .nullable()
    .test(
      "fileSize",
      "Prescription file size cannot exceed 5MB.",
      (value: any) => {
        if (!value) return true;
        return value.size <= 5 * 1024 * 1024;
      }
    ),
  nextRefillDate: yup.date().nullable(),
});

type OrderFormData = yup.InferType<typeof orderSchema>;

interface MedicationEntry {
  medicationName: string;
  dosage: string;
  route: MedicationRoute | "";
  frequency: string;
  startDate: Date | null;
  endDate: Date | null;
  medicationExpirationDate: Date | null;
  note: string;
}

const standardFrequencies = [
  "Once daily",
  "Twice daily",
  "Three times daily",
  "Four times daily",
  "Every 4 hours",
  "Every 6 hours",
  "Every 8 hours",
  "As needed PRN",
  "At bedtime",
  "Evening time",

  "Other",
];

const DOSE_UNIT_OPTIONS = [
  { value: "mg", label: "mg" },
  { value: "ml", label: "mL" },
  { value: "ug", label: "\u00b5g" }, // microgram
  { value: "g", label: "g" },
  { value: "l", label: "L" },
  { value: "iu", label: "IU" },
  { value: "units", label: "Units" },
  { value: "drops", label: "drops" },
  { value: "mg/ml", label: "mg/mL" },
  { value: "mcg/ml", label: "mcg/mL" },
  { value: "%", label: "%" },
  { value: "tablet", label: "Tablet" },
  { value: "capsule", label: "Capsule" },
  { value: "puff", label: "Puff" },
];

type DoseUnitValue = (typeof DOSE_UNIT_OPTIONS)[number]["value"];

const splitDoseAndUnit = (
  dose?: string,
): { value: string; unit: DoseUnitValue | "" } => {
  const rawDose = (dose || "").trim();
  if (!rawDose) return { value: "", unit: "" };

  // Matches:
  //  - "100 mg", "100mg"
  //  - "5 mg/mL", "5mg/mL"
  //  - "50%"
  //  - "2 ug", "2\u00b5g"
  const match = rawDose.match(
    /^(.+?)\s*(mcg\s*\/\s*ml|mg\s*\/\s*ml|mcg|ug|\u00b5g|units|unit|drops|drop|iu|ml|mg|g|l|%|puff|tablet|tab|capsule|cap)$/i,
  );

  if (!match) return { value: rawDose, unit: "" };

  const value = match[1].trim();
  const unitRaw = match[2].trim().toLowerCase();

  let normalizedUnit = unitRaw
    .replace(/\u00b5/g, "u")
    .replace(/\s+/g, "");

  // Map variations to standard keys
  if (normalizedUnit.startsWith("mg/")) return { value, unit: "mg/ml" };
  if (normalizedUnit.startsWith("mcg/")) return { value, unit: "mcg/ml" };
  if (normalizedUnit === "ug" || normalizedUnit === "mcg") return { value, unit: "mcg" }; // backend often uses mcg
  if (normalizedUnit === "ml") return { value, unit: "ml" };
  if (normalizedUnit === "mg") return { value, unit: "mg" };
  if (normalizedUnit === "g") return { value, unit: "g" };
  if (normalizedUnit === "l") return { value, unit: "l" };
  if (normalizedUnit === "iu") return { value, unit: "iu" };
  if (normalizedUnit === "units" || normalizedUnit === "unit") return { value, unit: "units" };
  if (normalizedUnit === "drops" || normalizedUnit === "drop") return { value, unit: "drops" };
  if (normalizedUnit === "%") return { value, unit: "%" };
  if (normalizedUnit === "tablet" || normalizedUnit === "tab") return { value, unit: "tablet" };
  if (normalizedUnit === "capsule" || normalizedUnit === "cap") return { value, unit: "capsule" };
  if (normalizedUnit === "puff") return { value, unit: "puff" };

  return { value, unit: "" };
};

const formatDoseWithUnit = (
  value: string,
  unit: DoseUnitValue | string,
): string => {
  const doseValue = (value || "").trim();
  const doseUnitNormalized = (unit || "").trim().toLowerCase().replace(/\u00b5/g, "u");
  if (!doseValue) return "";
  if (!doseUnitNormalized) return doseValue;

  // Use ASCII-friendly units for payload/storage. (We still show `\u00b5g` in the UI.)
  const unitPayload =
    doseUnitNormalized === "l"
      ? "L"
      : doseUnitNormalized === "ml"
        ? "mL"
        : doseUnitNormalized === "iu"
          ? "IU"
          : doseUnitNormalized === "mg/ml"
            ? "mg/mL"
            : doseUnitNormalized === "mcg/ml"
              ? "mcg/mL"
              : doseUnitNormalized === "%"
                ? "%"
                : doseUnitNormalized;

  if (unitPayload === "%") return `${doseValue}%`;
  return `${doseValue} ${unitPayload}`;
};

const formatDoseForDisplay = (rawDose?: string): string => {
  const parsed = splitDoseAndUnit(rawDose);
  if (!parsed.value) return rawDose || "";
  if (!parsed.unit) return rawDose || parsed.value;

  const unitOption = DOSE_UNIT_OPTIONS.find((u) => u.value === parsed.unit);
  const unitLabel = unitOption?.label || parsed.unit;
  if (parsed.unit === "%") return `${parsed.value}%`;
  return `${parsed.value} ${unitLabel}`;
};

const getDoseUnitInputSuffix = (unit?: string): string => {
  if (!unit) return "";
  const unitOption = DOSE_UNIT_OPTIONS.find((u) => u.value === unit);
  if (!unitOption) return unit;
  return unitOption.label;
};

type ActivityType = "NOTE" | "VITAL" | "MAR" | "APPOINTMENT" | "DOCUMENT" | "FACESHEET" | "PRESCRIBED_MEDICATION" | "CARE_PLAN" | "PHARMACY_ORDER";

interface Activity {
  id: string;
  type: ActivityType;
  title: string;
  description: string;
  timestamp: string;
  metadata?: any;
  createdBy?: string;
  color?: string;
  heading?: string;
  entityName?: string;
}

const ClinicalNotesPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const { tenant } = useTenant();

  // Core state
  // clinical notes come from hook (persistent)
  const {
    clinicalNotes,
    loading: notesLoading,
    error: notesError,
    createClinicalNote,
    updateClinicalNote,
    deleteClinicalNote: deleteClinicalNoteApi,
    fetchClinicalNotes,
    setClinicalNotes,
    fetchClinicalNotesByResident,
  } = useClinicalNotes({ autoFetch: false });
  const location = useLocation();
  const { user } = useAuth();

  const [topTab, setTopTab] = useState(0);
  const [emrTab, setEmrTab] = useState(0);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedResident, setSelectedResident] = useState<
    string | number | null
  >(() => (location.state as any)?.residentId || null);
  const [prefilledResident, setPrefilledResident] = useState<any>(() => (location.state as any)?.patientDetails || null);
  const effectiveResidentId = useMemo(() => {
    const id = selectedResident !== null &&
      selectedResident !== undefined &&
      selectedResident !== ""
      ? String(selectedResident)
      : prefilledResident?.id
        ? String(prefilledResident.id)
        : null;
    return id ? String(id) : null;
  }, [selectedResident, prefilledResident?.id]);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [fullResidentData, setFullResidentData] = useState<ResidentDTO | null>(null);
  const [insuranceCards, setInsuranceCards] = useState<InsuranceCard[]>([]);
  const [isInsuranceDialogOpen, setIsInsuranceDialogOpen] = useState(false);
  const [editingInsurance, setEditingInsurance] = useState<InsuranceCard | null>(null);
  const [expandedInsurances, setExpandedInsurances] = useState<Set<string>>(new Set());
  const [insuranceErrors, setInsuranceErrors] = useState<{ [key: string]: string }>({});
  const [deleteInsurance, setDeleteInsurance] = useState<InsuranceCard | null>(null);
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [signatureOrderId, setSignatureOrderId] = useState<string | null>(null);
  const [signingLoading, setSigningLoading] = useState(false);
  const [insuranceForm, setInsuranceForm] = useState<InsuranceCard>({
    insuranceCompanyName: "",
    insuranceType: "PRIVATE",
    payerId: "",
    insurancePhone: "",
    insuranceEmail: "",
    subscriberName: "",
    subscriberRelationship: "",
    subscriberDob: "",
    subscriberGender: "",
    subscriberPhone: "",
    subscriberAddress: "",
    subscriberEmployerName: "",
    subscriberEmployerAddress: "",
    policyMemberId: "",
    groupNumber: "",
    planName: "",
    planType: "HMO",
    coverageType: "",
    effectiveDate: "",
    terminationDate: "",
    authorizationNumber: "",
    copayAmount: "",
    coinsurancePercentage: "",
    annualDeductible: "",
    remainingDeductible: "",
    outOfPocketLimit: "",
  });
  const [isFacesheetEditing, setIsFacesheetEditing] = useState(false);
  const [facesheetErrors, setFacesheetErrors] = useState<{ [key: string]: string }>({});
  // const [showSSN, setShowSSN] = useState(false);
  const [facesheetForm, setFacesheetForm] = useState<Partial<ResidentDTO>>({});
  const [genderOptions, setGenderOptions] = useState<{ valueName: string }[]>([]);
  const [insuranceTypeOptions, setInsuranceTypeOptions] = useState<{ valueName: string; orderNumber: number }[]>([]);
  const [planTypeOptions, setPlanTypeOptions] = useState<{ valueName: string; orderNumber: number }[]>([]);
  const [coverageTypeOptions, setCoverageTypeOptions] = useState<{ valueName: string; orderNumber: number }[]>([]);
  const [statusOptions, setStatusOptions] = useState<{ valueName: string; orderNumber: number }[]>([]);
  const [admissionTypeOptions, setAdmissionTypeOptions] = useState<{ valueName: string; orderNumber: number }[]>([]);
  const [bloodGroupOptions, setBloodGroupOptions] = useState<{ valueName: string; orderNumber: number }[]>([]);
  const [dnrStatusOptions, setDnrStatusOptions] = useState<{ valueName: string; orderNumber: number }[]>([]);
  const [subscriberRelationshipOptions, setSubscriberRelationshipOptions] = useState<{ valueName: string; orderNumber: number }[]>([]);
  const [unitOptions, setUnitOptions] = useState<{ id: string; valueName: string; orderNumber: number }[]>([]);
  const [routeOptions, setRouteOptions] = useState<{ id: string; valueName: string; orderNumber: number }[]>([]);
  const [scheduleOptions, setScheduleOptions] = useState<{ id: string; valueName: string; orderNumber: number }[]>([]);
  const [appointmentTypeOptions, setAppointmentTypeOptions] = useState<{ id: string; valueName: string; orderNumber: number }[]>([]);
  const [mobilityTypeOptions, setMobilityTypeOptions] = useState<{ id: string; valueName: string; orderNumber: number }[]>([]);
  const [statusConfirmOpen, setStatusConfirmOpen] = useState(false);
  const [pendingStatusUpdate, setPendingStatusUpdate] = useState<string | null>(null);
  const [statusReason, setStatusReason] = useState("");

  const formatStatusDisplay = (str?: string | null) => {
    if (!str) return "Active";
    const cleaned = str.replace(/_/g, " ").trim();
    if (!cleaned) return "Active";
    return cleaned
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Fetch status options when a resident is selected (needed for banner status dropdown)
  useEffect(() => {
    if (!effectiveResidentId) return;
    const loadStatusLookups = async () => {
      try {
        if (statusOptions.length === 0) {
          const sOptions = await masterDataService.getActiveLookupValuesByName('Residentpatientstatus');
          let filteredStatusOptions = sOptions;
          const userRole = String(user?.role || "").toUpperCase().replace(/^ROLE_/, "");
          if (userRole === "CAREGIVER" || userRole === "LEAD_CAREGIVER" || userRole === "NURSE") {
            const allowedStatuses = ["active", "hospital appointment", "hospital_appointment", "with family", "with_family", "transferred"];
            filteredStatusOptions = sOptions.filter(opt => allowedStatuses.includes((opt.valueName || "").toLowerCase()));
          }
          setStatusOptions(filteredStatusOptions);
        }
      } catch (err) {
        console.error('Failed to load status lookup options', err);
      }
    };
    loadStatusLookups();
  }, [effectiveResidentId, statusOptions.length, user?.role]);

  // Fetch Facesheet specific options when Facesheet editing is enabled
  useEffect(() => {
    if (!isFacesheetEditing) return;
    const loadFacesheetLookups = async () => {
      try {
        const fetches: Promise<any>[] = [];
        if (genderOptions.length === 0) {
          fetches.push(masterDataService.getActiveLookupValuesByName('gender').then(setGenderOptions));
        }
        if (admissionTypeOptions.length === 0) {
          fetches.push(masterDataService.getActiveLookupValuesByName('admissionType').then(setAdmissionTypeOptions));
        }
        if (bloodGroupOptions.length === 0) {
          fetches.push(masterDataService.getActiveLookupValuesByName('bloodGroup').then(setBloodGroupOptions));
        }
        if (dnrStatusOptions.length === 0) {
          fetches.push(masterDataService.getActiveLookupValuesByName('DNRStatus').then(setDnrStatusOptions));
        }
        if (fetches.length > 0) await Promise.all(fetches);
      } catch (err) {
        console.error('Failed to load Facesheet lookup options', err);
      }
    };
    loadFacesheetLookups();
  }, [isFacesheetEditing, genderOptions.length, admissionTypeOptions.length, bloodGroupOptions.length, dnrStatusOptions.length]);

  // Fetch Insurance specific options when Insurance dialog is opened
  useEffect(() => {
    if (!isInsuranceDialogOpen) return;
    const loadInsuranceLookups = async () => {
      try {
        const fetches: Promise<any>[] = [];
        if (planTypeOptions.length === 0) {
          fetches.push(masterDataService.getActiveLookupValuesByName('planType').then(setPlanTypeOptions));
        }
        if (coverageTypeOptions.length === 0) {
          fetches.push(masterDataService.getActiveLookupValuesByName('coverageType').then(setCoverageTypeOptions));
        }
        if (insuranceTypeOptions.length === 0) {
          fetches.push(masterDataService.getActiveLookupValuesByName('insuranceType').then(setInsuranceTypeOptions));
        }
        if (subscriberRelationshipOptions.length === 0) {
          fetches.push(masterDataService.getActiveLookupValuesByName('subscriberRelationship').then(setSubscriberRelationshipOptions));
        }
        if (genderOptions.length === 0) {
          fetches.push(masterDataService.getActiveLookupValuesByName('gender').then(setGenderOptions));
        }
        if (fetches.length > 0) await Promise.all(fetches);
      } catch (err) {
        console.error('Failed to load Insurance lookup options', err);
      }
    };
    loadInsuranceLookups();
  }, [isInsuranceDialogOpen, planTypeOptions.length, coverageTypeOptions.length, insuranceTypeOptions.length, subscriberRelationshipOptions.length, genderOptions.length]);

  // Fetch unit, route & schedule options lazily when Prescribe Medication tab (topTab === 1) is opened
  useEffect(() => {
    if (topTab !== 1 && topTab !== 6) return;
    const loadPrescribeLookups = async () => {
      try {
        const fetches: Promise<any>[] = [];
        if (unitOptions.length === 0) {
          fetches.push(
            masterDataService.getActiveLookupValuesByName('Unit').then(setUnitOptions)
          );
        }
        if (routeOptions.length === 0) {
          fetches.push(
            masterDataService.getActiveLookupValuesByName('Route').then(setRouteOptions)
          );
        }
        if (scheduleOptions.length === 0) {
          fetches.push(
            masterDataService.getActiveLookupValuesByName('Schedule').then(setScheduleOptions)
          );
        }
        await Promise.all(fetches);
      } catch (err) {
        console.error('Failed to load prescribe medication lookup options', err);
      }
    };
    loadPrescribeLookups();
  }, [topTab]);

  // Fetch appointment type & mobility type options lazily when Medical Appointment tab (topTab === 4) is opened
  useEffect(() => {
    if (topTab !== 4) return;
    const loadAppointmentLookups = async () => {
      try {
        if (appointmentTypeOptions.length === 0) {
          const options = await masterDataService.getActiveLookupValuesByName('AppointmentType');
          setAppointmentTypeOptions(options);
        }
        if (mobilityTypeOptions.length === 0) {
          const options = await masterDataService.getActiveLookupValuesByName('MobilityTypes');
          setMobilityTypeOptions(options);
        }
      } catch (err) {
        console.error('Failed to load appointment/mobility lookup options', err);
      }
    };
    loadAppointmentLookups();
  }, [topTab, appointmentTypeOptions.length, mobilityTypeOptions.length]);

  // Resident search state
  const [residentSearchQuery, setResidentSearchQuery] = useState("");
  const [residentSearchResults, setResidentSearchResults] = useState<any[]>([]);
  const [isSearchingResidents, setIsSearchingResidents] = useState(false);
  const [residentSearchOpen, setResidentSearchOpen] = useState(false);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);


  // Determine the effective resident ID from all possible sources
  // 1. selectedResident state (user explicitly selected in dropdown/search if implemented)
  // 2. location.state.residentId (navigation from residents list)
  // 3. prefilledResident.id (derived from location state patient details)
  const [residents, setResidents] = useState<any[]>([]);
  const [residentsLoading, setResidentsLoading] = useState(false);

  // Fetch all active residents for the facility to use in local search/filtering
  useEffect(() => {
    if (!tenant?.id) return;
    const loadResidents = async () => {
      setResidentsLoading(true);
      try {
        const data = await residentService.getActiveLookup(tenant.id);
        setResidents(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Failed to load active residents lookup', err);
      } finally {
        setResidentsLoading(false);
      }
    };
    loadResidents();
  }, [tenant?.id]);


  // Utility functions
  const getResident = useCallback((id: number | string | null) =>
    residents.find((r) => String(r.id) === String(id)) || null, [residents]);

  const getUserDisplay = useCallback((userId?: string | null) => {
    if (!userId) return "-";

    if (
      user &&
      (user.id === userId ||
        user.username === userId ||
        (user.id &&
          userId &&
          String(user.id).toLowerCase() === String(userId).toLowerCase()))
    ) {
      return user.name || user.username;
    }
    return userId;
  }, [user]);

  const isResidentActive = useMemo(() => {
    const res = fullResidentData || prefilledResident;
    if (!res) return false;
    const rawStatus = res.status || res.residentStatus || res.statusText || (res.isActive ? "ACTIVE" : "INACTIVE");
    return String(rawStatus).toUpperCase().replace(/ /g, "_") === "ACTIVE";
  }, [fullResidentData, prefilledResident]);

  const isCaregiver = useMemo(() => {
    const role = (user?.role || "").toString().toUpperCase().replace(/^ROLE_/, "");
    return role === "CAREGIVER" || role === "LEAD_CAREGIVER" || role === "NURSE";
  }, [user?.role]);

  const isOnlyCaregiver = useMemo(() => {
    const role = (user?.role || "").toString().toUpperCase().replace(/^ROLE_/, "");
    return role === "CAREGIVER" || role === "LEAD_CAREGIVER" || role === "NURSE";
  }, [user?.role]);

  const isStrictlyCaregiver = useMemo(() => {
    const role = (user?.role || "").toString().toUpperCase().replace(/^ROLE_/, "");
    return role === "CAREGIVER" || role === "LEAD_CAREGIVER" || role === "NURSE";
  }, [user?.role]);


  const residentBanner = useMemo(() =>
    prefilledResident ||
    (selectedResident ? getResident(Number(selectedResident)) : null),
    [prefilledResident, selectedResident, residents]);

  // Daily Progress Notes (Clinical Notes with noteType === PROGRESS)
  const dailyProgressNotes = (clinicalNotes || [])
    .filter((n) => n?.noteType === ClinicalNoteType.PROGRESS)
    .sort((a: any, b: any) => {
      const at = a?.noteDate ? new Date(a.noteDate).getTime() : 0;
      const bt = b?.noteDate ? new Date(b.noteDate).getTime() : 0;
      return bt - at;
    });

  // MAR Header state
  const [marHeader, setMarHeader] = useState({
    physician: "",
    medicalRecordNo: "",
    admissionDate: "",
    dob: "",
    allergies: "" as string,
  });

  // Medication state
  const [medications, setMedications] = useState<Medication[]>([]);
  const [isLoadingMedications, setIsLoadingMedications] = useState(false);
  const [medForm, setMedForm] = useState({
    id: "",
    med: "",
    dose: "",
    doseUnit: "",
    route: "",
    start: new Date() as Date | null,
    end: null as Date | null,
    schedule: "",
    timeSlot: "" as string, // For "Once Daily" schedule time selection
    note: "",
    discontinued: false,
    isNarcotic: false,
    isCritical: false,
    narcoticStorageLocation: "",
  });
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [isSavingMedication, setIsSavingMedication] = useState(false);
  const [medicationError, setMedicationError] = useState<string | null>(null);
  const [openDiscontinueDialog, setOpenDiscontinueDialog] = useState(false);
  const [discontinueDialogMode, setDiscontinueDialogMode] = useState<
    "DISCONTINUE" | "UNDO"
  >("DISCONTINUE");
  const [openMedicationUpdateChoice, setOpenMedicationUpdateChoice] = useState(false);

  // Nurse entries state
  const [nurseEntries, setNurseEntries] = useState<NurseEntry[]>([]);
  const [nurseForm, setNurseForm] = useState({
    noteDate: new Date() as Date | null,
    medication: "",
    dosage: "",
    reason: "",
    results: "",
    initials: "",
    lowStock: false,
    schedule: "",
  });

  // State variables already declared below at lines 290+
  // Removing duplicates introduced in previous step

  const [appointments, setAppointments] = useState<any[]>([]);
  const [printingId, setPrintingId] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [appointmentForm, setAppointmentForm] = useState({
    dateTime: null as Date | null,
    doctor: "",
    hospitalName: "",
    type: "",
    reason: "",
    includeInBilling: false,
    isTransportationScheduled: false,
    mobilityType: "",
    vendor: "",
    transportContactNumber: "",
  });

  useEffect(() => {
    if (fullResidentData && topTab === 4) {
      setAppointmentForm((prev) => ({
        ...prev,
        hospitalName: prev.hospitalName || fullResidentData.hospitalName || "",
        doctor: prev.doctor || fullResidentData.doctorName || "",
      }));
    }
  }, [fullResidentData, topTab]);

  // Daily Progress Note (MAR tab) form state
  const [dailyProgressNoteDateTime, setDailyProgressNoteDateTime] =
    useState<Date | null>(new Date());
  const [dailyProgressNoteText, setDailyProgressNoteText] = useState("");
  const [dailyProgressNoteSaving, setDailyProgressNoteSaving] = useState(false);
  const [dailyProgressNoteError, setDailyProgressNoteError] = useState<
    string | null
  >(null);
  const [appointmentError, setAppointmentError] = useState<string | null>(null);
  const [appointmentInfo, setAppointmentInfo] = useState<string | null>(null);
  const [appointmentSuccess, setAppointmentSuccess] = useState<string | null>(
    null,
  );
  const [isSavingAppointment, setIsSavingAppointment] = useState(false);

  // Pharmacy Tab state
  const { currentTenantId } = useTenant();
  const [pharmacyOrders, setPharmacyOrders] = useState<MedicationOrderDTO[]>([]);
  const [isPharmacyLoading, setIsPharmacyLoading] = useState(false);
  const [viewingOrderId, setViewingOrderId] = useState<string | null>(null);
  const [viewingOrder, setViewingOrder] = useState<MedicationOrderDTO | null>(
    null,
  );
  const [openOrderDialog, setOpenOrderDialog] = useState(false);
  const [residentMasterMedications, setResidentMasterMedications] = useState<any[]>([]);
  const [newMedItem, setNewMedItem] = useState<MedicationEntry>({
    medicationName: "",
    dosage: "",
    route: "",
    frequency: "",
    startDate: new Date(),
    endDate: null,
    medicationExpirationDate: null,
    note: "",
  });
  const [isSavingOrder, setIsSavingOrder] = useState(false);
  const [orderError, setOrderError] = useState<string | null>(null);
  const [orderSuccess, setOrderSuccess] = useState<string | null>(null);
  const [newMedItemErrors, setNewMedItemErrors] = useState<{
    medicationName?: string;
    dosage?: string;
    frequency?: string;
    route?: string;
  }>({});


  const {
    control: orderControl,
    handleSubmit: handleOrderSubmit,
    reset: resetOrder,
    setValue: setOrderValue,
    watch: watchOrder,
    formState: { errors: orderErrors },
  } = useForm<OrderFormData>({
    resolver: yupResolver(orderSchema),
    defaultValues: {
      residentId: "",
      prescribingPhysician: "",
      pharmacyName: "",
      pharmacyEmail: "",
      pharmacyContactNumber: "",
      prescriptionNumber: "",
      medicationItems: [],
      prescriptionFile: null,
    },
  });

  const { fields: orderFields, append: appendOrder, remove: removeOrder } = useFieldArray({
    control: orderControl,
    name: "medicationItems",
  });
  const [isFetchingOrderDetails, setIsFetchingOrderDetails] = useState(false);
  const localFileUrlsRef = useRef<Record<string, { url: string; type?: string }>>({});

  // Utility functions
  const [editingAppointmentId, setEditingAppointmentId] = useState<
    string | null
  >(null);

  // Restore local file URLs from sessionStorage on mount
  useEffect(() => {
    try {
      const stored = sessionStorage.getItem("prescriptionFileUrls");
      if (stored) {
        const parsed = JSON.parse(stored);
        Object.entries(parsed).forEach(([orderId, fileData]: [string, any]) => {
          if (fileData && fileData.mimeType && fileData.dataUrl) {
            // Recreate object URL from the stored data URL
            fetch(fileData.dataUrl)
              .then((res) => res.blob())
              .then((blob) => {
                const url = window.URL.createObjectURL(blob);
                localFileUrlsRef.current[orderId] = {
                  url,
                  type: fileData.mimeType,
                };
              })
              .catch((err) =>
                console.error("Failed to restore file URL:", err),
              );
          }
        });
      }
    } catch (err) {
      console.error(
        "Error restoring prescription files from sessionStorage:",
        err,
      );
    }
  }, []);
  // 1. Nurse Notes Interface
  interface NurseEntry {
    id: number;
    apiId?: string;
    noteDate: Date | null;
    medication: string;
    dosage: string;
    reason: string;
    results: string;
    initials: string;
    schedule: string;
    lowStock?: boolean;
    residentId?: string;
    createdBy?: string;
    createdOn?: string;
    createdByName?: string;
    updatedBy?: string;
    updatedOn?: string;
    updatedByName?: string;
  }

  // 2. Appointment Interface
  interface Appointment {
    id: string;
    appointmentDate: string | null;
    appointmentTime: string | null;
    doctor: string;
    hospitalName?: string;
    type: string;
    reason: string;
    residentId?: string;
    userId?: string;
    // additional backend fields
    doctorName?: string;
    doctorId?: string;
    appointmentType?: string;
    createdBy?: string;
    createdOn?: string;
    createdByName?: string;
    updatedBy?: string;
    updatedOn?: string;
    updatedByName?: string;
    includeInBilling?: boolean;
    isTransportationScheduled?: boolean;
    mobilityType?: string;
    vendor?: string;
    transportContactNumber?: string;
  }

  // 3. Vitals Interface (assuming structure based on usage, or update existing if found)
  interface VitalSign {
    id: string;
    residentId: string;
    vitalDate: string;
    bloodPressure: string;
    weight: string;
    temperature: string;
    pulseRate: string;
    respirationRate: string;
    oxygenSaturation: string;
    painLevel: string;
    consciousnessLevel: string;
    mealPortionConsumed: string;
    bowelMovement: string;
    createdBy?: string;
    createdOn?: string;
    createdByName?: string;
    updatedBy?: string;
    updatedOn?: string;
    updatedByName?: string;
  }
  const [nurseConfirmationOpen, setNurseConfirmationOpen] = useState(false);
  const [nurseEntryError, setNurseEntryError] = useState<string | null>(null);
  const [nurseEntrySuccess, setNurseEntrySuccess] = useState<string | null>(
    null,
  );
  const [editingNurseIndex, setEditingNurseIndex] = useState<number | null>(
    null,
  );
  const [isSubmittingNurseEntry, setIsSubmittingNurseEntry] = useState(false);
  const [carePlanTab, setCarePlanTab] = useState(0);
  const [dailyMedTab, setDailyMedTab] = useState(0);
  const [marRefreshTrigger, setMarRefreshTrigger] = useState(0);
  const [deleteDocId, setDeleteDocId] = useState<string | null>(null);
  const [isDeletingDoc, setIsDeletingDoc] = useState(false);

  // Today's Vitals state
  const [todaysVitals, setTodaysVitals] = useState({
    vitalDate: null as Date | null,
    bloodPressure: "",
    weight: "",
    temperature: "",
    pulseRate: "",
    respirationRate: "",
    oxygenSaturation: "",
    painLevel: "",
    consciousnessLevel: "",
    mealPortionConsumed: "",
    bowelMovement: "",
    medicationCompliance: [] as Array<{
      medication: string;
      scheduledTime: string;
      actualTime: string;
      status: "Taken" | "Missed" | "In Progress";
      notes: string;
    }>,
  });
  const [isSavingVitals, setIsSavingVitals] = useState(false);
  const [vitalsError, setVitalsError] = useState<string | null>(null);
  const [vitalsSuccess, setVitalsSuccess] = useState<string | null>(null);

  // Care Plan state
  // We'll store/load drafts per-resident using keys that include the resident id
  const [carePlanData, setCarePlanData] = useState(() => {
    return {
      diagnosticsPlan: "",
      dailyActivities: "",
      ambulation: "",
      woundCare: "",
    };
  });
  const [existingCarePlanId, setExistingCarePlanId] = useState<string | null>(
    null,
  );
  const [isSavingCarePlan, setIsSavingCarePlan] = useState(false);
  const [carePlanError, setCarePlanError] = useState<string | null>(null);
  const [carePlanSuccess, setCarePlanSuccess] = useState<string | null>(null);
  const [carePlanMetadata, setCarePlanMetadata] = useState<{
    createdAt?: string;
    updatedAt?: string;
    createdBy?: string;
    updatedBy?: string;
  } | null>(null);

  // Speech-to-Text Setup
  const [listeningField, setListeningField] = useState<string | null>(null);
  const activeSpeechFieldRef = React.useRef<keyof typeof carePlanData | null>(null);
  const initialCarePlanTextRef = React.useRef<string>("");

  const { isListening, startListening, stopListening, isSupported } = useSpeechToText({
    interimResults: true,
    continuous: true,
    onResult: (spokenText) => {
      const field = activeSpeechFieldRef.current;
      if (field) {
        const base = initialCarePlanTextRef.current.trim();
        const cleanedSpoken = spokenText.trim();
        const newVal = base
          ? (cleanedSpoken ? `${base} ${cleanedSpoken}` : base)
          : cleanedSpoken;
        setCarePlanData((prev) => ({ ...prev, [field]: newVal }));
      }
    },
    onEnd: () => {
      setListeningField(null);
      activeSpeechFieldRef.current = null;
      initialCarePlanTextRef.current = "";
    },
  });

  const handleMicClick = (fieldName: keyof typeof carePlanData) => {
    if (listeningField === fieldName) {
      stopListening();
      setListeningField(null);
      activeSpeechFieldRef.current = null;
      initialCarePlanTextRef.current = "";
      return;
    }
    const currentVal = carePlanData[fieldName] || "";
    initialCarePlanTextRef.current = currentVal;
    activeSpeechFieldRef.current = fieldName;
    setListeningField(fieldName);
    startListening();
  };

  // Activity Timeline API State
  const [activityTimelineData, setActivityTimelineData] = useState<ActivityTimelineEvent[]>([]);
  const [isTimelineLoading, setIsTimelineLoading] = useState(false);
  const [isTimelineExpanded, setIsTimelineExpanded] = useState(false);

  // Vitals State
  const [vitalsHistory, setVitalsHistory] = useState<any[]>([]);
  const [editingVitalId, setEditingVitalId] = useState<string | null>(null);

  // Documents State
  const [activeDocuments, setActiveDocuments] = useState<ResidentDocumentDTO[]>(
    [],
  );
  const [isUploading, setIsUploading] = useState(false);
  const [isFetchingDocs, setIsFetchingDocs] = useState(false);
  const [documentsError, setDocumentsError] = useState<string | null>(null);
  const [documentsSuccess, setDocumentsSuccess] = useState<string | null>(null);

  // Documents Tab Upload State
  const [docsTabName, setDocsTabName] = useState("");
  const [docsTabFile, setDocsTabFile] = useState<File | null>(null);

  // Status Change State
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  // Photo Upload State
  const [isPhotoUploading, setIsPhotoUploading] = useState(false);
  const [isRemovePhotoDialogOpen, setIsRemovePhotoDialogOpen] = useState(false);
  const [photoRefreshTrigger, setPhotoRefreshTrigger] = useState(0);
  const [hasProfilePhoto, setHasProfilePhoto] = useState(false);
  const photoFileInputRef = useRef<HTMLInputElement>(null);
  const [profileMenuAnchor, setProfileMenuAnchor] = useState<null | HTMLElement>(null);

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setProfileMenuAnchor(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileMenuAnchor(null);
  };

  const handleRemovePhoto = () => {
    handleProfileMenuClose();
    setIsRemovePhotoDialogOpen(true);
  };

  const confirmRemovePhoto = async () => {
    if (!effectiveResidentId) return;

    setIsPhotoUploading(true);
    try {
      await residentService.deleteProfileImage(String(effectiveResidentId));
      setPhotoRefreshTrigger((prev) => prev + 1);
      // Re-fetch resident data to get updated photo URL (null)
      const data = await residentService.getResidentById(
        String(effectiveResidentId),
      );
      setFullResidentData(data);
      setHasProfilePhoto(false);
      setVitalsSuccess("Profile removed successfully");
      setTimeout(() => setVitalsSuccess(null), 3000);
    } catch (error) {
      console.error("Failed to remove profile", error);
      setVitalsError("Failed to remove profile");
      setTimeout(() => setVitalsError(null), 3000);
    } finally {
      setIsPhotoUploading(false);
      setIsRemovePhotoDialogOpen(false);
    }
  };

  // Form hook
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<FormData>({
    resolver: yupResolver(clinicalNoteSchema),
  });

  const fetchDocuments = async (residentId: string) => {
    setIsFetchingDocs(true);
    setDocumentsError(null);
    try {
      const docs = await residentService.getResidentDocuments(residentId);
      // Populate createdBy field if missing but uploadedBy is present
      const populatedDocs = docs.map((doc) => {

        return doc;
      });
      // Sort documents by creation date in descending order (newest first)
      populatedDocs.sort((a, b) => {
        const dateA = new Date(a.createdAt || a.uploadedAt || 0).getTime();
        const dateB = new Date(b.createdAt || b.uploadedAt || 0).getTime();
        return dateB - dateA;
      });
      setActiveDocuments(populatedDocs);
    } catch (error) {
      console.error("Failed to fetch documents", error);
      setDocumentsError("Unable to load documents for this resident.");
    } finally {
      setIsFetchingDocs(false);
    }
  };

  const handleDeleteDocument = (docId: string) => {
    setDeleteDocId(docId);
  };

  const confirmDeleteDocument = async () => {
    if (!effectiveResidentId || !deleteDocId) return;

    setIsDeletingDoc(true);
    try {
      await residentService.deleteDocument(String(effectiveResidentId), deleteDocId);
      await fetchDocuments(String(effectiveResidentId));
      fetchActivityTimeline();
      setVitalsSuccess("Document deleted successfully");
      setTimeout(() => setVitalsSuccess(null), 3000);
      setDeleteDocId(null);
    } catch (error) {
      console.error("Delete failed", error);
      setVitalsError("Failed to delete document");
      setTimeout(() => setVitalsError(null), 3000);
    } finally {
      setIsDeletingDoc(false);
    }
  };

  const handleDocsFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setDocsTabFile(e.target.files[0]);
    }
  };

  const handleAddDocsDocument = async () => {
    if (!docsTabFile || !docsTabName || !effectiveResidentId) return;

    // Client-side validation: prevent uploading files larger than 10MB
    if (docsTabFile.size > 10 * 1024 * 1024) {
      setDocumentsError("File size exceeds 10 MB limit");
      setTimeout(() => setDocumentsError(null), 10000);
      return;
    }

    setIsUploading(true);
    setDocumentsError(null);
    setDocumentsSuccess(null);

    const formData = new FormData();
    formData.append("file", docsTabFile);
    formData.append("documentName", docsTabName);
    formData.append("documentType", "Clinical");
    // formData.append("fileType", docsTabFile.type);

    // Add metadata
    const currentUserName = user ? user.name || user.username : "Current User";
    const now = new Date().toISOString();

    formData.append("createdBy", currentUserName);
    formData.append("created_by", currentUserName);
    formData.append("author", currentUserName);
    formData.append("createdAt", now);

    try {
      await residentService.uploadDocument(
        String(effectiveResidentId),
        formData,
      );
      await fetchDocuments(String(effectiveResidentId));

      // Manually update the latest document with current user and time if missing
      // This is a client-side fix to ensure immediate feedback as requested
      const currentUserName = user
        ? user.name || user.username
        : "Current User";
      const now = new Date().toISOString();

      setActiveDocuments((prev) => {
        const updated = prev.map((d) => {
          // Check if this is the document we just uploaded.
          // We check by name (trimmed) or if the document was created very recently (< 5 mins) and has no creator.
          const isNameMatch =
            d.documentName === docsTabName.trim() ||
            d.fileName === docsTabName.trim();
          const docDate = d.createdAt ? new Date(d.createdAt) : new Date();
          const isRecent =
            new Date().getTime() - docDate.getTime() < 5 * 60 * 1000; // 5 mins window

          if ((isNameMatch || (isRecent && !d.createdBy)) && !d.createdBy) {
            return { ...d, createdBy: currentUserName, uploadedBy: user?.id };
          }
          return d;
        });
        return updated;
      });

      setDocumentsSuccess("Document uploaded successfully!");
      setDocsTabName("");
      setDocsTabFile(null);
      fetchActivityTimeline();
      setTimeout(() => setDocumentsSuccess(null), 3000);
    } catch (error: any) {
      console.error("Upload failed", error);
      const errorMessage = error?.response?.data?.message || "Failed to upload document";
      setDocumentsError(errorMessage);
    } finally {
      setIsUploading(false);
    }
  };

  // Clinical note submission handler

  const handlePhotoClick = () => {
    handleProfileMenuClose();
    if (photoFileInputRef.current) {
      photoFileInputRef.current.click();
    }
  };

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0] && effectiveResidentId) {
      const file = e.target.files[0];
      setIsPhotoUploading(true);
      try {
        await residentService.uploadProfileImage(String(effectiveResidentId), file);
        // Re-fetch resident data to get updated photo URL
        const data = await residentService.getResidentById(String(effectiveResidentId));
        setFullResidentData(data);
        setHasProfilePhoto(true);

        // Increment trigger to refresh ResidentAvatar components
        setPhotoRefreshTrigger((prev) => prev + 1);

        setVitalsSuccess("Photo updated successfully");
        fetchActivityTimeline();
        setTimeout(() => setVitalsSuccess(null), 3000);
      } catch (error: any) {
        console.error('Error uploading photo:', error);
        const errorMessage = error.response?.data?.message || error.message || 'Failed to upload photo';
        setVitalsError(`${errorMessage}. Please try again.`);
        setTimeout(() => setVitalsError(null), 3000);
      } finally {
        setIsPhotoUploading(false);
        if (e.target) {
          e.target.value = '';
        }
      }
    }
  };

  // Clinical note submission handler
  const onSubmit = async (data: FormData) => {
    try {
      // Determine resident id from current selection / prefilled details / navigation state
      if (!effectiveResidentId) {
        window.alert("Please select a resident before saving a clinical note.");
        return;
      }
      const residentIdValue = effectiveResidentId;

      if (!residentIdValue) {
        window.alert("Please select a resident before saving a clinical note.");
        return;
      }

      const payload: CreateClinicalNoteRequest = {
        residentId: String(residentIdValue),
        noteType: data.noteType as ClinicalNoteType,
        subject: "Quick Note",
        content: data.content,
        noteDate: formatToBackendDateTime(data.noteDate as Date) || formatToBackendDateTime(new Date()) || "",
      };

      if (editingNoteId) {
        await updateClinicalNote(editingNoteId, payload as any);
        setEditingNoteId(null);
        reset();
      } else {
        await createClinicalNote(payload);
        reset();
      }
      fetchActivityTimeline();
    } catch (error) {
      console.error("Failed to save note", error);
    }
  };

  const handleEditNote = (note: any) => {
    setEditingNoteId(note.id);
    setValue("noteType", note.noteType);
    setValue("noteDate", note.noteDate ? new Date(note.noteDate) : null);
    setValue("content", note.content);
    // ensure UI scrolls to form if needed
    window.scrollTo({ top: 0, behavior: "smooth" });
  };




  const handleCancelEdit = () => {
    setEditingNoteId(null);
    reset();
  };

  const handleDeleteNote = (noteId?: string | null) => {
    if (!noteId) {
      console.error("Missing note id for delete");
      return;
    }
    if (!window.confirm("Are you sure you want to delete this note?")) return;
    // call API via hook
    deleteClinicalNoteApi(noteId)
      .then(() => fetchActivityTimeline())
      .catch((e) =>
        console.error("Delete failed", e),
      );
  };

  const handleChatAction = (action: string, payload: any) => {
    switch (action) {
      case "ADD_VITALS":
        setTopTab(2);
        setEmrTab(0);
        break;
      case "BOOK_APPOINTMENT":
        setTopTab(4);
        break;
      case "GIVE_MEDICATION":
        setTopTab(2);
        setEmrTab(2);
        setDailyMedTab(1); // Open MAR
        break;
      case "ADD_NOTES":
        setTopTab(2);
        setEmrTab(1);
        break;
      default:
        console.log("Unknown action:", action, payload);
    }
  };

  const handleEditAppointment = (appt: any) => {

    setEditingAppointmentId(appt.id);
    let dateTime: Date | null = null;
    if (appt.appointmentDate) {
      const [y, m, d] = appt.appointmentDate.split("-").map(Number);
      dateTime = new Date(y, m - 1, d);
      if (appt.appointmentTime) {
        const [hh, mm] = appt.appointmentTime.split(":").map(Number);
        dateTime.setHours(hh, mm, 0, 0);
      }
    }
    setAppointmentForm({
      dateTime: dateTime,
      doctor: appt.doctorName || appt.userId || appt.doctorId || "",
      hospitalName: appt.hospitalName || appt.hospital_name || "",
      type: appt.appointmentType,
      reason: appt.reason,
      includeInBilling: appt.includeInBilling || false,
      isTransportationScheduled: appt.isTransportationScheduled || false,
      mobilityType: appt.mobilityType || "",
      vendor: appt.vendor || "",
      transportContactNumber: appt.transportContactNumber || "",
    });
  };

  const handleCancelAppointmentEdit = () => {
    setEditingAppointmentId(null);
    setAppointmentForm({
      dateTime: null,
      doctor: "",
      hospitalName: "",
      type: "",
      reason: "",
      includeInBilling: false,
      isTransportationScheduled: false,
      mobilityType: "",
      vendor: "",
      transportContactNumber: "",
    });
  };

  // Filter notes by active tab and by resident ID if selected
  const filterByResident = (notes: any[]) => {
    if (!effectiveResidentId) {
      return notes;
    }
    const residentIdToMatch = effectiveResidentId;

    if (!residentIdToMatch) {
      return notes;
    }

    return notes.filter((note) => {
      const noteResidentId = note.residentId ? String(note.residentId) : null;
      return noteResidentId === residentIdToMatch;
    });
  };

  const baseFilteredNotes =
    activeTab === 0
      ? clinicalNotes
      : activeTab === 1
        ? clinicalNotes.filter((note) => note.noteType === "PROGRESS")
        : activeTab === 2
          ? clinicalNotes.filter((note) => note.noteType === "NURSE")
          : clinicalNotes.filter(
            (note) =>
              note.noteType === "SOCIAL_WORK" ||
              note.noteType === "SPIRITUAL_CARE",
          );

  // Apply resident filter
  const filteredNotes = filterByResident(baseFilteredNotes);


  // Effects
  useEffect(() => {
    const state: any = location.state;
    if (state) {
      if (state.residentId) {
        setSelectedResident(state.residentId);
      }
      if (state.view === "medication") {
        setTopTab(0);
        setEmrTab(2);
      }
      if (state.view === "nurse-notes") {
        setTopTab(2);
        setEmrTab(2);
        setDailyMedTab(0);
      }
      if (state.view === "mar-grid") {
        setTopTab(2);
        setEmrTab(2);
        setDailyMedTab(1);
      }
      if (state.view === "vitals") {
        setTopTab(2);
        setEmrTab(0);
      }
      if (state.view === "facesheet") {
        setTopTab(0);
      }
      if (state.view === "medical-appointment") {
        setTopTab(4);
      }
      if (state.patientDetails) {
        setPrefilledResident(state.patientDetails);
      }
    }
  }, [location.state, setValue]);

  // Fetch for Documents (now topTab 5)
  useEffect(() => {
    if (effectiveResidentId && topTab === 5) {
      // Clear old docs while switching between residents
      setActiveDocuments([]);
      fetchDocuments(String(effectiveResidentId));
    }
  }, [effectiveResidentId, topTab === 5]);





  // Fetch full resident data and insurances when resident changes
  const lastFetchedResidentRef = useRef<string | null>(null);

  useEffect(() => {
    const fetchFullData = async () => {
      if (!effectiveResidentId || effectiveResidentId === lastFetchedResidentRef.current) return;

      // Clear previous data
      setFullResidentData(null);
      setInsuranceCards([]);
      setHasProfilePhoto(false);

      if (!effectiveResidentId) return;

      try {
        const data = await residentService.getResidentById(String(effectiveResidentId));
        setFullResidentData(data);

        // Check for profile picture existence robustly
        if (data.profilePictureUrl) {
          setHasProfilePhoto(true);
        } else {
          try {
            const blob = await residentService.getProfilePicture(String(effectiveResidentId));
            setHasProfilePhoto(blob && blob.size > 0);
          } catch (e) {
            setHasProfilePhoto(false);
          }
        }

        // Fetch insurances separately
        try {
          const insurances = await residentService.getResidentInsurances(String(effectiveResidentId));
          setInsuranceCards(insurances);
        } catch (e) {
          console.error("Failed to fetch insurances separately", e);
          // Fallback to what's in ResidentDTO
          setInsuranceCards(data.insuranceCards || []);
        }
      } catch (error) {
        console.error("Failed to fetch full resident data", error);
      }
    };
    fetchFullData();
    lastFetchedResidentRef.current = effectiveResidentId;
  }, [effectiveResidentId]);

  // Fetch clinical notes for selected resident when it changes
  const lastNotesResidentRef = useRef<string | null>(null);
  useEffect(() => {
    // Clear clinical notes immediately to prevent showing previous resident's notes
    if (setClinicalNotes) {
      setClinicalNotes([]);
    }

    if (effectiveResidentId && effectiveResidentId !== lastNotesResidentRef.current) {
      fetchClinicalNotesByResident(effectiveResidentId).then(() => {
        lastNotesResidentRef.current = effectiveResidentId;
      }).catch((e) =>
        console.error("Failed to fetch clinical notes for resident", e),
      );
    } else if (!effectiveResidentId) {
      lastNotesResidentRef.current = null;
    }
  }, [effectiveResidentId, fetchClinicalNotesByResident, setClinicalNotes]);

  // Refresh clinical notes when opening Daily Progress Note tab so Resident List popup saves show up.
  useEffect(() => {
    if (topTab !== 2) return;
    if (emrTab !== 1) return;
    if (!effectiveResidentId) return;
    fetchClinicalNotesByResident(effectiveResidentId).catch((e) =>
      console.error("Failed to refresh clinical notes for resident", e),
    );
    setDailyProgressNoteDateTime(new Date());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topTab, emrTab, effectiveResidentId]);

  const handleSaveDailyProgressNote = async () => {
    if (!effectiveResidentId) {
      setDailyProgressNoteError("Please select a resident first.");
      return;
    }
    if (!dailyProgressNoteDateTime) {
      setDailyProgressNoteError("Please select a date and time.");
      return;
    }
    if (!dailyProgressNoteText.trim()) {
      setDailyProgressNoteError("Please enter a note.");
      return;
    }

    setDailyProgressNoteSaving(true);
    setDailyProgressNoteError(null);
    try {
      const payload: CreateClinicalNoteRequest = {
        residentId: String(effectiveResidentId),
        noteType: ClinicalNoteType.PROGRESS,
        subject: "Progress Note",
        content: dailyProgressNoteText.trim(),
        noteDate: formatToBackendDateTime(dailyProgressNoteDateTime) || "",
      };
      await createClinicalNote(payload);
      await fetchClinicalNotesByResident(String(effectiveResidentId));
      fetchActivityTimeline();
      setDailyProgressNoteText("");
      setDailyProgressNoteDateTime(new Date());
    } catch (e: any) {
      console.error("Failed to save daily progress note", e);
      setDailyProgressNoteError(
        e?.response?.data?.message ||
        "Failed to save progress note. Please try again.",
      );
    } finally {
      setDailyProgressNoteSaving(false);
    }
  };

  useEffect(() => {
    const fetchMedications = async () => {
      setIsLoadingMedications(true);
      setMedications([]); // Clear previous data immediately
      try {
        // If a resident is selected, try to fetch medications filtered by residentId
        // Otherwise, fetch all and filter client-side
        let url = "/v1/EMR-medications";
        if (effectiveResidentId) {
          // Try to fetch with residentId query parameter
          url = `/v1/EMR-medications?residentId=${effectiveResidentId}`;
        }

        const response = await apiClient.get(url);
        // Handle potential different response shapes (array vs {content: []})
        const responseData = Array.isArray(response.data)
          ? response.data
          : response.data?.content || [];

        let apiMedications = responseData.map((med: any) => ({
          id: med.id,
          med: med.medicationName,
          dose: med.dosage,
          route: med.route,
          start: med.startDate,
          end: med.endDate,
          schedule: med.schedule,
          timeSlot: med.timeSlot || (med.timeOfDay ? getSlotFromTime(med.timeOfDay) : ""),
          residentId: med.residentId || med.resident_id,
          createdBy: med.created_by || med.createdBy,
          createdByName:
            med.createdByName ||
            med.creatorName ||
            med.created_by_name ||
            getUserDisplay(med.created_by || med.createdBy),
          createdOn: med.created_at || med.createdAt || med.createdDate,
          updatedBy: med.updated_by || med.updatedBy || med.lastModifiedBy,
          updatedByName:
            med.updatedByName ||
            med.updaterName ||
            med.updated_by_name ||
            getUserDisplay(
              med.updated_by || med.updatedBy || med.lastModifiedBy,
            ),
          updatedOn: med.updated_at || med.updatedAt || med.lastModifiedDate,
          note: med.note || med.description,
          discontinued: med.isDiscontinued || med.discontinued || false,
          isNarcotic: med.isNarcotic || false,
          isCritical: med.isCritical || med.is_critical || false,
          narcoticStorageLocation: med.narcoticStorageLocation || "",
        }));

        // Always filter client-side if a resident is selected, to ensure isolation
        if (effectiveResidentId) {
          const targetId = String(effectiveResidentId).trim();
          apiMedications = apiMedications.filter(
            (med: Medication) =>
              med.residentId && String(med.residentId).trim() === targetId,
          );
        }

        // Sort by creation date descending (newest first), then by start date
        apiMedications.sort((a: any, b: any) => {
          const dateA = new Date(
            a.createdOn || a.createdAt || a.created_at || a.start || 0,
          ).getTime();
          const dateB = new Date(
            b.createdOn || b.createdAt || b.created_at || b.start || 0,
          ).getTime();
          return dateB - dateA;
        });

        setMedications(apiMedications);
      } catch (error) {
        console.error("Failed to fetch medications", error);
        // Fallback: fetch all and filter client-side if a resident is selected
        if (effectiveResidentId) {
          try {
            const response = await apiClient.get("/v1/EMR-medications");
            const responseData = Array.isArray(response.data)
              ? response.data
              : response.data?.content || [];
            const allMedications = responseData.map((med: any) => ({
              id: med.id,
              med: med.medicationName,
              dose: med.dosage,
              route: med.route,
              start: med.startDate,
              end: med.endDate,
              schedule: med.schedule,
              timeSlot: med.timeSlot || (med.timeOfDay ? getSlotFromTime(med.timeOfDay) : ""),
              residentId: med.residentId || med.resident_id,
              createdBy: med.created_by || med.createdBy,
              createdOn: med.created_at || med.createdAt || med.createdDate,
              updatedBy: med.updated_by || med.updatedBy || med.lastModifiedBy,
              updatedByName:
                med.updatedByName || med.updaterName || med.updated_by_name,
              updatedOn:
                med.updated_at || med.updatedAt || med.lastModifiedDate,
              note: med.note || "",
              discontinued: med.discontinued || false,
              isNarcotic: med.isNarcotic || false,
              isCritical: med.isCritical || med.is_critical || false,
              narcoticStorageLocation: med.narcoticStorageLocation || "",
            }));

            const targetId = String(effectiveResidentId).trim();
            const filtered = allMedications.filter(
              (med: Medication) =>
                med.residentId && String(med.residentId).trim() === targetId,
            );
            setMedications(filtered);
          } catch (fallbackError) {
            console.error(
              "Failed to fetch medications (fallback)",
              fallbackError,
            );
            setMedications([]);
          }
        } else {
          setMedications([]);
        }
      } finally {
        setIsLoadingMedications(false);
      }
    };
    if (topTab === 1) {
      fetchMedications();
    }
  }, [effectiveResidentId, topTab]);

  // Refactored fetchNurseNotes into a useCallback for reusability
  const fetchNurseNotesData = React.useCallback(async () => {
    setNurseEntries([]); // Clear previous data immediately
    if (!effectiveResidentId) {
      return;
    }
    setMarRefreshTrigger(prev => prev + 1);
    try {
      const notes =
        await clinicalNoteService.getNurseNotesByResident(effectiveResidentId);
      let mappedNotes = notes.map((note: any, index: number) => {
        const creatorId = note.created_by || note.createdBy || note.creatorId;
        const updaterId =
          note.updated_by ||
          note.updatedBy ||
          note.lastModifiedBy ||
          note.updaterId;
        return {
          id: index,
          apiId: note.id,
          noteDate: note.dateTime ? new Date(note.dateTime) : null,
          medication: note.medicationName || "",
          dosage: note.dosage || "",
          reason: note.reason || "",
          schedule: note.schedule || "",
          results: note.result || "",
          initials: note.medicationStatus || note.initials || "",
          lowStock: note.lowStock,
          residentId: note.residentId,
          createdBy: creatorId,
          createdByName:
            note.createdByName ||
            note.creatorName ||
            note.created_by_name ||
            (creatorId ? getUserDisplay(creatorId) : "-"),
          createdOn: note.created_at || note.createdAt || note.createdDate,
          updatedBy: updaterId,
          updatedByName:
            note.updatedByName ||
            note.updaterName ||
            note.updated_by_name ||
            (updaterId ? getUserDisplay(updaterId) : "-"),
          updatedOn: note.updated_at || note.updatedAt || note.lastModifiedDate,
        };
      });

      // Filter by residentId to ensure isolation
      if (effectiveResidentId) {
        mappedNotes = mappedNotes.filter(
          (note: any) =>
            note.residentId && String(note.residentId) === effectiveResidentId,
        );
      }

      setNurseEntries(
        mappedNotes.sort((a: any, b: any) => {
          const dateA = a.noteDate ? new Date(a.noteDate).getTime() : 0;
          const dateB = b.noteDate ? new Date(b.noteDate).getTime() : 0;
          return dateB - dateA;
        }),
      );
    } catch (error) {
      console.error("Failed to fetch nurse notes", error);
      setNurseEntries([]);
    }
  }, [effectiveResidentId]);





  // Helper to build per-resident draft keys


  useEffect(() => {
    const fetchAppointments = async () => {
      setAppointments([]); // Clear previous data immediately

      // Wait for facilityId (from prefilledResident) before fetching to avoid 500 error
      const facilityId = prefilledResident?.facilityId;

      if (!effectiveResidentId || !facilityId || topTab !== 4) {
        return;
      }
      try {
        const data = await appointmentService.getAppointments(
          String(effectiveResidentId),
          facilityId,
        );
        // Ensure strictly filtered by residentId in case backend returns all
        const filteredData = Array.isArray(data)
          ? data
            .filter(
              (appt: any) =>
                appt.residentId &&
                String(appt.residentId) === String(effectiveResidentId),
            )
            .map((appt: any) => {
              const creatorId =
                appt.createdBy || appt.created_by || appt.authorId;
              const updaterId =
                appt.updatedBy || appt.updated_by || appt.lastModifiedBy;
              return {
                ...appt,
                createdByName:
                  appt.createdByName ||
                  appt.creatorName ||
                  (creatorId ? getUserDisplay(creatorId) : "-"),
                updatedByName:
                  appt.updatedByName ||
                  appt.updaterName ||
                  (updaterId ? getUserDisplay(updaterId) : "-"),
              };
            })
          : [];
        // Sort by date and time descending (newest first)
        filteredData.sort((a: any, b: any) => {
          const dateStrA = a.appointmentDate || "1970-01-01";
          const timeStrA = a.appointmentTime
            ? a.appointmentTime.length === 5
              ? `${a.appointmentTime}:00`
              : a.appointmentTime
            : "00:00:00";

          const dateStrB = b.appointmentDate || "1970-01-01";
          const timeStrB = b.appointmentTime
            ? b.appointmentTime.length === 5
              ? `${b.appointmentTime}:00`
              : b.appointmentTime
            : "00:00:00";

          const dateTimeA = new Date(`${dateStrA}T${timeStrA}`).getTime();
          const dateTimeB = new Date(`${dateStrB}T${timeStrB}`).getTime();

          return dateTimeB - dateTimeA;
        });

        setAppointments(filteredData);
      } catch (error) {
        console.error("Failed to fetch appointments", error);
        setAppointments([]);
      }
    };
    fetchAppointments();
  }, [effectiveResidentId, appointmentSuccess, prefilledResident, topTab]);


  // Load existing care plan when resident selection (effectiveResidentId) changes
  useEffect(() => {
    const fetchCarePlan = async () => {
      // If no resident selected, clear the fields and existing id
      if (!effectiveResidentId) {
        setCarePlanData({
          diagnosticsPlan: "",
          dailyActivities: "",
          ambulation: "",
          woundCare: "",
        });
        setExistingCarePlanId(null);
        return;
      }

      try {
        // First try to get care plan by resident ID (use effectiveResidentId)
        const response = await carePlanService.getCarePlanByResidentId(
          String(effectiveResidentId),
        );

        let carePlan: any = response;

        // Robustly extract the care plan object from various possible response shapes
        if (response && (response as any).data)
          carePlan = (response as any).data;
        else if (response && (response as any).result)
          carePlan = (response as any).result;

        // If it's an array, take the latest (last) entry
        if (Array.isArray(carePlan)) {
          carePlan = carePlan.length > 0 ? carePlan[carePlan.length - 1] : null;
        }

        if (carePlan && carePlan.id) {
          // If we have a care plan with ID, fetch it using GET by ID API to ensure we have latest
          try {
            const fetchedCarePlan = await carePlanService.getCarePlanById(
              carePlan.id,
            );
            const actualCP =
              fetchedCarePlan && (fetchedCarePlan as any).data
                ? (fetchedCarePlan as any).data
                : fetchedCarePlan;

            setCarePlanData({
              diagnosticsPlan: actualCP.diagnosticsPlan || "",
              dailyActivities: actualCP.dailyActivities || "",
              ambulation: actualCP.ambulation || "",
              woundCare: actualCP.woundCare || "",
            });
            setCarePlanMetadata({
              createdAt: actualCP.createdDate || actualCP.createdAt,
              updatedAt: actualCP.lastModifiedDate || actualCP.updatedAt,
              createdBy: actualCP.createdByName || actualCP.createdBy,
              updatedBy: actualCP.lastModifiedBy || actualCP.updatedBy,
            });
            setExistingCarePlanId(actualCP.id);
          } catch (fetchError) {
            // Fallback to the care plan we already have from the list/resident search
            setCarePlanData({
              diagnosticsPlan: carePlan.diagnosticsPlan || "",
              dailyActivities: carePlan.dailyActivities || "",
              ambulation: carePlan.ambulation || "",
              woundCare: carePlan.woundCare || "",
            });
            setCarePlanMetadata({
              createdAt: carePlan.createdDate || carePlan.createdAt,
              updatedAt: carePlan.lastModifiedDate || carePlan.updatedAt,
              createdBy: carePlan.createdByName || carePlan.createdBy,
              updatedBy: carePlan.lastModifiedBy || carePlan.updatedBy,
            });
            setExistingCarePlanId(carePlan.id);
          }
        } else {
          // No server care plan; clear fields
          setCarePlanData({
            diagnosticsPlan: "",
            dailyActivities: "",
            ambulation: "",
            woundCare: "",
          });
          setCarePlanMetadata(null);
          setExistingCarePlanId(null);
        }
      } catch (error) {
        // On error, clear fields
        setCarePlanData({
          diagnosticsPlan: "",
          dailyActivities: "",
          ambulation: "",
          woundCare: "",
        });
        setCarePlanMetadata(null);
        setExistingCarePlanId(null);
      }
    };
    if (topTab === 3) {
      fetchCarePlan();
    }
  }, [effectiveResidentId, topTab]);

  // Fetch Pharmacy Orders
  const fetchPharmacyOrders = async () => {
    setPharmacyOrders([]); // Clear previous
    if (!effectiveResidentId) {
      return;
    }
    setIsPharmacyLoading(true);
    try {
      const orders = await OrderMedicationService.getOrdersByResident(
        String(effectiveResidentId),
      );
      const ordersArray = Array.isArray(orders) ? orders : [];
      const sortedOrders = [...ordersArray].sort((a: any, b: any) => {
        const timeA = new Date(a.createdAt || a.orderedAt || 0).getTime();
        const timeB = new Date(b.createdAt || b.orderedAt || 0).getTime();
        return timeB - timeA;
      });
      setPharmacyOrders(sortedOrders);
    } catch (error) {
      console.error("Failed to fetch pharmacy orders", error);
      setPharmacyOrders([]);
    } finally {
      setIsPharmacyLoading(false);
    }
  };

  useEffect(() => {
    if (topTab === 6) {
      fetchPharmacyOrders();
    }
  }, [effectiveResidentId, topTab]);

  const fetchResidentMasterMedications = useCallback(
    async (residentId: string) => {
      try {
        const response = await apiClient.get(
          `/v1/EMR-medications?residentId=${residentId}`,
        );
        const responseData = Array.isArray(response.data)
          ? response.data
          : response.data?.content || [];

        const targetId = String(residentId).trim();
        const filtered = responseData.filter((med: any) => {
          const medResidentId = med.residentId || med.resident_id;
          return medResidentId && String(medResidentId).trim() === targetId;
        });

        setResidentMasterMedications(filtered);
      } catch (err) {
        console.error("Failed to fetch resident medications:", err);
        setResidentMasterMedications([]);
      }
    },
    [],
  );

  const handleOpenOrderDialog = () => {
    // Fetch lookup options if not already loaded
    if (scheduleOptions.length === 0 || routeOptions.length === 0) {
      const fetchOptions = async () => {
        try {
          const [schedules, routes] = await Promise.all([
            masterDataService.getActiveLookupValuesByName('medicationSchedule'),
            masterDataService.getActiveLookupValuesByName('medicationRoute')
          ]);
          setScheduleOptions(schedules || []);
          setRouteOptions(routes || []);
        } catch (err) {
          console.error('Failed to load lookup options', err);
        }
      };
      fetchOptions();
    }

    resetOrder({
      residentId: String(effectiveResidentId),
      prescribingPhysician: fullResidentData?.attendingPhysicianName || "",
      medicationItems: [],
      prescriptionFile: null,
      nextRefillDate: null,
    });
    setNewMedItem({
      medicationName: "",
      dosage: "",
      route: "",
      frequency: "",
      startDate: new Date(),
      endDate: null,
      medicationExpirationDate: null,
      note: "",
    });
    setNewMedItemErrors({});
    if (effectiveResidentId) {
      fetchResidentMasterMedications(String(effectiveResidentId));
    }
    setOpenOrderDialog(true);
  };

  const handleCloseOrderDialog = () => {
    setOpenOrderDialog(false);
    resetOrder();
    setOrderError(null);
    setOrderSuccess(null);
  };

  const formatLocalDateTime = (date?: Date | null) => {
    if (!date) return undefined;
    const pad = (value: number) => String(value).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  };

  const onSubmitOrder = async (data: OrderFormData) => {
    try {
      setOrderError(null);
      setOrderSuccess(null);
      setIsSavingOrder(true);

      const now = new Date();

      // Validate that all medications have valid routes (after normalization)
      const invalidMeds =
        data.medicationItems?.filter((item) => {
          const normalizedRoute = normalizeMedicationRoute(String(item.route || ""));
          return !normalizedRoute || !VALID_ROUTES.includes(normalizedRoute as string);
        }) || [];
      if (invalidMeds.length > 0) {
        setOrderError("One or more medications have invalid or missing routes. Please review and correct.");
        setIsSavingOrder(false);
        return;
      }

      const orderData = {
        residentId: data.residentId,
        prescribingPhysicianName: data.prescribingPhysician,
        pharmacyName: data.pharmacyName,
        pharmacyEmail: data.pharmacyEmail,
        pharmacyContactNumber: data.pharmacyContactNumber,
        prescriptionNumber: data.prescriptionNumber,
        endDate: formatLocalDateTime(data.nextRefillDate),
        medicationItems:
          data.medicationItems?.map((item) => {
            const normalizedRoute =
              normalizeMedicationRoute(String(item.route || "")) ||
              (item.route as MedicationRoute);

            return {
              medicationName: item.medicationName,
              dosage: item.dosage,
              route: normalizedRoute as MedicationRoute,
              frequency: item.frequency,
              startDate:
                formatLocalDateTime(item.startDate) || formatLocalDateTime(now),
              endDate: formatLocalDateTime(item.endDate),
              medicationExpirationDate: formatLocalDateTime(
                item.medicationExpirationDate,
              ),
              specialInstructions: `${item.frequency} via ${normalizedRoute}`,
              genericName: "",
              indication: "",
              notes: "",
              isPrn: false,
              quantity: "",
              refillsAuthorized: 0,
              residentId: data.residentId,
              tenantId: currentTenantId || undefined,
              facilityId: currentTenantId || undefined,
            };
          }) || [],
        facilityId: currentTenantId || undefined,
        tenantId: currentTenantId || undefined,
        orderedBy: user?.id,
        notes: "",
      };

      const formData = new FormData();
      formData.append(
        "order",
        new Blob([JSON.stringify(orderData)], { type: "application/json" }),
      );

      if (data.prescriptionFile) {
        formData.append("file", data.prescriptionFile);
      }

      const created = await OrderMedicationService.createOrder(formData);

      // If a top-level prescription file was uploaded, create a local object URL so it can be previewed immediately
      if (data.prescriptionFile && created && created.id) {
        try {
          const f = data.prescriptionFile as File;
          const url = window.URL.createObjectURL(f);
          localFileUrlsRef.current[created.id] = { url, type: f.type };

          // Also store in sessionStorage for persistence across page refreshes
          const reader = new FileReader();
          reader.onload = () => {
            try {
              if (created.id) {
                const stored = JSON.parse(
                  sessionStorage.getItem("prescriptionFileUrls") || "{}",
                );
                stored[created.id] = {
                  mimeType: f.type,
                  dataUrl: reader.result,
                };
                sessionStorage.setItem(
                  "prescriptionFileUrls",
                  JSON.stringify(stored),
                );
              }
            } catch (err) {
              console.error("Failed to store file in sessionStorage:", err);
            }
          };
          reader.readAsDataURL(f);
        } catch (e) {
          // ignore
        }
      }

      setOrderSuccess("Order created successfully!");
      handleCloseOrderDialog();
      await fetchPharmacyOrders();
    } catch (err: any) {
      setOrderError(err.message || "Failed to create medication order");
      console.error("Failed to create medication order:", err);
    } finally {
      setIsSavingOrder(false);
    }
  };

  const availableMedicationOptions = useMemo(() => {
    const allNames = Array.from(
      new Set(residentMasterMedications.map((m) => m.medicationName)),
    );
    const addedNames = (watchOrder("medicationItems") || [])
      .map((it: any) => it?.medicationName)
      .filter(Boolean);
    return allNames.filter((n) => !addedNames.includes(n));
  }, [residentMasterMedications, watchOrder("medicationItems")]);

  const handleAddOrderItem = () => {
    const rowErrors: any = {};
    if (!newMedItem.medicationName) rowErrors.medicationName = "Medication name is required";
    if (!newMedItem.dosage) rowErrors.dosage = "Dosage is required";
    if (!newMedItem.frequency) rowErrors.frequency = "Frequency is required";
    if (!newMedItem.route) rowErrors.route = "Route is required";

    if (Object.keys(rowErrors).length > 0) {
      setNewMedItemErrors(rowErrors);
      return;
    }

    const normalizedRoute =
      normalizeMedicationRoute(newMedItem.route as string) ||
      (newMedItem.route as MedicationRoute);

    appendOrder({
      ...newMedItem,
      route: normalizedRoute as MedicationRoute,
      endDate: newMedItem.endDate || null,
      medicationExpirationDate: newMedItem.medicationExpirationDate || null,
    });
    setNewMedItem({
      medicationName: "",
      dosage: "",
      route: "",
      frequency: "",
      startDate: new Date(),
      endDate: null,
      medicationExpirationDate: null,
      note: "",
    });
    setNewMedItemErrors({});
  };

  // Utility functions

  const calcAge = (dob: string) => {
    try {
      const birth = new Date(dob);
      const diff = Date.now() - birth.getTime();
      const ageDt = new Date(diff);
      return Math.abs(ageDt.getUTCFullYear() - 1970);
    } catch {
      return "";
    }
  };


  // Fetch Order Details for Pharmacy Tab
  useEffect(() => {
    const fetchOrderDetails = async () => {
      if (!viewingOrderId) {
        setViewingOrder(null);
        return;
      }

      setIsFetchingOrderDetails(true);
      try {
        const order = await OrderMedicationService.getOrderById(viewingOrderId);
        setViewingOrder(order);
      } catch (error) {
        console.error("Failed to fetch order details", error);
      } finally {
        setIsFetchingOrderDetails(false);
      }
    };

    fetchOrderDetails();
  }, [viewingOrderId]);

  const handleSendToPharmacy = async (id: string) => {
    setSignatureOrderId(id);
    setSignatureModalOpen(true);
  };

  const handleSignatureSave = async (signatureData: string) => {
    if (!signatureOrderId) return;
    try {
      setSigningLoading(true);
      await OrderMedicationService.sendToPharmacy(
        signatureOrderId,
        signatureData,
      );
      await fetchPharmacyOrders();
    } catch (err: any) {
      console.error("Failed to send to pharmacy:", err);
    } finally {
      setSigningLoading(false);
      setSignatureModalOpen(false);
      setSignatureOrderId(null);
    }
  };

  // Get resident name for display in clinical notes table
  const getResidentNameForNote = (note: any) => {
    // First, use the residentName from the note if available
    if (note.residentName) {
      return note.residentName;
    }

    // If note belongs to the currently selected/prefilled resident, use that name
    const noteResidentId = String(note.residentId);
    const currentResidentId = selectedResident
      ? String(selectedResident)
      : null;
    const prefilledId = prefilledResident
      ? String(prefilledResident.id || prefilledResident.residentId || "")
      : null;

    if (
      (currentResidentId && noteResidentId === currentResidentId) ||
      (prefilledId && noteResidentId === prefilledId)
    ) {
      // Use name from prefilledResident or residentBanner
      if (prefilledResident?.name) {
        return prefilledResident.name;
      }
      if (residentBanner?.name) {
        return residentBanner.name;
      }
    }

    // Fallback: try to find in mock residents array
    const mockResident = residents.find((r) => String(r.id) === noteResidentId);
    if (mockResident) {
      return mockResident.name;
    }

    // If nothing found, return empty or a placeholder
    return "";
  };

  // Activity Timeline Rendering Helper
  const renderActivityTimeline = () => {
    if (isTimelineLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={32} />
        </Box>
      );
    }

    if (aggregatedActivities.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <TimelineIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography variant="subtitle2" color="text.secondary">
            No activities recorded yet.
          </Typography>
          <Typography variant="caption" color="text.disabled">
            Real activities and reviews will appear here.
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ position: 'relative' }}>
        {/* Vertical Line */}
        <Box
          sx={{
            position: 'absolute',
            left: 17,
            top: 0,
            bottom: 0,
            width: '2px',
            bgcolor: 'grey.100',
            zIndex: 0
          }}
        />

        <Stack spacing={2.5} sx={{ position: 'relative', zIndex: 1 }}>
          {aggregatedActivities.map((activity) => (
            <Box key={activity.id} sx={{ display: 'flex', gap: 2 }}>
              {/* Icon Circle */}
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'white',
                  border: '2px solid',
                  borderColor: activity.color || getActivityColor(activity.type),
                  color: activity.color || getActivityColor(activity.type),
                  flexShrink: 0,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                  zIndex: 2
                }}
              >
                {getActivityIcon(activity.type)}
              </Box>

              {/* Content Card */}
              <Card
                variant="outlined"
                sx={{
                  flexGrow: 1,
                  borderLeft: '4px solid',
                  borderLeftColor: activity.color || getActivityColor(activity.type),
                  bgcolor: 'background.paper',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    transform: 'translateY(-1px)'
                  },
                  transition: 'all 0.2s',
                  borderRadius: 1.5
                }}
              >
                <Box sx={{ p: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                    <Box>
                      <Typography variant="subtitle2" fontWeight="700" color="text.primary" sx={{ lineHeight: 1.2 }}>
                        {activity.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.2 }}>
                        <HistoryIcon sx={{ fontSize: 12 }} />
                        {formatToUIDateTimeLong(activity.timestamp)}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={(activity.entityName === "MAR" || activity.type === "MAR") ? "Medication Status" : (activity.entityName || activity.type)}
                      sx={{
                        height: 18,
                        fontSize: '0.6rem',
                        fontWeight: 'bold',
                        textTransform: 'uppercase',
                        bgcolor: `${activity.color || getActivityColor(activity.type)}10`,
                        color: activity.color || getActivityColor(activity.type),
                        border: 'none'
                      }}
                    />
                  </Box>

                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      lineHeight: 1.4,
                      fontStyle: activity.type === 'NOTE' ? 'italic' : 'normal'
                    }}
                  >
                    {activity.description}
                  </Typography>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, pt: 0.8, borderTop: '1px solid', borderColor: 'grey.50' }}>
                    <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
                      By: <strong>{activity.createdBy || 'System'}</strong>
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Box>
          ))}
        </Stack>
      </Box>
    );
  };

  // Activity Timeline Aggregation
  const aggregatedActivities = useMemo(() => {
    if (!effectiveResidentId) return [];

    // STRICT MODE: Only show what comes from the Activity Timeline API
    // as requested by the user to fix mapping discrepancies.
    // This ensures only the exact number of records from the backend are displayed.
    return (activityTimelineData || [])
      .filter(event => {
        // Robust resident ID check on the raw item if available
        const rId = event.raw?.residentId || event.raw?.resident_id || event.raw?.patientId;
        return !rId || String(rId) === String(effectiveResidentId);
      })
      .map(event => ({
        id: event.id,
        type: event.type as any,
        title: event.heading || event.title,
        description: event.description,
        timestamp: event.timestamp,
        createdBy: event.createdBy,
        metadata: event.raw,
        color: event.color,
        heading: event.heading,
        entityName: event.raw?.entityName || event.raw?.EntityName,
      }))
      .sort((a, b) => {
        const tA = parseApiDateTime(a.timestamp)?.getTime() ?? 0;
        const tB = parseApiDateTime(b.timestamp)?.getTime() ?? 0;
        return tB - tA; // Newest first
      });
  }, [activityTimelineData, effectiveResidentId]);

  // Fetch Activity Timeline from API
  const fetchActivityTimeline = React.useCallback(async () => {
    if (!effectiveResidentId) {
      setActivityTimelineData([]);
      return;
    }

    setIsTimelineLoading(true);
    try {
      const data = await activityTimelineService.getResidentTimeline(String(effectiveResidentId));
      setActivityTimelineData(data || []);
    } catch (error) {
      console.error("Failed to fetch activity timeline", error);
      setActivityTimelineData([]);
    } finally {
      setIsTimelineLoading(false);
    }
  }, [effectiveResidentId]);

  const lastTimelineResidentRef = useRef<string | null>(null);
  useEffect(() => {
    if (effectiveResidentId && effectiveResidentId !== lastTimelineResidentRef.current) {
      fetchActivityTimeline();
      lastTimelineResidentRef.current = effectiveResidentId;
    } else if (!effectiveResidentId) {
      lastTimelineResidentRef.current = null;
    }
  }, [effectiveResidentId, fetchActivityTimeline]);

  const fetchVitalsHistory = useCallback(async () => {
    if (!effectiveResidentId) {
      setVitalsHistory([]);
      return;
    }

    try {
      const data = await vitalsService.getVitals(String(effectiveResidentId));
      let history = Array.isArray(data) ? data : data ? [data] : [];
      history = history.filter((v) => String(v.residentId) === String(effectiveResidentId));
      history.sort(
        (a, b) =>
          new Date(b.vitalDate || 0).getTime() - new Date(a.vitalDate || 0).getTime(),
      );
      setVitalsHistory(history);
    } catch (error) {
      console.error("Failed to fetch vitals history", error);
      setVitalsHistory([]);
    }
  }, [effectiveResidentId]);

  useEffect(() => {
    fetchVitalsHistory();
  }, [fetchVitalsHistory]);

  // Activity UI Helpers
  const getActivityIcon = (type: ActivityType) => {
    switch (type) {
      case "NOTE": return <AssignmentIcon fontSize="small" />;
      case "VITAL": return <HistoryIcon fontSize="small" />;
      case "MAR": return <LocalPharmacyIcon fontSize="small" />;
      case "APPOINTMENT": return <EventIcon fontSize="small" />;
      case "DOCUMENT": return <DescriptionIcon fontSize="small" />;
      case "FACESHEET": return <AssignmentIndIcon fontSize="small" />;
      case "PRESCRIBED_MEDICATION": return <MedicationIcon fontSize="small" />;
      case "CARE_PLAN": return <AssignmentTurnedInIcon fontSize="small" />;
      case "PHARMACY_ORDER": return <LocalPharmacyIcon fontSize="small" />;
      default: return <HistoryIcon fontSize="small" />;
    }
  };

  const getActivityColor = (type: ActivityType) => {
    switch (type) {
      case "NOTE": return "#2196f3"; // Blue
      case "VITAL": return "#f06292"; // Pink
      case "MAR": return "#4caf50"; // Green
      case "APPOINTMENT": return "#ff9800"; // Orange
      case "DOCUMENT": return "#9c27b0"; // Purple
      case "FACESHEET": return "#009688"; // Teal
      case "PRESCRIBED_MEDICATION": return "#f44336"; // Red
      case "CARE_PLAN": return "#3f51b5"; // Indigo
      case "PHARMACY_ORDER": return "#795548"; // Brown
      default: return "#757575"; // Grey
    }
  };


  // Separate effect to update medication compliance in the form when medications are loaded
  useEffect(() => {
    if (medications && medications.length > 0) {
      setTodaysVitals((prev) => ({
        ...prev,
        medicationCompliance: medications.map((med) => ({
          medication: med.med || "Unknown",
          scheduledTime: med.schedule || "N/A",
          actualTime: "",
          status: "In Progress" as const,
          notes: "",
        })),
      }));
    }
  }, [medications]);

  const getTimeFromSlot = (slot: string) => {
    switch (slot) {
      case "Morning": return "08:00";
      case "Mid-AM": return "10:30";
      case "Noon": return "13:00";
      case "Afternoon": return "15:00";
      case "Evening": return "18:00";
      case "Night": return "21:00";
      default: return "";
    }
  };

  const getSlotFromTime = (time: string) => {
    if (!time) return "";
    // Normalize time string (e.g., "08:00:00" -> "08:00")
    const shortTime = time.substring(0, 5);
    switch (shortTime) {
      case "08:00": return "Morning";
      case "10:30": return "Mid-AM";
      case "13:00": return "Noon";
      case "15:00": return "Afternoon";
      case "18:00": return "Evening";
      case "21:00": return "Night";
      default: return "";
    }
  };

  // Medication handlers
  const handleAddMedication = async () => {
    if (
      !medForm.med ||
      !medForm.dose ||
      !medForm.doseUnit ||
      !medForm.route ||
      !medForm.start ||
      !medForm.schedule
    ) {
      setMedicationError("All fields are required.");
      return;
    }

    // Validate time slot for "Once daily" schedule
    if (medForm.schedule === "Once daily" && !medForm.timeSlot) {
      setMedicationError("Time of day is required for Once Daily schedule.");
      return;
    }

    if (!effectiveResidentId) {
      setMedicationError("Select a resident before adding medication.");
      return;
    }



    if (
      medForm.start &&
      medForm.end &&
      !isNaN(new Date(medForm.end).getTime()) &&
      new Date(medForm.end) < new Date(medForm.start)
    ) {
      setMedicationError(
        "Refill Date should not be less than the Medication Start Date.",
      );
      return;
    }

    try {
      setMedicationError(null);
      setIsSavingMedication(true);
      const dosageValue = formatDoseWithUnit(medForm.dose, medForm.doseUnit);
      const payload = {
        residentId: effectiveResidentId,
        medicationName: medForm.med,
        dosage: dosageValue,
        route: medForm.route,
        schedule: medForm.schedule,
        timeOfDay: (medForm.schedule === "Once daily" || medForm.schedule === "STAT Dose") ? getTimeFromSlot(medForm.timeSlot) : undefined,
        startDate: medForm.start
          ? format(medForm.start as Date, "yyyy-MM-dd'T'HH:mm:ss")
          : null,
        endDate: medForm.end
          ? format(medForm.end as Date, "yyyy-MM-dd'T'HH:mm:ss")
          : null,
        note: medForm.schedule === "Other" ? medForm.note : undefined,
        discontinued: medForm.discontinued,
        isDiscontinued: medForm.discontinued,
        isNarcotic: medForm.isNarcotic,
        isCritical: medForm.isCritical,
        narcoticStorageLocation: medForm.isNarcotic ? medForm.narcoticStorageLocation : undefined,
      };
      const response = await apiClient.post("/v1/EMR-medications", payload);
      const created = response.data;
      // Reload medications for the selected resident to ensure we have the latest data
      // and maintain the filter
      if (effectiveResidentId) {
        try {
          let url = `/v1/EMR-medications?residentId=${effectiveResidentId}`;
          const refreshResponse = await apiClient.get(url);
          let refreshedMeds = refreshResponse.data.map((med: any) => {
            const creatorId = med.created_by || med.createdBy || med.creatorId;
            const updaterId =
              med.updated_by ||
              med.updatedBy ||
              med.lastModifiedBy ||
              med.updaterId;
            return {
              id: med.id,
              med: med.medicationName,
              dose: med.dosage,
              route: med.route,
              start: med.startDate,
              end: med.endDate,
              schedule: med.schedule,
              timeSlot: med.timeSlot || (med.timeOfDay ? getSlotFromTime(med.timeOfDay) : ""),
              timeOfDay: med.timeOfDay || "",
              residentId: med.residentId,
              createdBy: creatorId,
              createdByName:
                med.createdByName ||
                med.creatorName ||
                med.created_by_name ||
                (creatorId ? getUserDisplay(creatorId) : "-"),
              createdOn: med.created_at || med.createdAt || med.createdDate,
              updatedBy: updaterId,
              updatedByName:
                med.updatedByName ||
                med.updaterName ||
                med.updated_by_name ||
                (updaterId ? getUserDisplay(updaterId) : "-"),
              updatedOn:
                med.updated_at || med.updatedAt || med.lastModifiedDate,
              note: med.note || "",
              discontinued: med.isDiscontinued || med.discontinued || false,
              isNarcotic: med.isNarcotic || false,
              isCritical: med.isCritical || med.is_critical || false,
              narcoticStorageLocation: med.narcoticStorageLocation || "",
            };
          });
          // Filter client-side if needed
          /* if (refreshedMeds.length > 0 && refreshedMeds[0].residentId) {
            refreshedMeds = refreshedMeds.filter(
              (med: Medication) => String(med.residentId) === String(residentForFetch)
            );
          } */ // Redundant if we trust backend + generic filter, but safe to leave or rely on effect.
          // Actually, the effect will re-run if we depend on it, OR we just update simplified local state.
          // Ideally we just re-fetch using the effect? No, manual update is faster UI.

          refreshedMeds = refreshedMeds.filter(
            (med: Medication) =>
              med.residentId && String(med.residentId) === effectiveResidentId,
          );

          // Sort by creation date descending (newest first), then by start date
          refreshedMeds.sort((a: any, b: any) => {
            const dateA = new Date(a.createdOn || a.start || 0).getTime();
            const dateB = new Date(b.createdOn || b.start || 0).getTime();
            return dateB - dateA;
          });

          setMedications(refreshedMeds);
        } catch (refreshError) {
          // If refresh fails, add the new medication manually
          const newRow: Medication = {
            id: created.id,
            med: created.medicationName || medForm.med,
            dose: created.dosage || dosageValue,
            route: created.route || medForm.route,
            start: medForm.start
              ? format(medForm.start as Date, "yyyy-MM-dd'T'HH:mm:ss")
              : "",
            end: medForm.end
              ? format(medForm.end as Date, "yyyy-MM-dd'T'HH:mm:ss")
              : null,
            schedule: created.schedule || medForm.schedule,
            timeSlot: created.timeSlot || (medForm.schedule === "Once daily" ? medForm.timeSlot : ""),
            residentId: created.residentId || effectiveResidentId,
            createdBy: created.created_by || created.createdBy,
            createdOn:
              created.created_at || created.createdAt || created.createdDate,
            updatedBy:
              created.updated_by || created.updatedBy || created.lastModifiedBy,
            updatedOn:
              created.updated_at ||
              created.updatedAt ||
              created.lastModifiedDate,
            note:
              created.note ||
              (medForm.schedule === "Other" ? medForm.note : ""),
            discontinued: created.discontinued || false,
            isNarcotic: created.isNarcotic || medForm.isNarcotic || false,
            isCritical: created.isCritical || medForm.isCritical || false,
            narcoticStorageLocation: created.narcoticStorageLocation || (medForm.isNarcotic ? medForm.narcoticStorageLocation : "") || "",
          };
          setMedications((prev) => [newRow, ...prev]);
        }
      } else {
        // Should not happen due to check above
      }
      setMedForm({
        id: "",
        med: "",
        dose: "",
        doseUnit: "",
        route: "",
        start: new Date(),
        end: null,
        schedule: "",
        timeSlot: "",
        note: "",
        discontinued: false,
        isNarcotic: false,
        isCritical: false,
        narcoticStorageLocation: "",
      });
      fetchActivityTimeline();
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to add medication. Please try again.";
      setMedicationError(message);
    } finally {
      setIsSavingMedication(false);
    }
  };

  const handleEditMedication = (index: number) => {
    const row = medications[index];
    const parsedDose = splitDoseAndUnit(row.dose);

    // Robust matching for Route, Schedule and Dose Unit to handle case-sensitivity from master data
    const matchedRoute = routeOptions.find(opt =>
      opt.valueName.trim().toLowerCase() === (row.route || "").trim().toLowerCase()
    )?.valueName || row.route || "";

    const matchedSchedule = scheduleOptions.find(opt =>
      opt.valueName.trim().toLowerCase() === (row.schedule || "").trim().toLowerCase()
    )?.valueName || row.schedule || "";

    const matchedUnit = unitOptions.find(opt =>
      opt.valueName.trim().toLowerCase() === (parsedDose.unit || "").trim().toLowerCase()
    )?.valueName || (parsedDose.unit === "mg/ml" ? "mg/mL" : parsedDose.unit);

    setMedForm({
      id: row.id,
      med: row.med,
      dose: parsedDose.value,
      doseUnit: matchedUnit,
      route: matchedRoute,
      start: row.start ? new Date(row.start) : new Date(),
      end: (row as any).end ? new Date((row as any).end) : null,
      schedule: matchedSchedule,
      timeSlot: (row as any).timeSlot || ((row as any).timeOfDay ? getSlotFromTime((row as any).timeOfDay) : ""),
      note: row.note || "",
      discontinued: row.discontinued || false,
      isNarcotic: (row as any).isNarcotic || false,
      isCritical: (row as any).isCritical || false,
      narcoticStorageLocation: (row as any).narcoticStorageLocation || "",
    });
    setEditingIndex(index);
    // Keep the user on the "Prescribe Medication" tab when editing a medication
    setTopTab(1);
    // Ensure MAR subtab is reset to the default when switching from other tabs
    setEmrTab(0);
  };

  const handleToggleDiscontinued = (row: Medication, index: number) => {
    const parsedDose = splitDoseAndUnit(row.dose);

    // Robust matching for Route, Schedule and Dose Unit to handle case-sensitivity from master data
    const matchedRoute = routeOptions.find(opt =>
      opt.valueName.trim().toLowerCase() === (row.route || "").trim().toLowerCase()
    )?.valueName || row.route || "";

    const matchedSchedule = scheduleOptions.find(opt =>
      opt.valueName.trim().toLowerCase() === (row.schedule || "").trim().toLowerCase()
    )?.valueName || row.schedule || "";

    const matchedUnit = unitOptions.find(opt =>
      opt.valueName.trim().toLowerCase() === (parsedDose.unit || "").trim().toLowerCase()
    )?.valueName || (parsedDose.unit === "mg/ml" ? "mg/mL" : parsedDose.unit);

    setMedForm({
      id: row.id,
      med: row.med,
      dose: parsedDose.value,
      doseUnit: matchedUnit,
      route: matchedRoute,
      start: row.start ? new Date(row.start) : new Date(),
      end: (row as any).end ? new Date((row as any).end) : null,
      schedule: matchedSchedule,
      timeSlot: (row as any).timeSlot || "",
      note: row.note || "",
      discontinued: !row.discontinued,
      isNarcotic: (row as any).isNarcotic || false,
      isCritical: (row as any).isCritical || false,
      narcoticStorageLocation: (row as any).narcoticStorageLocation || "",
    });
    setEditingIndex(index);
    setTopTab(1);
    setEmrTab(0);
    setDiscontinueDialogMode(row.discontinued ? "UNDO" : "DISCONTINUE");
    setOpenDiscontinueDialog(true);
  };

  const submitUpdateMedication = async () => {
    try {
      setIsSavingMedication(true);
      const dosageValue = formatDoseWithUnit(medForm.dose, medForm.doseUnit);
      const payload = {
        medicationName: medForm.med,
        dosage: dosageValue,
        route: medForm.route,
        schedule: medForm.schedule,
        timeOfDay: (medForm.schedule === "Once daily" || medForm.schedule === "STAT Dose") ? getTimeFromSlot(medForm.timeSlot) : undefined,
        startDate: medForm.start
          ? format(medForm.start as Date, "yyyy-MM-dd'T'HH:mm:ss")
          : null,
        endDate: medForm.end
          ? format(medForm.end as Date, "yyyy-MM-dd'T'HH:mm:ss")
          : null,
        note: medForm.schedule === "Other" ? medForm.note : undefined,
        discontinued: medForm.discontinued,
        isDiscontinued: medForm.discontinued,
        isNarcotic: medForm.isNarcotic,
        isCritical: medForm.isCritical,
        narcoticStorageLocation: medForm.isNarcotic ? medForm.narcoticStorageLocation : undefined,
      };
      const response = await apiClient.put(
        `/v1/EMR-medications/${medForm.id}`,
        payload,
      );

      // Reload medications for the selected resident to ensure we have the latest data
      if (effectiveResidentId) {
        try {
          let url = `/v1/EMR-medications?residentId=${effectiveResidentId}`;
          const refreshResponse = await apiClient.get(url);
          let refreshedMeds = refreshResponse.data.map((med: any) => {
            const creatorId = med.created_by || med.createdBy || med.creatorId;
            const updaterId =
              med.updated_by ||
              med.updatedBy ||
              med.lastModifiedBy ||
              med.updaterId;
            return {
              id: med.id,
              med: med.medicationName,
              dose: med.dosage,
              route: med.route,
              start: med.startDate,
              end: med.endDate,
              schedule: med.schedule,
              timeSlot: med.timeSlot || (med.timeOfDay ? getSlotFromTime(med.timeOfDay) : ""),
              residentId: med.residentId,
              createdBy: creatorId,
              createdByName:
                med.createdByName ||
                med.creatorName ||
                med.created_by_name ||
                (creatorId ? getUserDisplay(creatorId) : "-"),
              createdOn: med.created_at || med.createdAt || med.createdDate,
              updatedBy: updaterId,
              updatedByName:
                med.updatedByName ||
                med.updaterName ||
                med.updated_by_name ||
                (updaterId ? getUserDisplay(updaterId) : "-"),
              updatedOn:
                med.updated_at || med.updatedAt || med.lastModifiedDate,
              note: med.note || "",
              discontinued: med.isDiscontinued || med.discontinued || false,
              isNarcotic: med.isNarcotic || false,
              isCritical: med.isCritical || med.is_critical || false,
              narcoticStorageLocation: med.narcoticStorageLocation || "",
            };
          });

          refreshedMeds = refreshedMeds.filter(
            (med: Medication) =>
              med.residentId && String(med.residentId) === effectiveResidentId,
          );

          // Sort by start date descending
          refreshedMeds.sort(
            (a: any, b: any) =>
              new Date(b.start).getTime() - new Date(a.start).getTime(),
          );

          setMedications(refreshedMeds);
        } catch (refreshError) {
          console.error("Failed to refresh medications", refreshError);
          // Fallback: update local state manually but try to preserve existing metadata if possible
          const updatedList = [...medications];
          const existing = updatedList[editingIndex as number];
          updatedList[editingIndex as number] = {
            ...existing,
            med: medForm.med,
            dose: dosageValue,
            route: medForm.route,
            schedule: medForm.schedule,
            note: medForm.note,
            discontinued: medForm.discontinued,
            isNarcotic: medForm.isNarcotic,
            isCritical: medForm.isCritical,
            narcoticStorageLocation: medForm.isNarcotic ? medForm.narcoticStorageLocation : "",
            timeSlot: medForm.timeSlot,
            timeOfDay: getTimeFromSlot(medForm.timeSlot),
            start: medForm.start
              ? format(medForm.start as Date, "yyyy-MM-dd'T'HH:mm:ss")
              : "",
            end: medForm.end
              ? format(medForm.end as Date, "yyyy-MM-dd'T'HH:mm:ss")
              : null,
          } as any;
          setMedications(updatedList);
        }
      }

      setMedForm({
        id: "",
        med: "",
        dose: "",
        doseUnit: "",
        route: "",
        start: new Date(),
        end: null,
        schedule: "",
        timeSlot: "",
        note: "",
        discontinued: false,
        isNarcotic: false,
        isCritical: false,
        narcoticStorageLocation: "",
      });
      setEditingIndex(null);
      setOpenDiscontinueDialog(false); // Close dialog if open
      fetchActivityTimeline();
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to update medication. Please try again.";
      setMedicationError(message);
    } finally {
      setIsSavingMedication(false);
    }
  };

  const handleUpdateMedication = async () => {
    if (
      !medForm.med ||
      !medForm.dose ||
      !medForm.doseUnit ||
      !medForm.route ||
      !medForm.start ||
      !medForm.schedule
    ) {
      setMedicationError("All fields are required.");
      return;
    }

    // Validate time slot for "Once daily" schedule
    if (medForm.schedule === "Once daily" && !medForm.timeSlot) {
      setMedicationError("Time of day is required for Once Daily schedule.");
      return;
    }

    setMedicationError(null);



    if (
      medForm.start &&
      medForm.end &&
      !isNaN(new Date(medForm.end).getTime()) &&
      new Date(medForm.end) < new Date(medForm.start)
    ) {
      setMedicationError(
        "Refill Date should not be less than the Medication Start Date.",
      );
      return;
    }

    // Check if discontinued status changed
    if (editingIndex !== null) {
      const originalMed = medications[editingIndex];
      const originalDiscontinued = originalMed.discontinued || false;
      const newDiscontinued = medForm.discontinued || false;

      if (originalDiscontinued !== newDiscontinued) {
        if (newDiscontinued) {
          setDiscontinueDialogMode("DISCONTINUE");
        } else {
          setDiscontinueDialogMode("UNDO");
        }
        setOpenDiscontinueDialog(true);
        return;
      }

      // Check if schedule changed
      const originalSchedule = originalMed.schedule || "";
      const newSchedule = medForm.schedule || "";

      if (originalSchedule !== newSchedule) {
        // Open choice dialog to ask if they want to update existing or discontinue & add new
        setOpenMedicationUpdateChoice(true);
        return;
      }
    }

    // If schedule hasn't changed, update normally
    await submitUpdateMedication();
  };

  const handleUpdateExisting = async () => {
    setOpenMedicationUpdateChoice(false);
    await submitUpdateMedication();
  };

  const handleDiscontinueAndAddNew = async () => {
    setOpenMedicationUpdateChoice(false);
    if (!medForm.id) return;

    try {
      setIsSavingMedication(true);
      setMedicationError(null);

      // 1. Discontinue the old medication
      await apiClient.put(`/v1/EMR-medications/${medForm.id}`, {
        ...medications[editingIndex!],
        discontinued: true,
        isDiscontinued: true,
      });

      // 2. Add the new medication (similar to handleAddMedication logic)
      const dosageValue = formatDoseWithUnit(medForm.dose, medForm.doseUnit);
      const payload = {
        residentId: effectiveResidentId,
        medicationName: medForm.med,
        dosage: dosageValue,
        route: medForm.route,
        schedule: medForm.schedule,
        timeOfDay: (medForm.schedule === "Once daily" || medForm.schedule === "STAT Dose") ? getTimeFromSlot(medForm.timeSlot) : undefined,
        startDate: medForm.start ? format(medForm.start as Date, "yyyy-MM-dd'T'HH:mm:ss") : null,
        endDate: medForm.end ? format(medForm.end as Date, "yyyy-MM-dd'T'HH:mm:ss") : null,
        note: medForm.schedule === "Other" ? medForm.note : undefined,
        discontinued: false,
        isDiscontinued: false,
        isNarcotic: medForm.isNarcotic,
        isCritical: medForm.isCritical,
        narcoticStorageLocation: medForm.isNarcotic ? medForm.narcoticStorageLocation : undefined,
      };

      await apiClient.post("/v1/EMR-medications", payload);

      // Reload medications
      if (effectiveResidentId) {
        const url = `/v1/EMR-medications?residentId=${effectiveResidentId}`;
        const refreshResponse = await apiClient.get(url);
        let refreshedMeds = refreshResponse.data.map((med: any) => {
          const creatorId = med.created_by || med.createdBy || med.creatorId;
          const updaterId = med.updated_by || med.updatedBy || med.lastModifiedBy || med.updaterId;
          return {
            id: med.id,
            med: med.medicationName,
            dose: med.dosage,
            route: med.route,
            start: med.startDate,
            end: med.endDate,
            schedule: med.schedule,
            timeSlot: med.timeSlot || (med.timeOfDay ? getSlotFromTime(med.timeOfDay) : ""),
            residentId: med.residentId,
            createdBy: creatorId,
            createdByName: med.createdByName || med.creatorName || med.created_by_name || (creatorId ? getUserDisplay(creatorId) : "-"),
            createdOn: med.created_at || med.createdAt || med.createdDate,
            updatedBy: updaterId,
            updatedByName: med.updatedByName || med.updaterName || med.updated_by_name || (updaterId ? getUserDisplay(updaterId) : "-"),
            updatedOn: med.updated_at || med.updatedAt || med.lastModifiedDate,
            note: med.note || "",
            discontinued: med.isDiscontinued || med.discontinued || false,
            isNarcotic: med.isNarcotic || false,
            isCritical: med.isCritical || med.is_critical || false,
            narcoticStorageLocation: med.narcoticStorageLocation || "",
          };
        });

        refreshedMeds = refreshedMeds.filter((med: Medication) => med.residentId && String(med.residentId) === effectiveResidentId);
        refreshedMeds.sort((a: any, b: any) => new Date(b.createdOn || b.start || 0).getTime() - new Date(a.createdOn || a.start || 0).getTime());
        setMedications(refreshedMeds);
      }

      setMedForm({
        id: "",
        med: "",
        dose: "",
        doseUnit: "",
        route: "",
        start: new Date(),
        end: null,
        schedule: "",
        timeSlot: "",
        note: "",
        discontinued: false,
        isNarcotic: false,
        isCritical: false,
        narcoticStorageLocation: "",
      });
      setEditingIndex(null);
      fetchActivityTimeline();
    } catch (error: any) {
      console.error("Failed to discontinue and add new medication", error);
      setMedicationError(error?.response?.data?.message || error?.message || "Failed to process medication update.");
    } finally {
      setIsSavingMedication(false);
    }
  };

  const handleCancelEditMedication = () => {
    setMedForm({
      id: "",
      med: "",
      dose: "",
      doseUnit: "",
      route: "",
      start: new Date(),
      end: null,
      schedule: "",
      timeSlot: "",
      note: "",
      discontinued: false,
      isNarcotic: false,
      isCritical: false,
      narcoticStorageLocation: "",
    });
    setEditingIndex(null);
    setMedicationError(null);
  };

  // Status Change Handler
  const handleStatusChangeRequest = (newStatus: string) => {
    const userRole = String(user?.role || "").toUpperCase().replace(/^ROLE_/, "");
    if (userRole === "CAREGIVER" || userRole === "LEAD_CAREGIVER" || userRole === "NURSE") {
      setPendingStatusUpdate(newStatus);
      setStatusConfirmOpen(true);
    } else {
      handleStatusChange(newStatus);
    }
  };

  const handleStatusChange = async (newStatus: string, reason?: string) => {
    if (!effectiveResidentId) return;

    try {
      setIsUpdatingStatus(true);
      // Use specific status endpoint as requested
      await residentService.updateResidentStatus(
        String(effectiveResidentId),
        newStatus,
        reason
      );

      // Update local banner resident status
      if (prefilledResident) {
        setPrefilledResident({ ...prefilledResident, status: newStatus });
      }

      // Immediately refresh Recent Updates timeline
      await fetchActivityTimeline();
      setTimeout(() => {
        fetchActivityTimeline();
      }, 500);
    } catch (error) {
      console.error("Failed to update status", error);
      alert("Failed to update status");
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleOpenInsuranceDialog = (insurance?: InsuranceCard) => {
    if (insurance) {
      setEditingInsurance(insurance);
      setInsuranceForm({
        ...insurance,
        insuranceCompanyName: insurance.insuranceCompanyName || insurance.provider || "",
        policyMemberId: insurance.policyMemberId || insurance.policyNumber || "",
        insuranceType: insurance.insuranceType || "PRIVATE",
        planType: insurance.planType || "HMO",
      });
    } else {
      setEditingInsurance(null);
      setInsuranceForm({
        insuranceCompanyName: "",
        insuranceType: "PRIVATE",
        payerId: "",
        insurancePhone: "",
        insuranceEmail: "",
        subscriberName: "",
        subscriberRelationship: "",
        subscriberDob: "",
        subscriberGender: "",
        subscriberPhone: "",
        subscriberAddress: "",
        subscriberEmployerName: "",
        subscriberEmployerAddress: "",
        policyMemberId: "",
        groupNumber: "",
        planName: "",
        planType: "HMO",
        coverageType: "",
        effectiveDate: "",
        terminationDate: "",
        authorizationNumber: "",
        copayAmount: "",
        coinsurancePercentage: "",
        annualDeductible: "",
        remainingDeductible: "",
        outOfPocketLimit: "",
      });
    }
    setInsuranceErrors({});
    setIsInsuranceDialogOpen(true);
  };

  const toggleInsuranceExpansion = (cardId: string) => {
    setExpandedInsurances((prev) => {
      const next = new Set(prev);
      if (next.has(cardId)) {
        next.delete(cardId);
      } else {
        next.add(cardId);
      }
      return next;
    });
  };

  const handleSaveInsurance = async () => {
    if (!effectiveResidentId || !fullResidentData) return;

    // RBAC check: Restrict Caregivers from adding/updating insurance
    if (user?.role === "CAREGIVER" || user?.role === "LEAD_CAREGIVER") {
      setVitalsError("You don't have permission to access this resource.");
      setIsInsuranceDialogOpen(false);
      setTimeout(() => setVitalsError(null), 3000);
      return;
    }

    const currentErrors = { ...insuranceErrors };
    let hasError = false;

    if (insuranceForm.insuranceEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(insuranceForm.insuranceEmail)) {
      currentErrors.insuranceEmail = "Invalid email format";
      hasError = true;
    }

    const validateDecimal = (val: string) => {
      if (!val) return "";
      return /^\d+(\.\d{1,2})?$/.test(val) ? "" : "Invalid format (e.g. 25.50)";
    };

    const decimalFields = ["copayAmount", "coinsurancePercentage", "annualDeductible", "remainingDeductible", "outOfPocketLimit"] as const;
    decimalFields.forEach(field => {
      const val = String((insuranceForm as any)[field] || "");
      if (val) {
        const err = validateDecimal(val);
        if (err) {
          currentErrors[field] = err;
          hasError = true;
        }
      }
    });

    if (hasError || Object.values(currentErrors).some(err => err !== "")) {
      setInsuranceErrors(currentErrors);
      setVitalsError("Please fix the errors before saving.");
      setTimeout(() => setVitalsError(null), 3000);
      return;
    }

    try {
      const payload: any = {
        ...insuranceForm,
      };

      if (payload.coinsurancePercentage !== "") payload.coinsurancePercentage = Number(payload.coinsurancePercentage);
      if (payload.annualDeductible !== "") payload.annualDeductible = Number(payload.annualDeductible);
      if (payload.remainingDeductible !== "") payload.remainingDeductible = Number(payload.remainingDeductible);
      if (payload.outOfPocketLimit !== "") payload.outOfPocketLimit = Number(payload.outOfPocketLimit);

      if (!payload.terminationDate) payload.terminationDate = null;
      if (!payload.effectiveDate) payload.effectiveDate = null;
      if (!payload.subscriberDob) payload.subscriberDob = null;

      payload.requiresPriorAuthorization = false;
      payload.requiresReferral = false;

      if (editingInsurance && editingInsurance.id) {
        await residentService.updateInsurance(String(effectiveResidentId), editingInsurance.id, payload);
      } else {
        await residentService.addInsurance(String(effectiveResidentId), payload);
      }

      // Re-fetch all insurances to ensure local state matches server
      const updatedCards = await residentService.getResidentInsurances(String(effectiveResidentId));
      setInsuranceCards(updatedCards);

      if (fullResidentData) {
        setFullResidentData({
          ...fullResidentData,
          insuranceCards: updatedCards,
        });
      }

      setIsInsuranceDialogOpen(false);

      if (isFacesheetEditing) {
        setFacesheetForm((prev: any) => ({
          ...prev,
          insuranceCards: updatedCards,
        }));
      }

      setVitalsSuccess("Insurance information updated successfully");
      fetchActivityTimeline();
      setTimeout(() => setVitalsSuccess(null), 3000);
    } catch (error) {
      console.error("Failed to save insurance information", error);
      setVitalsError("Failed to save insurance information");
      setTimeout(() => setVitalsError(null), 3000);
    }
  };

  const handleDeleteInsurance = (insurance: InsuranceCard) => {
    setDeleteInsurance(insurance);
  };

  const confirmDeleteInsurance = async () => {
    if (!effectiveResidentId || !fullResidentData || !deleteInsurance || !deleteInsurance.id) return;

    try {
      await residentService.deleteInsurance(String(effectiveResidentId), deleteInsurance.id);

      // Re-fetch all insurances
      const updatedCards = await residentService.getResidentInsurances(String(effectiveResidentId));
      setInsuranceCards(updatedCards);

      if (fullResidentData) {
        setFullResidentData({
          ...fullResidentData,
          insuranceCards: updatedCards,
        });
      }

      if (isFacesheetEditing) {
        setFacesheetForm((prev: any) => ({
          ...prev,
          insuranceCards: updatedCards,
        }));
      }

      setVitalsSuccess("Insurance card deleted successfully");
      fetchActivityTimeline();
      setTimeout(() => setVitalsSuccess(null), 3000);
      setDeleteInsurance(null);
    } catch (error) {
      console.error("Failed to delete insurance card", error);
      setVitalsError("Failed to delete insurance card");
      setTimeout(() => setVitalsError(null), 3000);
      setDeleteInsurance(null);
    }
  };

  const handleToggleFacesheetEdit = () => {
    if (!isFacesheetEditing && fullResidentData) {
      setFacesheetForm({ ...fullResidentData });
    }
    setIsFacesheetEditing(!isFacesheetEditing);
  };

  const handleSaveFacesheet = async () => {
    if (!effectiveResidentId || !fullResidentData) return;

    const errors: { [key: string]: string } = {};
    if (!facesheetForm.firstName?.trim()) errors.firstName = "First Name is required";
    if (!facesheetForm.lastName?.trim()) errors.lastName = "Last Name is required";
    if (!facesheetForm.dateOfBirth?.trim()) errors.dateOfBirth = "Date of Birth is required";
    if (!facesheetForm.gender?.trim()) errors.gender = "Gender is required";
    if (!facesheetForm.medicalRecordNumber?.trim()) errors.medicalRecordNumber = "Medical Record Number is required";
    if (!facesheetForm.admissionType?.trim()) errors.admissionType = "Level of Care is required";
    if (!facesheetForm.dnrStatus?.trim()) errors.dnrStatus = "DNR Status is required";
    if (!facesheetForm.emergencyContactPhone?.trim()) errors.emergencyContactPhone = "Guardian Mobile Number is required";
    if (!facesheetForm.height) errors.height = "Height is required";
    if (!facesheetForm.attendingPhysicianPhone?.trim()) {
      errors.attendingPhysicianPhone = "Doctor Contact is required";
    } else if (!/^\d{10}$/.test(facesheetForm.attendingPhysicianPhone.trim())) {
      errors.attendingPhysicianPhone = "Doctor Contact must be exactly 10 digits";
    }
    if (!facesheetForm.attendingPhysicianEmail?.trim()) errors.attendingPhysicianEmail = "Doctor Email is required";

    if (Object.keys(errors).length > 0) {
      setFacesheetErrors(errors);
      return;
    }
    setFacesheetErrors({});

    try {
      if (facesheetForm.phone && facesheetForm.phone.length > 0 && facesheetForm.phone.length !== 10) {
        setVitalsError("Phone number must be exactly 10 digits");
        setTimeout(() => setVitalsError(null), 3000);
        return;
      }
      if (facesheetForm.emergencyContactPhone && facesheetForm.emergencyContactPhone.length > 0 && facesheetForm.emergencyContactPhone.length !== 10) {
        setVitalsError("Emergency contact phone must be exactly 10 digits");
        setTimeout(() => setVitalsError(null), 3000);
        return;
      }

      setIsUpdatingStatus(true);
      const payload = { ...facesheetForm };

      await residentService.updateResident(String(effectiveResidentId), payload as any);

      const updatedResident = { ...fullResidentData, ...payload } as ResidentDTO;
      setFullResidentData(updatedResident);

      // Update banner data
      const bannerData = {
        ...prefilledResident,
        name: `${updatedResident.firstName} ${updatedResident.middleName ? updatedResident.middleName + " " : ""}${updatedResident.lastName}`.trim(),
        EMRNo: updatedResident.mrnNumber || updatedResident.medicalRecordNumber || updatedResident.emrNumber || String(updatedResident.id) || "N/A",
        bp: updatedResident.bloodPressure || "N/A",
        weight: updatedResident.weight !== undefined ? String(updatedResident.weight) : "N/A",
        height: updatedResident.height !== undefined ? String(updatedResident.height) : "N/A",
        bmi: updatedResident.bmi !== undefined ? String(updatedResident.bmi) : "N/A",
        room: updatedResident.roomNumber && updatedResident.bedNumber
          ? `${updatedResident.roomNumber}-${updatedResident.bedNumber}`
          : updatedResident.roomNumber || "Not Assigned",
        sex: updatedResident.gender || "N/A",
        dob: updatedResident.dateOfBirth || "N/A",
        attendingPhysician: updatedResident.attendingPhysicianName || "N/A",
        bloodGroup: updatedResident.bloodGroup || "N/A",
        allergies: updatedResident.allergies
          ? String(updatedResident.allergies)
            .split(",")
            .map((a: string) => a.trim())
            .filter(Boolean)
          : [],
        admissionType: updatedResident.admissionType || updatedResident.careType || "N/A",
      };
      setPrefilledResident(bannerData);

      setIsFacesheetEditing(false);
      setVitalsSuccess("Facesheet updated successfully");
      fetchActivityTimeline();
      setTimeout(() => setVitalsSuccess(null), 3000);
    } catch (error: any) {
      console.error("Failed to update Facesheet", error);

      // Handle backend validation errors
      if (error.response?.status === 400 && error.response?.data?.validationErrors) {
        const backendErrors: { [key: string]: string } = {};
        const validationErrors = error.response.data.validationErrors;

        // Map backend error field names to form field names
        Object.keys(validationErrors).forEach((field) => {
          const messages = validationErrors[field];
          if (Array.isArray(messages) && messages.length > 0) {
            backendErrors[field] = messages[0];
          }
        });

        setFacesheetErrors(backendErrors);
        setVitalsError("Please correct the validation errors below");
        setTimeout(() => setVitalsError(null), 5000);
      } else {
        setVitalsError("You don't have permission to access this resource.");
        setTimeout(() => setVitalsError(null), 3000);
      }
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleRemoveMedication = async (index: number) => {
    const row = medications[index];
    try {
      await apiClient.delete(`/v1/EMR-medications/${row.id}`);
      // Reload medications for the selected resident to ensure we have the latest data
      if (effectiveResidentId) {
        try {
          let url = `/v1/EMR-medications?residentId=${effectiveResidentId}`;
          const refreshResponse = await apiClient.get(url);
          let refreshedMeds = refreshResponse.data.map((med: any) => {
            const creatorId = med.created_by || med.createdBy || med.creatorId;
            const updaterId =
              med.updated_by ||
              med.updatedBy ||
              med.lastModifiedBy ||
              med.updaterId;
            return {
              id: med.id,
              med: med.medicationName,
              dose: med.dosage,
              route: med.route,
              start: med.startDate,
              end: med.endDate,
              schedule: med.schedule,
              timeSlot: med.timeSlot || (med.timeOfDay ? getSlotFromTime(med.timeOfDay) : ""),
              timeOfDay: med.timeOfDay || "",
              residentId: med.residentId,
              createdBy: creatorId,
              createdByName:
                med.createdByName ||
                med.creatorName ||
                med.created_by_name ||
                (creatorId ? getUserDisplay(creatorId) : "-"),
              createdOn: med.created_at || med.createdAt || med.createdDate,
              updatedBy: updaterId,
              updatedByName:
                med.updatedByName ||
                med.updaterName ||
                med.updated_by_name ||
                (updaterId ? getUserDisplay(updaterId) : "-"),
              updatedOn: med.updated_at || med.updatedAt || med.lastModifiedDate,
              note: med.note || "",
              discontinued: med.isDiscontinued || med.discontinued || false,
              isNarcotic: med.isNarcotic || false,
              narcoticStorageLocation: med.narcoticStorageLocation || "",
            };
          });
          // Filter client-side if needed
          refreshedMeds = refreshedMeds.filter(
            (med: Medication) =>
              med.residentId && String(med.residentId) === effectiveResidentId,
          );
          setMedications(refreshedMeds);
        } catch (refreshError) {
          // If refresh fails, remove locally
          setMedications((prev) => prev.filter((_, i) => i !== index));
        }
      } else {
        // If no resident selected (unlikely given checks), remove locally
        setMedications((prev) => prev.filter((_, i) => i !== index));
      }
      if (editingIndex === index) {
        handleCancelEditMedication();
      }
    } catch (error) {
      console.error("Failed to remove medication", error);
    } finally {
      fetchActivityTimeline();
    }
  };

  // Nurse entry handlers
  const handleAddNurseEntry = async (isConfirmed: boolean = false) => {
    if (
      !nurseForm.noteDate ||
      !nurseForm.medication ||
      !nurseForm.initials ||
      !nurseForm.dosage ||
      !nurseForm.schedule ||
      !effectiveResidentId
    ) {
      setNurseEntryError(
        "Please fill in all required fields (Date, Medication, Response Status, Dosage, Schedule)",
      );
      return;
    }

    // Validation for schedule limits
    if (!isConfirmed && nurseForm.schedule) {
      const limit = getScheduleLimit(nurseForm.schedule);
      if (limit !== null) {
        const todayStr = nurseForm.noteDate
          ? format(nurseForm.noteDate, "yyyy-MM-dd")
          : format(new Date(), "yyyy-MM-dd");

        // Count existing doses for today
        const todaysDoses = nurseEntries.filter((entry) => {
          const entryDate = entry.noteDate
            ? format(new Date(entry.noteDate), "yyyy-MM-dd")
            : "";
          const entryMed = entry.medication || "";
          return (
            entryDate === todayStr &&
            entryMed.toLowerCase() === nurseForm.medication.toLowerCase()
          );
        }).length;

        if (todaysDoses >= limit) {
          setNurseConfirmationOpen(true);
          return;
        }
      }
    }

    executeAddNurseEntry();
  };

  const getScheduleLimit = (schedule: string): number | null => {
    const lower = schedule.trim().toLowerCase();
    if (
      lower === "once daily" ||
      lower === "one time" ||
      lower === "at bedtime" ||
      lower === "evening time"
    )
      return 1;
    if (lower === "twice daily") return 2;
    if (lower === "three times daily") return 3;
    if (lower === "four times daily") return 4;
    if (lower === "every 4 hours") return 6;
    if (lower === "every 6 hours") return 4;
    if (lower === "every 8 hours") return 3;
    return null; // No limit for "Other", "PRN", etc.
  };

  const executeAddNurseEntry = async () => {
    setIsSubmittingNurseEntry(true);
    setNurseEntryError(null);
    setNurseEntrySuccess(null);
    setNurseConfirmationOpen(false);

    try {
      const payload = {
        residentId: effectiveResidentId as string,
        medicationName: nurseForm.medication,
        dosage: nurseForm.dosage,
        // send LocalDateTime string (no zone)
        dateTime: format(nurseForm.noteDate as Date, "yyyy-MM-dd'T'HH:mm:ss"),
        medicationStatus: nurseForm.initials,
        reason: nurseForm.reason,
        schedule: nurseForm.schedule,
        result: nurseForm.results,
        lowStock: nurseForm.lowStock,
      };
      await clinicalNoteService.createNurseNote(payload);

      // Refresh from server to get generated fields
      await fetchNurseNotesData();
      fetchActivityTimeline();
      setNurseEntrySuccess("Daily Medication entry added successfully");
      setNurseForm({
        noteDate: new Date(),
        medication: "",
        dosage: "",
        reason: "",
        results: "",
        initials: "",
        lowStock: false,
        schedule: "",
      });
      setTimeout(() => setNurseEntrySuccess(null), 3000);
    } catch (error: any) {
      console.error("Failed to add Daily Medication entry", error);
      setNurseEntryError(
        error.response?.data?.message ||
        error.message ||
        "Failed to add Daily Medication entry. Please try again.",
      );
    } finally {
      setIsSubmittingNurseEntry(false);
    }
  };

  const handleRemoveNurseEntry = async (id: number, apiId?: string) => {
    if (!apiId) {
      console.error("Missing apiId for delete");
      return;
    }
    if (!window.confirm("Are you sure you want to remove this nurse entry?"))
      return;
    try {
      await clinicalNoteService.deleteNurseNote(apiId);
      await fetchNurseNotesData();
      setNurseEntrySuccess("Nurse entry removed successfully");
      fetchActivityTimeline();
      setTimeout(() => setNurseEntrySuccess(null), 3000);
    } catch (error: any) {
      console.error("Failed to remove nurse entry", error);
      setNurseEntryError(
        error.response?.data?.message ||
        error.message ||
        "Failed to remove nurse entry. Please try again.",
      );
    }
  };

  const handleEditNurseEntry = (index: number) => {
    const entry = nurseEntries[index];
    setNurseForm({
      noteDate: entry.noteDate,
      medication: entry.medication,
      dosage: entry.dosage,
      reason: entry.reason,
      schedule: entry.schedule || "",
      results: entry.results,
      initials: entry.initials,
      lowStock: !!entry.lowStock,
    });
    setEditingNurseIndex(index);
    setTopTab(2);
    setEmrTab(2);
  };

  const handleUpdateNurseEntry = async () => {
    if (
      !nurseForm.noteDate ||
      !nurseForm.medication ||
      !nurseForm.initials ||
      !nurseForm.dosage ||
      !nurseForm.schedule
    ) {
      setNurseEntryError(
        "Please fill in all required fields (Date, Medication, Response Status, Dosage, Schedule)",
      );
      return;
    }
    if (!effectiveResidentId) {
      setNurseEntryError("Please select a resident");
      return;
    }
    setIsSubmittingNurseEntry(true);
    setNurseEntryError(null);
    setNurseEntrySuccess(null);
    try {
      const entry = nurseEntries[editingNurseIndex!];
      const payload = {
        residentId: effectiveResidentId,
        medicationName: nurseForm.medication,
        dosage: nurseForm.dosage,
        dateTime: format(nurseForm.noteDate as Date, "yyyy-MM-dd'T'HH:mm:ss"),
        medicationStatus: nurseForm.initials,
        reason: nurseForm.reason,
        schedule: nurseForm.schedule,
        result: nurseForm.results,
        lowStock: nurseForm.lowStock,
      };

      if (!entry.apiId) {
        throw new Error("Missing entry ID");
      }

      await clinicalNoteService.updateNurseNote(entry.apiId, payload);

      // Refresh from server to ensure Updated By/On fields are correct
      await fetchNurseNotesData();
      setNurseEntrySuccess("Nurse entry updated successfully");
      setNurseForm({
        noteDate: new Date(),
        medication: "",
        dosage: "",
        reason: "",
        results: "",
        initials: "",
        lowStock: false,
        schedule: "",
      });
      setEditingNurseIndex(null);
      fetchActivityTimeline();
      setTimeout(() => setNurseEntrySuccess(null), 3000);
    } catch (error: any) {
      console.error("Failed to update nurse entry", error);
      setNurseEntryError(
        error.response?.data?.message ||
        error.message ||
        "Failed to update nurse entry. Please try again.",
      );
    } finally {
      setIsSubmittingNurseEntry(false);
    }
  };

  const handleCancelEditNurseEntry = () => {
    setEditingNurseIndex(null);
    setNurseForm({
      noteDate: new Date(),
      medication: "",
      dosage: "",
      reason: "",
      results: "",
      initials: "",
      lowStock: false,
      schedule: "",
    });
    setNurseEntryError(null);
  };

  // Vitals handlers
  // All vitals handling logic moved to VitalsManager component.

  const handleViewDocument = async (doc: ResidentDocumentDTO) => {
    try {
      // true for inline view (new tab)
      await residentService.openDocument(doc.id, true, doc.fileName);
    } catch (error) {
      console.error("Failed to open document", error);
      window.alert("Failed to open document. Please try again.");
    }
  };

  // Care Plan handlers
  const handleSaveCarePlan = async () => {
    if (!effectiveResidentId) {
      setCarePlanError("Please select a resident before saving the care plan.");
      return;
    }

    setIsSavingCarePlan(true);
    setCarePlanError(null);
    setCarePlanSuccess(null);

    try {
      const payload = {
        residentId: effectiveResidentId,
        diagnosticsPlan: carePlanData.diagnosticsPlan,
        dailyActivities: carePlanData.dailyActivities,
        ambulation: carePlanData.ambulation,
        woundCare: carePlanData.woundCare,
      };

      let carePlanId: string;

      if (existingCarePlanId) {
        // Update existing care plan
        await carePlanService.updateCarePlan(existingCarePlanId, payload);
        carePlanId = existingCarePlanId;
        setCarePlanSuccess("Care plan updated successfully!");
      } else {
        // Create new care plan
        const created = await carePlanService.createCarePlan(payload);
        carePlanId = created.id;
        setExistingCarePlanId(created.id);
        setCarePlanSuccess("Care plan saved successfully!");
      }

      // Fetch the latest care plan data using GET API by ID after save
      try {
        const fetchedCarePlan = await carePlanService.getCarePlanById(carePlanId);
        if (fetchedCarePlan) {
          setCarePlanData({
            diagnosticsPlan: fetchedCarePlan.diagnosticsPlan || "",
            dailyActivities: fetchedCarePlan.dailyActivities || "",
            ambulation: fetchedCarePlan.ambulation || "",
            woundCare: fetchedCarePlan.woundCare || "",
          });
          setExistingCarePlanId(fetchedCarePlan.id);
        }
      } catch (fetchError) {
        console.warn(
          "Failed to fetch updated care plan using GET by ID, but save was successful",
          fetchError,
        );
      }

      fetchActivityTimeline();
      setTimeout(() => setCarePlanSuccess(null), 3000);
    } catch (error: any) {
      console.error("Failed to save care plan", error);
      setCarePlanError(
        error.response?.data?.message ||
        error.message ||
        "Failed to save care plan. Please try again.",
      );
    } finally {
      setIsSavingCarePlan(false);
    }
  };

  const handleSaveAppointment = async () => {
    if (!effectiveResidentId) {
      setAppointmentError("Please select a resident to save appointment.");
      return;
    }
    if (
      !appointmentForm.dateTime ||
      !appointmentForm.hospitalName ||
      !appointmentForm.type
    ) {
      setAppointmentError(
        "Please fill in all required fields (Date & Time, Hospital Name, Type).",
      );
      return;
    }

    if (
      appointmentForm.isTransportationScheduled &&
      appointmentForm.transportContactNumber &&
      appointmentForm.transportContactNumber.length !== 10
    ) {
      setAppointmentError("Transport Contact Number must be exactly 10 digits.");
      return;
    }

    const formattedDate = format(appointmentForm.dateTime as Date, "yyyy-MM-dd");
    const formattedTime = format(appointmentForm.dateTime as Date, "HH:mm");

    const isDuplicate = appointments.some((appt) => {
      if (editingAppointmentId && appt.id === editingAppointmentId) {
        return false;
      }

      const existingDate = appt.appointmentDate;
      let existingTime = appt.appointmentTime || "";
      if (existingTime.length >= 5) {
        existingTime = existingTime.substring(0, 5);
      }

      return existingDate === formattedDate && existingTime === formattedTime;
    });

    if (isDuplicate) {
      setAppointmentInfo("This resident already has a medical appointment scheduled for the selected date and time. Please update the existing appointment instead of creating a new one.");
      setAppointmentError(null);
      setAppointmentSuccess(null);
      setTimeout(() => setAppointmentInfo(null), 5000);
      return;
    }

    setIsSavingAppointment(true);
    setAppointmentError(null);
    setAppointmentSuccess(null);
    setAppointmentInfo(null);

    try {
      if (editingAppointmentId) {
        // Update existing appointment
        await appointmentService.updateAppointment(editingAppointmentId, {
          appointmentDate: format(appointmentForm.dateTime as Date, "yyyy-MM-dd"),
          appointmentTime: format(appointmentForm.dateTime as Date, "HH:mm"),
          userId: appointmentForm.doctor, // Map Doctor Input to UserId
          doctorName: appointmentForm.doctor,
          doctorId: appointmentForm.doctor,
          residentId: String(effectiveResidentId),
          reason: appointmentForm.reason,
          appointmentType: appointmentForm.type,
          hospitalName: appointmentForm.hospitalName,
          facilityId: prefilledResident?.facilityId || "",
          includeInBilling: appointmentForm.includeInBilling,
          isTransportationScheduled: appointmentForm.isTransportationScheduled,
          mobilityType: appointmentForm.isTransportationScheduled ? appointmentForm.mobilityType : "",
          vendor: appointmentForm.isTransportationScheduled ? appointmentForm.vendor : "",
          transportContactNumber: appointmentForm.isTransportationScheduled ? appointmentForm.transportContactNumber : "",
        });
        setAppointmentSuccess("Medical appointment updated successfully!");
      } else {
        // Create new appointment
        await appointmentService.createAppointment({
          appointmentDate: format(appointmentForm.dateTime as Date, "yyyy-MM-dd"),
          appointmentTime: format(appointmentForm.dateTime as Date, "HH:mm"),
          userId: appointmentForm.doctor, // Map Doctor Input to UserId
          doctorName: appointmentForm.doctor,
          doctorId: appointmentForm.doctor,
          residentId: String(effectiveResidentId),
          reason: appointmentForm.reason,
          appointmentType: appointmentForm.type,
          hospitalName: appointmentForm.hospitalName,
          facilityId: prefilledResident?.facilityId || "",
          includeInBilling: appointmentForm.includeInBilling,
          isTransportationScheduled: appointmentForm.isTransportationScheduled,
          mobilityType: appointmentForm.isTransportationScheduled ? appointmentForm.mobilityType : "",
          vendor: appointmentForm.isTransportationScheduled ? appointmentForm.vendor : "",
          transportContactNumber: appointmentForm.isTransportationScheduled ? appointmentForm.transportContactNumber : "",
        });
        setAppointmentSuccess("Medical appointment saved successfully!");
      }

      fetchActivityTimeline();

      setAppointmentForm({
        dateTime: null,
        doctor: "",
        hospitalName: "",
        type: "",
        reason: "",
        includeInBilling: false,
        isTransportationScheduled: false,
        mobilityType: "",
        vendor: "",
        transportContactNumber: "",
      });
      setEditingAppointmentId(null);
      setTimeout(() => setAppointmentSuccess(null), 3000);
      setAppointmentInfo(null);
    } catch (err: any) {
      console.error("Appointment save error", err);
      setAppointmentError(
        err.response?.data?.message ||
        "Failed to save appointment. Please try again.",
      );
    } finally {
      setIsSavingAppointment(false);
    }
  };

  const handlePrintClinicDocument = async (residentId: string) => {
    if (!residentId) return;

    if (printingId) {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      setPrintingId(null);
      if (printingId === residentId) return;
    }

    setPrintingId(residentId);
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const blob = await residentService.getClinicDocumentPdf(residentId);
      if (controller.signal.aborted) return;

      const url = window.URL.createObjectURL(blob);
      const iframe = document.createElement("iframe");
      iframe.style.visibility = "hidden";
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "1px";
      iframe.style.height = "1px";
      iframe.style.border = "0";
      iframe.src = url;
      document.body.appendChild(iframe);

      const cleanup = () => {
        if (document.body.contains(iframe)) {
          document.body.removeChild(iframe);
        }
        window.URL.revokeObjectURL(url);
      };

      iframe.onload = () => {
        if (controller.signal.aborted) {
          cleanup();
          return;
        }

        setPrintingId(null);
        abortControllerRef.current = null;

        try {
          if (iframe.contentWindow) {
            iframe.contentWindow.focus();
            // Use onafterprint to cleanup immediately after dialog is dismissed
            iframe.contentWindow.onafterprint = cleanup;
            iframe.contentWindow.print();
          }
        } catch (printErr) {
          console.error("Direct print failed:", printErr);
          cleanup();
        }
      };

      // Increase safety timeout to 15 minutes to prevent premature closing of print preview in Chrome
      setTimeout(cleanup, 900000);

    } catch (err: any) {
      if (err.name === 'AbortError') {
        console.log("Print request cancelled");
      } else {
        console.error("Error printing clinic document:", err);
      }
      setPrintingId(null);
      abortControllerRef.current = null;
    }
  };

  /*
    const handleDeleteAppointment = async (id: string) => {
      if (window.confirm("Are you sure you want to delete this appointment?")) {
        try {
          await appointmentService.deleteAppointment(id);
          const facilityId = prefilledResident?.facilityId;
          const data = await appointmentService.getAppointments(
            String(effectiveResidentId),
            facilityId,
          );
          // Ensure strictly filtered by residentId
          const filteredData = Array.isArray(data)
            ? data.filter(
              (appt: any) =>
                appt.residentId &&
                String(appt.residentId) === String(effectiveResidentId),
            )
            : [];
          setAppointments(filteredData);
          fetchActivityTimeline();
        } catch (error) {
          console.error("Failed to delete appointment", error);
          setAppointmentError("Failed to delete appointment");
        }
      }
    };
  */

  const latestVital = vitalsHistory.length > 0 ? vitalsHistory[0] : null;

  const displayBloodPressure = useMemo(() => {
    const latestVitalWithBp = vitalsHistory.find((v: any) => v.dailyBloodPressure || v.bloodPressure);
    const fromLatestVital = latestVitalWithBp?.dailyBloodPressure || latestVitalWithBp?.bloodPressure;
    if (fromLatestVital) return fromLatestVital;

    const fromBanner =
      residentBanner?.bp && residentBanner.bp !== "N/A" ? residentBanner.bp : null;
    if (fromBanner) return fromBanner;

    return fullResidentData?.bloodPressure || "N/A";
  }, [vitalsHistory, residentBanner?.bp, fullResidentData?.bloodPressure]);

  const displayWeight = useMemo(() => {
    const latestVitalWithWeight = vitalsHistory.find((v: any) => v.dailyWeight || v.weight);
    const fromLatestVital = latestVitalWithWeight?.dailyWeight || latestVitalWithWeight?.weight;
    if (fromLatestVital) return fromLatestVital;

    const fromBanner =
      residentBanner?.weight && residentBanner.weight !== "N/A" ? residentBanner.weight : null;
    if (fromBanner) return fromBanner;

    return fullResidentData?.weight || "N/A";
  }, [vitalsHistory, residentBanner?.weight, fullResidentData?.weight]);

  // Fetch Resident and Vitals on selection
  useEffect(() => {
    if (effectiveResidentId) {
      // Fetch Resident
      residentService
        .getResidentById(String(effectiveResidentId))
        .then((res: ResidentDTO) => {
          setFullResidentData(res);
          // Map to patient details shape used by banner
          // We manually map because the shape expected by prefilledResident is flat
          const bannerData = {
            id: res.id,
            facilityId: res.facilityId,
            name: `${res.firstName} ${res.middleName ? res.middleName + " " : ""}${res.lastName}`.trim(),
            EMRNo: res.mrnNumber || res.medicalRecordNumber || res.emrNumber || String(res.id) || "N/A",
            bp: res.bloodPressure || "N/A",
            weight: res.weight !== undefined ? String(res.weight) : "N/A",
            height: res.height !== undefined ? String(res.height) : "N/A",
            bmi: res.bmi !== undefined ? String(res.bmi) : "N/A",
            bloodGroup: res.bloodGroup || "N/A",
            room:
              res.roomNumber && res.bedNumber
                ? `${res.roomNumber}-${res.bedNumber}`
                : res.roomNumber || "Not Assigned",
            sex: res.gender || "N/A",
            status: res.status || "ACTIVE",
            dob: res.dateOfBirth || "N/A",
            attendingPhysician: res.attendingPhysicianName || "N/A",
            allergies: res.allergies
              ? String(res.allergies)
                .split(",")
                .map((a: string) => a.trim())
                .filter(Boolean)
              : [],
            profilePictureUrl: res.profilePictureUrl,
            admissionType: res.admissionType || res.careType || "N/A",
          };
          setPrefilledResident(bannerData);
          setInsuranceCards(res.insuranceCards || []);
        })
        .catch((err) => console.error("Failed to fetch resident details", err));


    }
  }, [effectiveResidentId]);

  // ─── Resident search handlers ───────────────────────────────────────────────
  const handleResidentSearchChange = (query: string) => {
    setResidentSearchQuery(query);
    setResidentSearchOpen(true);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    if (!query.trim()) {
      setResidentSearchResults([]);
      return;
    }
    searchDebounceRef.current = setTimeout(async () => {
      setIsSearchingResidents(true);
      try {
        const q = query.toLowerCase().trim();
        // Use the pre-fetched residents list from getActiveLookup API
        const filtered = (residents || []).filter((r: any) => {
          const name = (r.residentName || `${r.firstName || ""} ${r.middleName || ""} ${r.lastName || ""}`).toLowerCase();
          const mrn = (r.mrnNumber || r.medicalRecordNumber || r.emrNumber || "").toLowerCase();
          const room = (r.roomNumber || "").toLowerCase();
          return name.includes(q) || mrn.includes(q) || room.includes(q);
        });
        setResidentSearchResults(filtered.slice(0, 15));
      } catch (err) {
        console.error("Resident search failed", err);
        setResidentSearchResults([]);
      } finally {
        setIsSearchingResidents(false);
      }
    }, 300);

  };

  const handleSelectResidentFromSearch = async (resident: any) => {
    // Clear search query on selection for better UX
    setResidentSearchQuery("");
    setResidentSearchOpen(false);
    setSelectedResident(String(resident.id));
    // Trigger the same banner + data load that navigation triggers
    try {
      const res: ResidentDTO = await residentService.getResidentById(String(resident.id));
      setFullResidentData(res);
      setInsuranceCards(res.insuranceCards || []);
      setPrefilledResident({
        id: res.id,
        facilityId: res.facilityId,
        name: `${res.firstName} ${res.middleName ? res.middleName + " " : ""}${res.lastName}`.trim(),
        EMRNo: res.mrnNumber || res.medicalRecordNumber || res.emrNumber || String(res.id) || "N/A",
        bp: res.bloodPressure || "N/A",
        weight: res.weight !== undefined ? String(res.weight) : "N/A",
        height: res.height !== undefined ? String(res.height) : "N/A",
        bmi: res.bmi !== undefined ? String(res.bmi) : "N/A",
        room: res.roomNumber && res.bedNumber ? `${res.roomNumber}-${res.bedNumber}` : res.roomNumber || "Not Assigned",
        sex: res.gender || "N/A",
        status: res.status || "ACTIVE",
        dob: res.dateOfBirth || "N/A",
        attendingPhysician: res.attendingPhysicianName || "N/A",
        allergies: res.allergies ? String(res.allergies).split(",").map((a: string) => a.trim()).filter(Boolean) : [],
        profilePictureUrl: res.profilePictureUrl,
        admissionType: res.admissionType || res.careType || "N/A",
      });
    } catch (e) {
      console.error("Failed to load resident from search", e);
    }
  };

  return (
    <Box sx={{ height: "calc(100vh - 60px)", display: "flex", flexDirection: "column", overflow: "visible" }}>
      <Box sx={{ px: 3, pt: 3, flexShrink: 0 }}>
        {/* ── Resident Search ── */}
        <Paper
          elevation={0}
          sx={{
            mb: 1.5,
            p: 1.5,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 3,
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
            background: "linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)",
            boxShadow: '0 2px 10px rgba(0,0,0,0.03)'
          }}
        >
          {!effectiveResidentId ? (
            <Box sx={{ position: "relative", flexGrow: 1 }}>
              <Autocomplete
                id="resident-selector"
                options={residents.slice().sort((a: any, b: any) => {
                  const nameA = (a.residentName || `${a.firstName || ""} ${a.lastName || ""}`).toLowerCase().trim();
                  const nameB = (b.residentName || `${b.firstName || ""} ${b.lastName || ""}`).toLowerCase().trim();
                  return nameA.localeCompare(nameB);
                }).filter((r: any) => r?.status?.toUpperCase() === "ACTIVE")}
                getOptionLabel={(option: any) =>
                  `${option.residentName || `${option.firstName || ''} ${option.lastName || ''}`.trim()} ${option.roomNumber ? `(Room: ${option.roomNumber}${option.bedNumber ? `, Bed: ${option.bedNumber}` : ""})` : ""}`
                }
                loading={residentsLoading}
                value={null}
                onChange={(event: any, newValue: any | null) => {
                  if (newValue) {
                    handleSelectResidentFromSearch(newValue);
                  }
                }}
                inputValue={residentSearchQuery}
                onInputChange={(event, newInputValue) => {
                  setResidentSearchQuery(newInputValue);
                }}
                isOptionEqualToValue={(option: any, value: any) => option.id === value?.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search Resident by Name"
                    variant="outlined"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <React.Fragment>
                          {residentsLoading ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                        backgroundColor: "#f5f7f8",
                      },
                    }}
                  />
                )}
                sx={{ flex: 1 }}
              />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexGrow: 1 }}>
              <Typography variant="subtitle1" fontWeight={700} color="text.secondary">
                Currently Viewing:
              </Typography>
              <Chip
                avatar={
                  <ResidentAvatar
                    residentId={prefilledResident?.id}
                    residentName={prefilledResident?.name}
                    fallbackUrl={prefilledResident?.profilePictureUrl}
                    refreshTrigger={photoRefreshTrigger}
                    sx={{ bgcolor: "primary.main", fontSize: "0.75rem", fontWeight: 'bold' }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontWeight: 700, px: 0.5 }}>
                    {prefilledResident?.name}
                  </Typography>
                }
                onDelete={() => {
                  setSelectedResident(null);
                  setPrefilledResident(null);
                  setFullResidentData(null);
                  setHasProfilePhoto(false);
                  setResidentSearchQuery("");
                }}
                color="primary"
                variant="filled"
                sx={{
                  height: 48,
                  borderRadius: 3,
                  boxShadow: '0 4px 12px rgba(33, 150, 243, 0.2)',
                  '& .MuiChip-label': { display: 'flex', alignItems: 'center' },
                  '& .MuiChip-deleteIcon': { color: 'white', '&:hover': { color: 'grey.200' } }
                }}
              />
              <Button
                startIcon={<SearchIcon />}
                onClick={() => {
                  setSelectedResident(null);
                  setPrefilledResident(null);
                  setFullResidentData(null);
                  setHasProfilePhoto(false);
                  setResidentSearchQuery("");
                  setResidentSearchOpen(false);
                }}
                sx={{ ml: 'auto', color: 'text.secondary' }}
              >
                Switch Resident
              </Button>
            </Box>
          )}
        </Paper>
      </Box>

      {/* ── Scrollable Content Area ── */}
      <Box sx={{ flexGrow: 1, overflowY: "auto", px: 3, pb: 4 }}>

        {!effectiveResidentId ? (
          <Box sx={{ mt: 4, textAlign: "center", p: 8, bgcolor: "background.paper", borderRadius: 3, border: "1px dashed", borderColor: "divider" }}>
            <SearchIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2, opacity: 0.3 }} />
            <Typography variant="h5" color="text.primary" fontWeight={700} gutterBottom>
              No Resident Selected
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 400, mx: "auto" }}>
              Please search and select a resident from the search bar above to view their clinical details, medications, care plan, and documents.
            </Typography>
          </Box>
        ) : (
          <>
            <Typography variant="h5" fontWeight="700" sx={{ mb: 1.5, color: '#1A2B45' }}>
              Resident Details
            </Typography>

            {
              vitalsError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setVitalsError(null)}>
                  {vitalsError}
                </Alert>
              )
            }
            {
              vitalsSuccess && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setVitalsSuccess(null)}>
                  {vitalsSuccess}
                </Alert>
              )
            }
            <Card
              elevation={0}
              sx={{
                mb: 3,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 3,
                background: '#ffffff'
              }}
            >
              <CardContent sx={{ p: { xs: 1.5, md: 2 }, '&:last-child': { pb: { xs: 1.5, md: 2 } } }}>
                {residentBanner ? (
                  <Grid
                    container
                    spacing={2}
                    alignItems="stretch"
                  >
                    {/* LEFT COLUMN: Avatar & Profile Info */}
                    <Grid item xs={12} md={5.5} sx={{ display: 'flex', gap: 2.5, alignItems: 'center' }}>
                      {/* Avatar container */}
                      <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, ml: 2 }}>
                        <input
                          type="file"
                          hidden
                          ref={photoFileInputRef}
                          onChange={handlePhotoChange}
                          accept="image/*"
                        />
                        <Box sx={{ position: 'relative' }}>
                          <ResidentAvatar
                            residentId={fullResidentData?.id}
                            residentName={residentBanner.name}
                            fallbackUrl={fullResidentData?.profilePictureUrl}
                            refreshTrigger={photoRefreshTrigger}
                            sx={{
                              width: 96,
                              height: 96,
                              fontSize: "2.3rem",
                              bgcolor: "primary.main",
                              boxShadow: '0 4px 10px rgba(0,0,0,0.05)'
                            }}
                          />
                          {isPhotoUploading && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                borderRadius: '50%',
                                bgcolor: 'rgba(0,0,0,0.4)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 2,
                              }}
                            >
                              <CircularProgress size={20} color="inherit" sx={{ color: 'white' }} />
                            </Box>
                          )}
                          <Tooltip title="Update Profile Photo">
                            <IconButton
                              size="small"
                              onClick={handleProfileMenuOpen}
                              sx={{
                                position: 'absolute',
                                bottom: 0,
                                right: 0,
                                bgcolor: 'background.paper',
                                boxShadow: 2,
                                '&:hover': { bgcolor: 'grey.100' },
                                border: '1px solid',
                                borderColor: 'divider',
                                p: 0.5,
                              }}
                            >
                              <EditIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                            </IconButton>
                          </Tooltip>
                        </Box>

                        <Menu
                          anchorEl={profileMenuAnchor}
                          open={Boolean(profileMenuAnchor)}
                          onClose={handleProfileMenuClose}
                          anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                          }}
                          transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                          }}
                        >
                          <MenuItem onClick={handlePhotoClick}>
                            <PhotoCameraIcon sx={{ mr: 1, fontSize: 18 }} /> Upload Photo
                          </MenuItem>
                          {hasProfilePhoto && (
                            <MenuItem onClick={handleRemovePhoto} sx={{ color: 'error.main' }}>
                              <DeleteIcon sx={{ mr: 1, fontSize: 18 }} /> Remove Photo
                            </MenuItem>
                          )}
                        </Menu>

                        <Dialog open={isRemovePhotoDialogOpen} onClose={() => setIsRemovePhotoDialogOpen(false)}>
                          <DialogTitle>Remove Profile Photo</DialogTitle>
                          <DialogContent>
                            <Typography>Are you sure you want to remove this resident's profile photo?</Typography>
                          </DialogContent>
                          <DialogActions sx={{ px: 3, pb: 2.5 }}>
                            <Button
                              onClick={() => setIsRemovePhotoDialogOpen(false)}
                              variant="outlined"
                              color="inherit"
                              disabled={isPhotoUploading}
                            >
                              Cancel
                            </Button>
                            <Button
                              onClick={confirmRemovePhoto}
                              color="error"
                              variant="contained"
                              disabled={isPhotoUploading}
                              startIcon={isPhotoUploading ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
                            >
                              {isPhotoUploading ? "Removing..." : "Remove"}
                            </Button>
                          </DialogActions>
                        </Dialog>
                      </Box>

                      {/* Profile details text */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, ml: 6 }}>
                        <Typography variant="h5" fontWeight="900" color="#1e293b" sx={{ fontSize: '1.8rem !important', lineHeight: 1.5, letterSpacing: '-0.3px' }}>
                          {residentBanner.name}
                        </Typography>

                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.5 }}>
                          MRN: {residentBanner.EMRNo || residentBanner.mrnNumber || "N/A"}
                        </Typography>

                        {/* SSN Field */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                            SSN:
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                            ****
                            {/* {showSSN ? (fullResidentData?.ssn || "N/A") : "****"} */}
                          </Typography>
                          {/* <IconButton size="small" onClick={() => setShowSSN(!showSSN)} sx={{ p: 0.25, color: 'warning.main', ml: 0.5 }}>
                            {showSSN ? <VisibilityOffIcon sx={{ fontSize: 16 }} /> : <VisibilityIcon sx={{ fontSize: 16 }} />}
                          </IconButton> */}
                        </Box>

                        {/* Outline Demographics Chips */}
                        <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                          <Box sx={{ border: '1px solid #e2e8f0', borderRadius: '8px', px: 1.5, py: 0.5, fontSize: '0.8125rem', color: '#475569', fontWeight: '600', lineHeight: 1.5 }}>
                            Age: {residentBanner.dob ? calcAge(residentBanner.dob) : "N/A"}
                          </Box>
                          <Box sx={{ border: '1px solid #e2e8f0', borderRadius: '8px', px: 1.5, py: 0.5, fontSize: '0.8125rem', color: '#475569', fontWeight: '600', lineHeight: 1.5 }}>
                            Gender  : {residentBanner.sex}
                          </Box>
                          <Box sx={{ border: '1px solid #e2e8f0', borderRadius: '8px', px: 1.5, py: 0.5, fontSize: '0.8125rem', color: '#475569', fontWeight: '600', lineHeight: 1.5 }}>
                            Room: {residentBanner.room}
                          </Box>
                        </Box>
                      </Box>
                    </Grid>

                    {/* VERTICAL DIVIDER */}
                    <Grid item xs="auto" sx={{ display: { xs: 'none', md: 'block' } }}>
                      <Divider orientation="vertical" sx={{ height: '100%', borderColor: '#e2e8f0' }} />
                    </Grid>

                    {/* RIGHT COLUMN: Attending Physician, Allergies, Language, Status, SSN, Admission Date */}
                    <Grid item xs={12} md>
                      <Box
                        sx={{
                          bgcolor: '#f8fafc',
                          borderRadius: 2,
                          p: { xs: 1, md: 1.5 },
                          border: '1px solid #f1f5f9',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        <Grid container spacing={1.5}>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary" fontWeight="600" display="block" sx={{ mb: 0.25 }}>
                              Attending Physician
                            </Typography>
                            <Typography variant="body2" color="text.primary" fontWeight="700">
                              {residentBanner.attendingPhysician || "Unassigned"}
                            </Typography>
                          </Grid>

                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary" fontWeight="600" display="block" sx={{ mb: 0.25 }}>
                              Known Allergies
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {!residentBanner.allergies || residentBanner.allergies.length === 0 ? (
                                <Typography variant="body2" color="text.primary" fontWeight="700">
                                  None Known
                                </Typography>
                              ) : (
                                residentBanner.allergies.map((a: string) => (
                                  <Typography key={a} variant="body2" color="error.main" fontWeight="700">
                                    {a}
                                  </Typography>
                                ))
                              )}
                            </Box>
                          </Grid>

                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary" fontWeight="600" display="block" sx={{ mb: 0.25 }}>
                              Level of Care
                            </Typography>
                            <Typography variant="body2" color="text.primary" fontWeight="700">
                              {(() => {
                                const type = residentBanner.admissionType || fullResidentData?.admissionType || "N/A";
                                const mapping: Record<string, string> = {
                                  ALF: "Assisted Living Facility",
                                  ASSISTED_LIVING: "Assisted Living Facility",
                                  HOSPICE: "Hospice Care"
                                };
                                return mapping[type] || type;
                              })()}
                            </Typography>
                          </Grid>

                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary" fontWeight="600" display="block" sx={{ mb: 0.25 }}>
                              DNR Status
                            </Typography>
                            {fullResidentData?.dnrStatus ? (
                              <Chip
                                label={fullResidentData.dnrStatus}
                                size="small"
                                color={
                                  String(fullResidentData.dnrStatus).toUpperCase().includes("YES") ||
                                    String(fullResidentData.dnrStatus).toUpperCase().includes("DNR") ||
                                    String(fullResidentData.dnrStatus).toUpperCase().includes("DNI")
                                    ? "error" : "success"
                                }
                                sx={{ fontWeight: "bold", fontSize: "0.75rem" }}
                              />
                            ) : (
                              <Typography variant="body2" color="text.primary" fontWeight="700">&mdash;</Typography>
                            )}
                          </Grid>

                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary" fontWeight="600" display="block" sx={{ mb: 0.25 }}>
                              Admission Date
                            </Typography>
                            <Typography variant="body2" color="text.primary" fontWeight="700">
                              {formatToUIDate(residentBanner.admissionDate || fullResidentData?.admissionDate) || "-"}
                            </Typography>
                          </Grid>

                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary" fontWeight="600" display="block" sx={{ mb: 0.25 }}>
                              Resident Status
                            </Typography>
                            <FormControl fullWidth size="small">
                              <Select
                                value={residentBanner.status || "ACTIVE"}
                                onChange={(e) => handleStatusChangeRequest(e.target.value)}
                                disabled={
                                  isUpdatingStatus ||
                                  (() => {
                                    const uRole = String(user?.role || "").toUpperCase().replace(/^ROLE_/, "");
                                    return !(
                                      uRole === "ADMIN" ||
                                      uRole === "FACILITY_ADMIN" ||
                                      uRole === "SUPER_ADMIN" ||
                                      uRole === "CLIENT_ADMIN" ||
                                      uRole === "CAREGIVER" ||
                                      uRole === "LEAD_CAREGIVER" ||
                                      uRole === "NURSE"
                                    );
                                  })()
                                }
                                sx={{
                                  bgcolor: 'background.paper',
                                  fontWeight: '600',
                                  borderRadius: 2,
                                  '& .MuiSelect-select': { py: 0.5, px: 1.5, fontSize: '0.875rem' }
                                }}
                              >
                                {statusOptions.map((opt) => (
                                  <MenuItem key={opt.valueName} value={opt.valueName}>{opt.valueName}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                        </Grid>
                      </Box>
                    </Grid>
                  </Grid>
                ) : (
                  <Box py={3} textAlign="center">
                    <Typography variant="body1" color="text.secondary">
                      Please select a resident from the Residents list to view and manage their clinical details.
                    </Typography>
                  </Box>
                )}
              </CardContent>

              {/* VITALS PANEL ROW (integrated inside the card to eliminate duplicate borders & gaps, exactly like screenshot) */}
              {residentBanner && (
                <>
                  <Divider sx={{ borderColor: 'divider' }} />
                  <Box
                    sx={{
                      p: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: 1,
                      background: '#ffffff',
                      borderBottomLeftRadius: 12,
                      borderBottomRightRadius: 12
                    }}
                  >
                    {/* Blood Pressure */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 150, justifyContent: 'center' }}>
                      <BloodPressureSvgIcon sx={{ color: '#64748b', fontSize: 26 }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight="600" display="block">
                          Blood Pressure
                        </Typography>
                        <Typography variant="body2" color="text.primary" fontWeight="700">
                          {displayBloodPressure}
                        </Typography>
                      </Box>
                    </Box>

                    <Divider orientation="vertical" flexItem sx={{ borderRightWidth: 1, borderColor: '#e2e8f0' }} />

                    {/* Height */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 120, justifyContent: 'center' }}>
                      <StraightenIcon sx={{ color: '#64748b', fontSize: 26, transform: 'rotate(90deg)' }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight="600" display="block">
                          Height
                        </Typography>
                        <Typography variant="body2" color="text.primary" fontWeight="700">
                          {residentBanner.height ? `${residentBanner.height} cm` : "170 cm"}
                        </Typography>
                      </Box>
                    </Box>

                    <Divider orientation="vertical" flexItem sx={{ borderRightWidth: 1, borderColor: '#e2e8f0' }} />

                    {/* Weight */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 120, justifyContent: 'center' }}>
                      <WeightSvgIcon sx={{ color: '#64748b', fontSize: 26 }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight="600" display="block">
                          Weight
                        </Typography>
                        <Typography variant="body2" color="text.primary" fontWeight="700">
                          {displayWeight !== "N/A" ? `${displayWeight} kg` : "N/A"}
                        </Typography>
                      </Box>
                    </Box>

                    <Divider orientation="vertical" flexItem sx={{ borderRightWidth: 1, borderColor: '#e2e8f0' }} />

                    {/* BMI */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 100, justifyContent: 'center' }}>
                      <BmiSvgIcon sx={{ color: '#64748b', fontSize: 26 }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight="600" display="block">
                          BMI
                        </Typography>
                        <Typography variant="body2" color="text.primary" fontWeight="700">
                          {(() => {
                            if (displayWeight === "N/A") return residentBanner.bmi || "25.9";
                            const w = parseFloat(displayWeight as string);
                            let h = parseFloat(residentBanner.height);
                            if (!w || !h || isNaN(w) || isNaN(h)) return residentBanner.bmi || "25.9";
                            if (h > 10) { h = h / 100; } else { h = h * 0.3048; }
                            const bmiVal = w / (h * h);
                            return isFinite(bmiVal) ? bmiVal.toFixed(1) : "25.9";
                          })()}
                        </Typography>
                      </Box>
                    </Box>

                    <Divider orientation="vertical" flexItem sx={{ borderRightWidth: 1, borderColor: '#e2e8f0' }} />

                    {/* Blood Group */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 120, justifyContent: 'center' }}>
                      <BloodGroupSvgIcon sx={{ color: '#64748b', fontSize: 26 }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight="600" display="block">
                          Blood Group
                        </Typography>
                        <Typography variant="body2" color="text.primary" fontWeight="700">
                          {residentBanner.bloodGroup || "N/A"}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </>
              )}
            </Card>

            <Grid container spacing={3}>
              {/* Main Content Area */}
              <Grid item xs={12} md={isTimelineExpanded ? 8.5 : 11.5} sx={{ transition: 'all 0.3s ease' }}>
                <Paper sx={{ p: 2, mb: 3 }}>
                  <Tabs
                    value={topTab}
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    onChange={(_, v) => {
                      setTopTab(v);
                      setMedicationError(null);
                      if (v === 2) {
                        setEmrTab(0);
                      }
                    }}
                  >
                    <Tab label="Facesheet" value={0} />
                    <Tab label="Prescribe Medication" value={1} />
                    <Tab label="MAR" value={2} />
                    <Tab label="Care Plan" value={3} />
                    <Tab label="Medical Appointment" value={4} />
                    <Tab label="Documents" value={5} />
                    <Tab label="Prescribe Pharmacy" value={6} />
                  </Tabs>

                  {/* Facesheet Tab */}
                  {topTab === 0 && (
                    <Paper sx={{ p: 3 }}>
                      {/* Header with Edit/Save/Cancel Buttons */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Typography variant="h6" color="text.primary" sx={{ fontWeight: 800 }}>
                          Resident Facesheet
                        </Typography>
                        <Box>
                          {isFacesheetEditing ? (
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button
                                size="small"
                                variant="outlined"
                                color="inherit"
                                onClick={handleToggleFacesheetEdit}
                                disabled={isUpdatingStatus}
                                sx={{ textTransform: 'none', fontWeight: 600 }}
                              >
                                Cancel
                              </Button>
                              <Button
                                size="small"
                                variant="contained"
                                startIcon={<CheckCircleIcon />}
                                onClick={handleSaveFacesheet}
                                disabled={isUpdatingStatus}
                                sx={{
                                  textTransform: 'none',
                                  fontWeight: 600,
                                  bgcolor: "#48CAE4",
                                  color: "#000000",
                                  "&:hover": { bgcolor: "#3ab3cc" }
                                }}
                              >
                                {isUpdatingStatus ? "Saving..." : "Save Changes"}
                              </Button>
                            </Box>
                          ) : (
                            isResidentActive && !isOnlyCaregiver && (
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<EditIcon />}
                                onClick={handleToggleFacesheetEdit}
                                sx={{ textTransform: 'none', fontWeight: 600 }}
                              >
                                Edit Facesheet
                              </Button>
                            )
                          )}
                        </Box>
                      </Box>

                      {!fullResidentData ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                          <CircularProgress size={24} />
                          <Typography sx={{ ml: 2 }}>Loading resident details...</Typography>
                        </Box>
                      ) : (
                        <Grid container spacing={3}>
                          {/* Left Column: Essential Information Sections */}
                          <Grid item xs={12} md={6}>
                            <Grid container spacing={3}>
                              {/* 1. Personal Information */}
                              <Grid item xs={12}>
                                <Card
                                  variant="outlined"
                                  sx={{
                                    height: '100%',
                                    borderRadius: 3,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    overflow: 'hidden',
                                    '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.04)' }
                                  }}
                                >
                                  <Box sx={{
                                    p: 1.5,
                                    px: 2,
                                    bgcolor: alpha(theme.palette.primary.main, 0.03),
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                  }}>
                                    <PersonIcon fontSize="small" sx={{ color: "#48CAE4" }} />
                                    <Typography variant="subtitle2" fontWeight="800" color="text.primary">
                                      Personal Information
                                    </Typography>
                                  </Box>
                                  <CardContent sx={{ p: 3, pt: 2 }}>
                                    <Grid container spacing={3} rowSpacing={3}>
                                      <Grid item xs={12} sm={4}>
                                        {isFacesheetEditing ? (
                                          <TextField
                                            label={<span>First Name <span style={{ color: '#d32f2f' }}>*</span></span>}
                                            fullWidth
                                            size="small"
                                            error={!!facesheetErrors.firstName}
                                            helperText={facesheetErrors.firstName || ""}
                                            value={facesheetForm.firstName || ""}
                                            onChange={(e) => {
                                              const value = e.target.value;
                                              // Only allow alphabetic characters, spaces, hyphens, and apostrophes
                                              if (value === "" || /^[a-zA-Z\s'-]*$/.test(value)) {
                                                setFacesheetForm({ ...facesheetForm, firstName: value });
                                                setFacesheetErrors({ ...facesheetErrors, firstName: "" });
                                              }
                                            }}
                                          />
                                        ) : (
                                          <>
                                            <Typography variant="body2" color="text.secondary" fontWeight="600">First Name</Typography>
                                            <Typography variant="body2" fontWeight="700" color="text.primary">{fullResidentData?.firstName || "-"}</Typography>
                                          </>
                                        )}
                                      </Grid>
                                      <Grid item xs={12} sm={4}>
                                        {isFacesheetEditing ? (
                                          <TextField
                                            label="Middle Name"
                                            fullWidth
                                            size="small"
                                            error={!!facesheetErrors.middleName}
                                            helperText={facesheetErrors.middleName || ""}
                                            value={facesheetForm.middleName || ""}
                                            onChange={(e) => {
                                              const value = e.target.value;
                                              // Only allow alphabetic characters, spaces, hyphens, and apostrophes
                                              if (value === "" || /^[a-zA-Z\s'-]*$/.test(value)) {
                                                setFacesheetForm({ ...facesheetForm, middleName: value });
                                                setFacesheetErrors({ ...facesheetErrors, middleName: "" });
                                              }
                                            }}
                                          />
                                        ) : (
                                          <>
                                            <Typography variant="body2" color="text.secondary" fontWeight="600">Middle Name</Typography>
                                            <Typography variant="body2" fontWeight="700" color="text.primary">{fullResidentData?.middleName || "-"}</Typography>
                                          </>
                                        )}
                                      </Grid>
                                      <Grid item xs={12} sm={4}>
                                        {isFacesheetEditing ? (
                                          <TextField
                                            label={<span>Last Name <span style={{ color: '#d32f2f' }}>*</span></span>}
                                            fullWidth
                                            size="small"
                                            error={!!facesheetErrors.lastName}
                                            helperText={facesheetErrors.lastName || ""}
                                            value={facesheetForm.lastName || ""}
                                            onChange={(e) => {
                                              const value = e.target.value;
                                              // Only allow alphabetic characters, spaces, hyphens, and apostrophes
                                              if (value === "" || /^[a-zA-Z\s'-]*$/.test(value)) {
                                                setFacesheetForm({ ...facesheetForm, lastName: value });
                                                setFacesheetErrors({ ...facesheetErrors, lastName: "" });
                                              }
                                            }}
                                          />
                                        ) : (
                                          <>
                                            <Typography variant="body2" color="text.secondary" fontWeight="600">Last Name</Typography>
                                            <Typography variant="body2" fontWeight="700" color="text.primary">{fullResidentData?.lastName || "-"}</Typography>
                                          </>
                                        )}
                                      </Grid>
                                      <Grid item xs={12} sm={4}>
                                        {isFacesheetEditing ? (
                                          <LocalizationProvider dateAdapter={AdapterDateFns}>
                                            <DatePicker
                                              label={<span>DOB <span style={{ color: '#d32f2f' }}>*</span></span>}
                                              format="dd/MM/yyyy"
                                              disableFuture
                                              value={facesheetForm.dateOfBirth ? parseISO(facesheetForm.dateOfBirth) : null}
                                              onChange={(date) => {
                                                setFacesheetForm({ ...facesheetForm, dateOfBirth: date ? format(date, "yyyy-MM-dd") : "" });
                                                if (facesheetErrors.dateOfBirth) setFacesheetErrors({ ...facesheetErrors, dateOfBirth: "" });
                                              }}
                                              slotProps={{
                                                textField: {
                                                  fullWidth: true,
                                                  size: "small",
                                                  error: !!facesheetErrors.dateOfBirth,
                                                  helperText: facesheetErrors.dateOfBirth || ""
                                                },
                                              }}
                                            />
                                          </LocalizationProvider>
                                        ) : (
                                          <>
                                            <Typography variant="body2" color="text.secondary" fontWeight="600">Date of Birth</Typography>
                                            <Typography variant="body2" fontWeight="700" color="text.primary">{formatToUIDate(fullResidentData?.dateOfBirth) || "-"}</Typography>
                                          </>
                                        )}
                                      </Grid>
                                      <Grid item xs={12} sm={4}>
                                        {isFacesheetEditing ? (
                                          <FormControl fullWidth size="small" error={!!facesheetErrors.gender}>
                                            <InputLabel><span>Gender <span style={{ color: '#d32f2f' }}>*</span></span></InputLabel>
                                            <Select
                                              label={<span>Gender <span style={{ color: '#d32f2f' }}>*</span></span>}
                                              value={facesheetForm.gender || ""}
                                              onChange={(e) => {
                                                setFacesheetForm({ ...facesheetForm, gender: e.target.value as string });
                                                if (facesheetErrors.gender) setFacesheetErrors({ ...facesheetErrors, gender: "" });
                                              }}
                                              MenuProps={{
                                                anchorOrigin: { vertical: "bottom", horizontal: "left" },
                                                transformOrigin: { vertical: "top", horizontal: "left" },
                                                PaperProps: { style: { maxHeight: 200 } }
                                              }}
                                            >
                                              {genderOptions.map((g) => (
                                                <MenuItem key={g.valueName} value={g.valueName}>{g.valueName}</MenuItem>
                                              ))}
                                            </Select>
                                            {facesheetErrors.gender && <FormHelperText>{facesheetErrors.gender}</FormHelperText>}
                                          </FormControl>
                                        ) : (
                                          <>
                                            <Typography variant="body2" color="text.secondary" fontWeight="600">Gender</Typography>
                                            <Typography variant="body2" fontWeight="700" color="text.primary">{fullResidentData?.gender || "-"}</Typography>
                                          </>
                                        )}
                                      </Grid>
                                    </Grid>
                                  </CardContent>
                                </Card>
                              </Grid>

                              {/* 2. Contact Information */}
                              <Grid item xs={12}>
                                <Card
                                  variant="outlined"
                                  sx={{
                                    height: '100%',
                                    borderRadius: 3,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    overflow: 'hidden',
                                    '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.04)' }
                                  }}
                                >
                                  <Box sx={{
                                    p: 1.5,
                                    px: 2,
                                    bgcolor: alpha(theme.palette.primary.main, 0.03),
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                  }}>
                                    <ContactPhoneIcon fontSize="small" sx={{ color: "#48CAE4" }} />
                                    <Typography variant="subtitle2" fontWeight="800" color="text.primary">
                                      Contact Information
                                    </Typography>
                                  </Box>
                                  <CardContent sx={{ p: 3, pt: 2 }}>
                                    <Grid container spacing={3} rowSpacing={3}>
                                      <Grid item xs={12} sm={6}>
                                        {isFacesheetEditing ? (
                                          <TextField
                                            label="Phone Number"
                                            fullWidth
                                            size="small"
                                            value={facesheetForm.phone || ""}
                                            onChange={(e) => {
                                              const val = e.target.value.replace(/\D/g, "");
                                              if (val.length <= 10) {
                                                setFacesheetForm({ ...facesheetForm, phone: val });
                                              }
                                            }}
                                            error={!!facesheetForm.phone && facesheetForm.phone.length > 0 && facesheetForm.phone.length < 10}
                                            helperText={facesheetForm.phone && facesheetForm.phone.length > 0 && facesheetForm.phone.length < 10 ? "Exactly 10 digits required" : ""}
                                            inputProps={{ maxLength: 10 }}
                                          />
                                        ) : (
                                          <>
                                            <Typography variant="body2" color="text.secondary" fontWeight="600">Phone Number</Typography>
                                            <Typography variant="body2" fontWeight="700" color="text.primary">{fullResidentData?.phone || "-"}</Typography>
                                          </>
                                        )}
                                      </Grid>
                                      <Grid item xs={12} sm={6}>
                                        {isFacesheetEditing ? (
                                          <TextField
                                            label="Email"
                                            fullWidth
                                            size="small"
                                            error={!!facesheetErrors.email}
                                            helperText={facesheetErrors.email || ""}
                                            value={facesheetForm.email || ""}
                                            onChange={(e) => {
                                              const value = e.target.value;
                                              setFacesheetForm({ ...facesheetForm, email: value });
                                              // Validate email format
                                              if (value === "") {
                                                setFacesheetErrors({ ...facesheetErrors, email: "" });
                                              } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                                                setFacesheetErrors({ ...facesheetErrors, email: "Invalid email format" });
                                              } else {
                                                setFacesheetErrors({ ...facesheetErrors, email: "" });
                                              }
                                            }}
                                          />
                                        ) : (
                                          <>
                                            <Typography variant="body2" color="text.secondary" fontWeight="600">Email</Typography>
                                            <Typography variant="body2" fontWeight="700" color="text.primary">{fullResidentData?.email || "-"}</Typography>
                                          </>
                                        )}
                                      </Grid>
                                    </Grid>
                                  </CardContent>
                                </Card>
                              </Grid>

                              {/* 3. Address Information */}
                              <Grid item xs={12}>
                                <Card
                                  variant="outlined"
                                  sx={{
                                    height: '100%',
                                    borderRadius: 3,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    overflow: 'hidden',
                                    '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.04)' }
                                  }}
                                >
                                  <Box sx={{
                                    p: 1.5,
                                    px: 2,
                                    bgcolor: alpha(theme.palette.primary.main, 0.03),
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                  }}>
                                    <LocationIcon fontSize="small" sx={{ color: "#48CAE4" }} />
                                    <Typography variant="subtitle2" fontWeight="800" color="text.primary">
                                      Address Information
                                    </Typography>
                                  </Box>
                                  <CardContent sx={{ p: 3, pt: 2 }}>
                                    <Grid container spacing={3} rowSpacing={3}>
                                      <Grid item xs={12}>
                                        {isFacesheetEditing ? (
                                          <TextField
                                            label="Street Address"
                                            fullWidth
                                            size="small"
                                            value={facesheetForm.address || ""}
                                            onChange={(e) => setFacesheetForm({ ...facesheetForm, address: e.target.value })}
                                          />
                                        ) : (
                                          <>
                                            <Typography variant="body2" color="text.secondary" fontWeight="600">Street Address</Typography>
                                            <Typography variant="body2" fontWeight="700" color="text.primary">{fullResidentData?.address || "-"}</Typography>
                                          </>
                                        )}
                                      </Grid>
                                      <Grid item xs={12} sm={4}>
                                        {isFacesheetEditing ? (
                                          <TextField
                                            label="City"
                                            fullWidth
                                            size="small"
                                            value={facesheetForm.city || ""}
                                            onChange={(e) => setFacesheetForm({ ...facesheetForm, city: e.target.value })}
                                          />
                                        ) : (
                                          <>
                                            <Typography variant="body2" color="text.secondary" fontWeight="600">City</Typography>
                                            <Typography variant="body2" fontWeight="700" color="text.primary">{fullResidentData?.city || "-"}</Typography>
                                          </>
                                        )}
                                      </Grid>
                                      <Grid item xs={12} sm={4}>
                                        {isFacesheetEditing ? (
                                          <TextField
                                            label="State"
                                            fullWidth
                                            size="small"
                                            value={facesheetForm.state || ""}
                                            onChange={(e) => setFacesheetForm({ ...facesheetForm, state: e.target.value })}
                                          />
                                        ) : (
                                          <>
                                            <Typography variant="body2" color="text.secondary" fontWeight="600">State</Typography>
                                            <Typography variant="body2" fontWeight="700" color="text.primary">{fullResidentData?.state || "-"}</Typography>
                                          </>
                                        )}
                                      </Grid>
                                      <Grid item xs={12} sm={4}>
                                        {isFacesheetEditing ? (
                                          <TextField
                                            label="Zip Code"
                                            fullWidth
                                            size="small"
                                            value={facesheetForm.postalCode || ""}
                                            onChange={(e) => {
                                              const val = e.target.value.replace(/[^0-9-]/g, "");
                                              setFacesheetForm({ ...facesheetForm, postalCode: val });
                                            }}
                                            inputProps={{ maxLength: 10 }}
                                          />
                                        ) : (
                                          <>
                                            <Typography variant="body2" color="text.secondary" fontWeight="600">Zip Code</Typography>
                                            <Typography variant="body2" fontWeight="700" color="text.primary">{fullResidentData?.postalCode || "-"}</Typography>
                                          </>
                                        )}
                                      </Grid>
                                    </Grid>
                                  </CardContent>
                                </Card>
                              </Grid>

                              {/* 4. Clinical Information */}
                              <Grid item xs={12}>
                                <Card
                                  variant="outlined"
                                  sx={{
                                    height: '100%',
                                    borderRadius: 3,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    overflow: 'hidden',
                                    '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.04)' }
                                  }}
                                >
                                  <Box sx={{
                                    p: 1.5,
                                    px: 2,
                                    bgcolor: alpha(theme.palette.primary.main, 0.03),
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                  }}>
                                    <AssignmentIcon fontSize="small" sx={{ color: "#48CAE4" }} />
                                    <Typography variant="subtitle2" fontWeight="800" color="text.primary">
                                      Clinical Information
                                    </Typography>
                                  </Box>
                                  <CardContent sx={{ p: 3, pt: 2 }}>
                                    <Grid container spacing={3} rowSpacing={3}>
                                      <Grid item xs={12}>
                                        {isFacesheetEditing ? (
                                          <TextField
                                            label="Primary Diagnosis"
                                            fullWidth
                                            size="small"
                                            multiline
                                            rows={2}
                                            value={facesheetForm.primaryDiagnosis || ""}
                                            onChange={(e) => setFacesheetForm({ ...facesheetForm, primaryDiagnosis: e.target.value })}
                                          />
                                        ) : (
                                          <>
                                            <Typography variant="body2" color="text.secondary" fontWeight="600">Primary Diagnosis</Typography>
                                            {fullResidentData?.primaryDiagnosis ? (
                                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                                {fullResidentData.primaryDiagnosis.split(',').map((diagnosis, index) => (
                                                  <Chip
                                                    key={index}
                                                    label={diagnosis.trim()}
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                    sx={{ fontWeight: '500' }}
                                                  />
                                                ))}
                                              </Box>
                                            ) : (
                                              <Typography variant="body2" fontWeight="700" color="text.primary">-</Typography>
                                            )}
                                          </>
                                        )}
                                      </Grid>
                                      <Grid item xs={12} sm={3}>
                                        {isFacesheetEditing ? (
                                          <FormControl fullWidth size="small" error={!!facesheetErrors.admissionType}>
                                            <InputLabel><span>Level of Care <span style={{ color: '#d32f2f' }}>*</span></span></InputLabel>
                                            <Select
                                              label={<span>Level of Care <span style={{ color: '#d32f2f' }}>*</span></span>}
                                              value={facesheetForm.admissionType || ""}
                                              onChange={(e) => {
                                                setFacesheetForm({ ...facesheetForm, admissionType: e.target.value });
                                                if (facesheetErrors.admissionType) setFacesheetErrors({ ...facesheetErrors, admissionType: "" });
                                              }}
                                              MenuProps={{
                                                anchorOrigin: { vertical: "bottom", horizontal: "left" },
                                                transformOrigin: { vertical: "top", horizontal: "left" },
                                                PaperProps: { style: { maxHeight: 200 } }
                                              }}
                                            >
                                              {admissionTypeOptions.map((opt) => (
                                                <MenuItem key={opt.valueName} value={opt.valueName}>{opt.valueName}</MenuItem>
                                              ))}
                                            </Select>
                                            {facesheetErrors.admissionType && <FormHelperText>{facesheetErrors.admissionType}</FormHelperText>}
                                          </FormControl>
                                        ) : (
                                          <>
                                            <Typography variant="body2" color="text.secondary" fontWeight="600">Level of Care</Typography>
                                            <Typography variant="body2" fontWeight="700" color="text.primary">
                                              {(() => {
                                                const type = fullResidentData?.admissionType || fullResidentData?.careType || "-";
                                                const mapping: Record<string, string> = {
                                                  ALF: "Assisted Living",
                                                  ASSISTED_LIVING: "Assisted Living",
                                                  HOSPICE: "Hospice Care"
                                                };
                                                return mapping[type as string] || type;
                                              })()}
                                            </Typography>
                                          </>
                                        )}
                                      </Grid>
                                      <Grid item xs={12} sm={3}>
                                        {isFacesheetEditing ? (
                                          <FormControl fullWidth size="small">
                                            <InputLabel>Blood Group</InputLabel>
                                            <Select
                                              label="Blood Group"
                                              value={facesheetForm.bloodGroup || ""}
                                              onChange={(e) => setFacesheetForm({ ...facesheetForm, bloodGroup: e.target.value })}
                                              MenuProps={{
                                                anchorOrigin: { vertical: "bottom", horizontal: "left" },
                                                transformOrigin: { vertical: "top", horizontal: "left" },
                                                PaperProps: { style: { maxHeight: 200 } }
                                              }}
                                            >
                                              {bloodGroupOptions.map((opt) => (
                                                <MenuItem key={opt.valueName} value={opt.valueName}>{opt.valueName}</MenuItem>
                                              ))}
                                            </Select>
                                          </FormControl>
                                        ) : (
                                          <>
                                            <Typography variant="body2" color="text.secondary" fontWeight="600">Blood Group</Typography>
                                            <Typography variant="body2" fontWeight="700" color="text.primary">{fullResidentData?.bloodGroup || "-"}</Typography>
                                          </>
                                        )}
                                      </Grid>
                                      <Grid item xs={12} sm={3}>
                                        {isFacesheetEditing ? (
                                          <FormControl fullWidth size="small" error={!!facesheetErrors.dnrStatus}>
                                            <InputLabel><span>DNR Status <span style={{ color: '#d32f2f' }}>*</span></span></InputLabel>
                                            <Select
                                              label={<span>DNR Status <span style={{ color: '#d32f2f' }}>*</span></span>}
                                              value={facesheetForm.dnrStatus || ""}
                                              onChange={(e) => {
                                                setFacesheetForm({ ...facesheetForm, dnrStatus: e.target.value });
                                                if (facesheetErrors.dnrStatus) setFacesheetErrors({ ...facesheetErrors, dnrStatus: "" });
                                              }}
                                              MenuProps={{
                                                anchorOrigin: { vertical: "bottom", horizontal: "left" },
                                                transformOrigin: { vertical: "top", horizontal: "left" },
                                                PaperProps: {
                                                  style: { maxHeight: 200, overflowY: 'auto' },
                                                },
                                              }}
                                            >
                                              {dnrStatusOptions.map((opt) => (
                                                <MenuItem key={opt.valueName} value={opt.valueName}>{opt.valueName}</MenuItem>
                                              ))}
                                            </Select>
                                            {facesheetErrors.dnrStatus && <FormHelperText>{facesheetErrors.dnrStatus}</FormHelperText>}
                                          </FormControl>
                                        ) : (
                                          <>
                                            <Typography variant="body2" color="text.secondary" fontWeight="600">DNR Status</Typography>
                                            <Box sx={{ mt: 0.5 }}>
                                              <Chip
                                                label={fullResidentData?.dnrStatus || "NOT SPECIFIED"}
                                                size="small"
                                                color={
                                                  String(fullResidentData?.dnrStatus || "").toUpperCase().includes("YES") ||
                                                    String(fullResidentData?.dnrStatus || "").toUpperCase().includes("DNR") ||
                                                    String(fullResidentData?.dnrStatus || "").toUpperCase().includes("DNI")
                                                    ? "error" : "success"
                                                }
                                                sx={{ fontWeight: 'bold' }}
                                              />
                                            </Box>
                                          </>
                                        )}
                                      </Grid>
                                      <Grid item xs={12} sm={3}>
                                        {isFacesheetEditing ? (
                                          <TextField
                                            label={<span>Height (cm) <span style={{ color: '#d32f2f' }}>*</span></span>}
                                            fullWidth
                                            size="small"
                                            type="number"
                                            error={!!facesheetErrors.height}
                                            helperText={facesheetErrors.height ? 'This field is required' : ''}
                                            value={facesheetForm.height || ""}
                                            onChange={(e) => {
                                              setFacesheetForm({ ...facesheetForm, height: e.target.value ? Number(e.target.value) : undefined });
                                              if (facesheetErrors.height) setFacesheetErrors({ ...facesheetErrors, height: "" });
                                            }}
                                          />
                                        ) : (
                                          <>
                                            <Typography variant="body2" color="text.secondary" fontWeight="600">Height</Typography>
                                            <Typography variant="body2" fontWeight="700" color="text.primary">
                                              {fullResidentData?.height ? `${fullResidentData.height} cm` : "-"}
                                            </Typography>
                                          </>
                                        )}
                                      </Grid>
                                      <Grid item xs={12}>
                                        {isFacesheetEditing ? (
                                          <TextField
                                            label="Allergies"
                                            fullWidth
                                            size="small"
                                            multiline
                                            rows={2}
                                            value={facesheetForm.allergies || ""}
                                            onChange={(e) => setFacesheetForm({ ...facesheetForm, allergies: e.target.value })}
                                          />
                                        ) : (
                                          <>
                                            <Typography variant="body2" color="text.secondary" fontWeight="600">Known Allergies</Typography>
                                            <Box sx={{ mt: 0.5 }}>
                                              {fullResidentData?.allergies ? (
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                  {fullResidentData.allergies.split(',').map((allergy, index) => (
                                                    <Chip
                                                      key={index}
                                                      label={allergy.trim()}
                                                      size="small"
                                                      color="error"
                                                      variant="outlined"
                                                      sx={{ fontWeight: 'bold' }}
                                                    />
                                                  ))}
                                                </Box>
                                              ) : (
                                                <Chip
                                                  label="NONE RECORDED"
                                                  size="small"
                                                  variant="outlined"
                                                  sx={{ fontWeight: 'bold', color: 'text.secondary', borderColor: 'divider' }}
                                                />
                                              )}
                                            </Box>
                                          </>
                                        )}
                                      </Grid>
                                    </Grid>
                                  </CardContent>
                                </Card>
                              </Grid>

                              {/* 5. Hospital Information */}
                              <Grid item xs={12}>
                                <Card
                                  variant="outlined"
                                  sx={{
                                    height: '100%',
                                    borderRadius: 3,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    overflow: 'hidden',
                                    '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.04)' }
                                  }}
                                >
                                  <Box sx={{
                                    p: 1.5,
                                    px: 2,
                                    bgcolor: alpha(theme.palette.primary.main, 0.03),
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                  }}>
                                    <LocalHospitalIcon fontSize="small" sx={{ color: "#48CAE4" }} />
                                    <Typography variant="subtitle2" fontWeight="800" color="text.primary">
                                      Hospital Information
                                    </Typography>
                                  </Box>
                                  <CardContent sx={{ p: 3, pt: 2 }}>
                                    <Grid container spacing={3} rowSpacing={3}>
                                      <Grid item xs={12} sm={4}>
                                        {isFacesheetEditing ? (
                                          <TextField
                                            label="Hospital Name"
                                            fullWidth
                                            size="small"
                                            value={facesheetForm.hospitalName || ""}
                                            onChange={(e) => setFacesheetForm({ ...facesheetForm, hospitalName: e.target.value })}
                                          />
                                        ) : (
                                          <>
                                            <Typography variant="body2" color="text.secondary" fontWeight="600">Hospital Name</Typography>
                                            <Typography variant="body2" fontWeight="700" color="text.primary">{fullResidentData?.hospitalName || "-"}</Typography>
                                          </>
                                        )}
                                      </Grid>
                                      <Grid item xs={12} sm={4}>
                                        {isFacesheetEditing ? (
                                          <TextField
                                            label="Doctor Name"
                                            fullWidth
                                            size="small"
                                            value={facesheetForm.attendingPhysicianName || facesheetForm.doctorName || ""}
                                            onChange={(e) => {
                                              const value = e.target.value;
                                              // Only allow alphabetic characters, spaces, hyphens, and apostrophes
                                              if (value === "" || /^[a-zA-Z\s'-]*$/.test(value)) {
                                                setFacesheetForm({ ...facesheetForm, attendingPhysicianName: value, doctorName: value });
                                              }
                                            }}
                                          />
                                        ) : (
                                          <>
                                            <Typography variant="body2" color="text.secondary" fontWeight="600">Doctor Name</Typography>
                                            <Typography variant="body2" fontWeight="700" color="text.primary">{fullResidentData?.attendingPhysicianName || fullResidentData?.doctorName || "-"}</Typography>
                                          </>
                                        )}
                                      </Grid>
                                      <Grid item xs={12} sm={4}>
                                        {isFacesheetEditing ? (
                                          <TextField
                                            label="Doctor Contact"
                                            fullWidth
                                            size="small"
                                            required
                                            value={facesheetForm.attendingPhysicianPhone || ""}
                                            onChange={(e) => {
                                              const value = e.target.value;
                                              if (value === "" || /^[0-9]+$/.test(value)) {
                                                if (value.length <= 10) {
                                                  setFacesheetForm({ ...facesheetForm, attendingPhysicianPhone: value });
                                                  if (facesheetErrors.attendingPhysicianPhone) {
                                                    setFacesheetErrors({ ...facesheetErrors, attendingPhysicianPhone: "" });
                                                  }
                                                }
                                              }
                                            }}
                                            error={!!facesheetErrors.attendingPhysicianPhone}
                                            helperText={facesheetErrors.attendingPhysicianPhone}
                                          />
                                        ) : (
                                          <>
                                            <Typography variant="body2" color="text.secondary" fontWeight="600">Doctor Contact</Typography>
                                            <Typography variant="body2" fontWeight="700" color="text.primary">{fullResidentData?.attendingPhysicianPhone || "-"}</Typography>
                                          </>
                                        )}
                                      </Grid>
                                      <Grid item xs={12} sm={4}>
                                        {isFacesheetEditing ? (
                                          <TextField
                                            label="Doctor Email"
                                            fullWidth
                                            size="small"
                                            required
                                            value={facesheetForm.attendingPhysicianEmail || ""}
                                            onChange={(e) => {
                                              setFacesheetForm({ ...facesheetForm, attendingPhysicianEmail: e.target.value });
                                              if (facesheetErrors.attendingPhysicianEmail) {
                                                setFacesheetErrors({ ...facesheetErrors, attendingPhysicianEmail: "" });
                                              }
                                            }}
                                            error={!!facesheetErrors.attendingPhysicianEmail}
                                            helperText={facesheetErrors.attendingPhysicianEmail}
                                          />
                                        ) : (
                                          <>
                                            <Typography variant="body2" color="text.secondary" fontWeight="600">Doctor Email</Typography>
                                            <Typography variant="body2" fontWeight="700" color="text.primary" sx={{ wordBreak: 'break-word' }}>{fullResidentData?.attendingPhysicianEmail || "-"}</Typography>
                                          </>
                                        )}
                                      </Grid>
                                      <Grid item xs={12} sm={4}>
                                        {isFacesheetEditing ? (
                                          <TextField
                                            label="Address"
                                            fullWidth
                                            size="small"
                                            value={facesheetForm.hospitalAddress || ""}
                                            onChange={(e) => setFacesheetForm({ ...facesheetForm, hospitalAddress: e.target.value })}
                                          />
                                        ) : (
                                          <>
                                            <Typography variant="body2" color="text.secondary" fontWeight="600">Address</Typography>
                                            <Typography variant="body2" fontWeight="700" color="text.primary">{fullResidentData?.hospitalAddress || "-"}</Typography>
                                          </>
                                        )}
                                      </Grid>
                                    </Grid>
                                  </CardContent>
                                </Card>
                              </Grid>

                              {/* 6. Emergency Contact */}
                              <Grid item xs={12}>
                                <Card
                                  variant="outlined"
                                  sx={{
                                    height: '100%',
                                    borderRadius: 3,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    overflow: 'hidden',
                                    '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.04)' }
                                  }}
                                >
                                  <Box sx={{
                                    p: 1.5,
                                    px: 2,
                                    bgcolor: alpha(theme.palette.primary.main, 0.03),
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                  }}>
                                    <ContactPhoneIcon fontSize="small" sx={{ color: "#48CAE4" }} />
                                    <Typography variant="subtitle2" fontWeight="800" color="text.primary">
                                      Emergency Contact
                                    </Typography>
                                  </Box>
                                  <CardContent sx={{ p: 3, pt: 2 }}>
                                    <Grid container spacing={3} rowSpacing={3}>
                                      <Grid item xs={12} sm={6}>
                                        {isFacesheetEditing ? (
                                          <TextField
                                            label="Guardian Name"
                                            fullWidth
                                            size="small"
                                            value={facesheetForm.emergencyContactName || ""}
                                            onChange={(e) => {
                                              const value = e.target.value;
                                              // Only allow alphabetic characters, spaces, hyphens, and apostrophes
                                              if (value === "" || /^[a-zA-Z\s'-]*$/.test(value)) {
                                                setFacesheetForm({ ...facesheetForm, emergencyContactName: value });
                                              }
                                            }}
                                          />
                                        ) : (
                                          <>
                                            <Typography variant="body2" color="text.secondary" fontWeight="600">Guardian Name</Typography>
                                            <Typography variant="body2" fontWeight="700" color="text.primary">{fullResidentData?.emergencyContactName || "-"}</Typography>
                                          </>
                                        )}
                                      </Grid>
                                      <Grid item xs={12} sm={6}>
                                        {isFacesheetEditing ? (
                                          <TextField
                                            label="Guardian Mobile Number"
                                            fullWidth
                                            size="small"
                                            required
                                            InputLabelProps={{ sx: { '& .MuiFormLabel-asterisk': { color: '#ef4444' } } }}
                                            value={facesheetForm.emergencyContactPhone || ""}
                                            onChange={(e) => {
                                              const val = e.target.value.replace(/\D/g, "");
                                              if (val.length <= 10) {
                                                setFacesheetForm({ ...facesheetForm, emergencyContactPhone: val });
                                              }
                                            }}
                                            error={!!facesheetErrors.emergencyContactPhone || (!!facesheetForm.emergencyContactPhone && facesheetForm.emergencyContactPhone.length > 0 && facesheetForm.emergencyContactPhone.length < 10)}
                                            helperText={facesheetErrors.emergencyContactPhone || (facesheetForm.emergencyContactPhone && facesheetForm.emergencyContactPhone.length > 0 && facesheetForm.emergencyContactPhone.length < 10 ? "Exactly 10 digits required" : "")}
                                            inputProps={{ maxLength: 10 }}
                                          />
                                        ) : (
                                          <>
                                            <Typography variant="body2" color="text.secondary" fontWeight="600">Guardian Mobile Number</Typography>
                                            <Typography variant="body2" fontWeight="700" color="text.primary">{fullResidentData?.emergencyContactPhone || "-"}</Typography>
                                          </>
                                        )}
                                      </Grid>
                                    </Grid>
                                  </CardContent>
                                </Card>
                              </Grid>
                            </Grid>
                          </Grid>

                          {/* Right Column: Insurance Information */}
                          <Grid item xs={12} md={6}>
                            {/* Insurance Information Header */}
                            <Box sx={{
                              p: 1,
                              px: 2,
                              mb: 2,
                              bgcolor: alpha(theme.palette.primary.main, 0.03),
                              borderRadius: 2,
                              border: '1px solid',
                              borderColor: 'divider',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}>
                              <Typography variant="subtitle2" fontWeight="800" color="text.primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CreditCardIcon fontSize="small" sx={{ color: "#48CAE4" }} /> Insurance Information
                              </Typography>
                              {!isOnlyCaregiver && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<AddIcon />}
                                  onClick={() => handleOpenInsuranceDialog()}
                                  sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2 }}
                                >
                                  Add Insurance
                                </Button>
                              )}
                            </Box>

                            {insuranceCards && insuranceCards.length > 0 ? (
                              <Grid container spacing={2}>
                                {insuranceCards.map((card, idx) => {
                                  const cardId = card.id || String(idx);
                                  const isExpanded = expandedInsurances.has(cardId) || isFacesheetEditing;

                                  return (
                                    <Grid item xs={12} key={cardId}>
                                      <Card
                                        variant="outlined"
                                        sx={{
                                          height: '100%',
                                          borderRadius: 3,
                                          border: '1px solid',
                                          borderColor: card.insuranceType === 'Primary' ? 'primary.light' : 'divider',
                                          bgcolor: card.insuranceType === 'Primary' ? alpha(theme.palette.primary.main, 0.02) : 'background.paper',
                                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                          overflow: 'hidden',
                                          '&:hover': {
                                            boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
                                            borderColor: 'primary.main',
                                            transform: 'translateY(-2px)'
                                          }
                                        }}
                                      >
                                        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                                          {/* Card Header */}
                                          <Box
                                            sx={{
                                              p: 2,
                                              display: 'flex',
                                              justifyContent: 'space-between',
                                              alignItems: 'center',
                                              cursor: 'pointer',
                                              bgcolor: isExpanded ? alpha(theme.palette.primary.main, 0.03) : 'transparent',
                                              borderBottom: isExpanded ? '1px solid' : 'none',
                                              borderColor: 'divider',
                                              '&:hover': {
                                                bgcolor: alpha(theme.palette.primary.main, 0.05)
                                              }
                                            }}
                                            onClick={() => toggleInsuranceExpansion(cardId)}
                                          >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                              <Avatar
                                                sx={{
                                                  bgcolor: card.insuranceType === 'Primary' ? '#E8F8FB' : 'grey.200',
                                                  color: card.insuranceType === 'Primary' ? '#48CAE4' : '#48CAE4',
                                                  width: 36,
                                                  height: 36,
                                                  fontSize: '1rem'
                                                }}
                                              >
                                                <CreditCardIcon fontSize="small" />
                                              </Avatar>
                                              <Box>
                                                <Typography variant="subtitle2" fontWeight="800" color="text.primary">
                                                  {card.insuranceCompanyName || card.provider || "Unnamed Insurance"}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" fontWeight="600">
                                                  {card.planName || 'Standard Plan'} {card.insuranceType && `· ${card.insuranceType}`}
                                                </Typography>
                                              </Box>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                              <Tooltip title={isExpanded ? "Close" : isOnlyCaregiver ? "Open" : "Show Details / Edit"}>
                                                <IconButton
                                                  size="small"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    toggleInsuranceExpansion(cardId);
                                                  }}
                                                >
                                                  {isExpanded ? <ExpandLessIcon fontSize="small" /> : isOnlyCaregiver ? <ExpandMoreIcon fontSize="small" sx={{ color: '#48CAE4' }} /> : <EditIcon fontSize="small" sx={{ color: '#48CAE4' }} />}
                                                </IconButton>
                                              </Tooltip>
                                            </Box>
                                          </Box>

                                          {/* Compact View (Always Visible or Collapsed) */}
                                          {!isExpanded && (
                                            <Box sx={{ p: 2, bgcolor: '#fdfbf7' }}> {/* Light beige background similar to image */}
                                              <Grid container spacing={2}>
                                                <Grid item xs={6}>
                                                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>Subscriber Name</Typography>
                                                  <Typography variant="caption" fontWeight="700">{card.subscriberName || '-'}</Typography>
                                                </Grid>
                                                <Grid item xs={6}>
                                                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>Identification Number</Typography>
                                                  <Typography variant="caption" fontWeight="700">{card.policyMemberId || card.policyNumber || '-'}</Typography>
                                                </Grid>
                                                <Grid item xs={6}>
                                                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>Group Number</Typography>
                                                  <Typography variant="caption" fontWeight="700">{card.groupNumber || '-'}</Typography>
                                                </Grid>
                                                <Grid item xs={6}>
                                                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>Effective Date</Typography>
                                                  <Typography variant="caption" fontWeight="700">
                                                    {card.effectiveDate ? formatToUIDate(card.effectiveDate) : '-'}
                                                  </Typography>
                                                </Grid>
                                              </Grid>
                                            </Box>
                                          )}

                                          {/* Expanded View */}
                                          <Collapse in={isExpanded}>
                                            <Box sx={{ p: 2.5 }}>
                                              <Grid container spacing={3}>
                                                {/* Section: Provider Details */}
                                                <Grid item xs={12}>
                                                  <Typography variant="caption" color="primary" fontWeight="800" sx={{ display: 'block', mb: 1.5, textTransform: 'uppercase', letterSpacing: 1 }}>
                                                    Provider & Contact
                                                  </Typography>
                                                  <Grid container spacing={2}>
                                                    <Grid item xs={6} sm={3}>
                                                      <Typography variant="body2" color="text.secondary" display="block">Payer ID</Typography>
                                                      <Typography variant="caption" fontWeight="600">{card.payerId || 'N/A'}</Typography>
                                                    </Grid>
                                                    <Grid item xs={6} sm={3}>
                                                      <Typography variant="body2" color="text.secondary" display="block">Phone</Typography>
                                                      <Typography variant="caption" fontWeight="600">{card.insurancePhone || 'N/A'}</Typography>
                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                      <Typography variant="body2" color="text.secondary" display="block">Email</Typography>
                                                      <Typography variant="caption" fontWeight="600" sx={{ wordBreak: 'break-all' }}>{card.insuranceEmail || 'N/A'}</Typography>
                                                    </Grid>
                                                  </Grid>
                                                </Grid>

                                                <Grid item xs={12}><Divider sx={{ borderStyle: 'dashed' }} /></Grid>

                                                {/* Section: Subscriber Details */}
                                                <Grid item xs={12}>
                                                  <Typography variant="caption" color="primary" fontWeight="800" sx={{ display: 'block', mb: 1.5, textTransform: 'uppercase', letterSpacing: 1 }}>
                                                    Subscriber Details
                                                  </Typography>
                                                  <Grid container spacing={2}>
                                                    <Grid item xs={6} sm={3}>
                                                      <Typography variant="body2" color="text.secondary" display="block">Name & Relation</Typography>
                                                      <Typography variant="caption" fontWeight="600">{card.subscriberName || 'N/A'} ({card.subscriberRelationship || 'Self'})</Typography>
                                                    </Grid>
                                                    <Grid item xs={6} sm={3}>
                                                      <Typography variant="body2" color="text.secondary" display="block">DOB & Gender</Typography>
                                                      <Typography variant="caption" fontWeight="600">{card.subscriberDob ? formatToUIDate(card.subscriberDob) : 'N/A'} | {card.subscriberGender || 'N/A'}</Typography>
                                                    </Grid>
                                                    <Grid item xs={6} sm={3}>
                                                      <Typography variant="body2" color="text.secondary" display="block">Contact Phone</Typography>
                                                      <Typography variant="caption" fontWeight="600">{card.subscriberPhone || 'N/A'}</Typography>
                                                    </Grid>
                                                    <Grid item xs={6} sm={3}>
                                                      <Typography variant="body2" color="text.secondary" display="block">Address</Typography>
                                                      <Typography variant="caption" fontWeight="500">{card.subscriberAddress || 'N/A'}</Typography>
                                                    </Grid>
                                                  </Grid>
                                                </Grid>

                                                <Grid item xs={12}><Divider sx={{ borderStyle: 'dashed' }} /></Grid>

                                                {/* Section: Policy Details */}
                                                <Grid item xs={12}>
                                                  <Typography variant="caption" color="primary" fontWeight="800" sx={{ display: 'block', mb: 1.5, textTransform: 'uppercase', letterSpacing: 1 }}>
                                                    Policy Details
                                                  </Typography>
                                                  <Grid container spacing={2}>
                                                    <Grid item xs={6} sm={3}>
                                                      <Typography variant="body2" color="text.secondary" display="block">Member ID</Typography>
                                                      <Typography variant="caption" fontWeight="700" color="primary.main">{card.policyMemberId || card.policyNumber || 'N/A'}</Typography>
                                                    </Grid>
                                                    <Grid item xs={6} sm={3}>
                                                      <Typography variant="body2" color="text.secondary" display="block">Group Number</Typography>
                                                      <Typography variant="caption" fontWeight="600">{card.groupNumber || 'N/A'}</Typography>
                                                    </Grid>
                                                    <Grid item xs={6} sm={3}>
                                                      <Typography variant="body2" color="text.secondary" display="block">Coverage</Typography>
                                                      <Chip label={card.coverageType || 'Medical'} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.6rem', fontWeight: 600 }} />
                                                    </Grid>
                                                    <Grid item xs={6} sm={3}>
                                                      <Typography variant="body2" color="text.secondary" display="block">Auth Number</Typography>
                                                      <Typography variant="caption" fontWeight="600">{card.authorizationNumber || 'N/A'}</Typography>
                                                    </Grid>
                                                    <Grid item xs={6} sm={3}>
                                                      <Typography variant="body2" color="text.secondary" display="block">Plan Type</Typography>
                                                      <Typography variant="caption" fontWeight="600">{card.planType || 'N/A'}</Typography>
                                                    </Grid>
                                                    <Grid item xs={6} sm={9}>
                                                      <Typography variant="body2" color="text.secondary" display="block">Term Period</Typography>
                                                      <Typography variant="caption" fontWeight="600">
                                                        {card.effectiveDate ? formatToUIDate(card.effectiveDate) : 'N/A'} - {card.terminationDate ? formatToUIDate(card.terminationDate) : 'Active'}
                                                      </Typography>
                                                    </Grid>
                                                  </Grid>
                                                </Grid>

                                                <Grid item xs={12}><Divider sx={{ borderStyle: 'dashed' }} /></Grid>

                                                {/* Section: Limits */}
                                                <Grid item xs={12}>
                                                  <Typography variant="caption" color="primary" fontWeight="800" sx={{ display: 'block', mb: 1.5, textTransform: 'uppercase', letterSpacing: 1 }}>
                                                    Coverage Limits
                                                  </Typography>
                                                  <Grid container spacing={2}>
                                                    <Grid item xs={4} sm={2.4}>
                                                      <Typography variant="body2" color="text.secondary" display="block">Copay</Typography>
                                                      <Typography variant="caption" fontWeight="700" color="success.main">${card.copayAmount || '0'}</Typography>
                                                    </Grid>
                                                    <Grid item xs={4} sm={2.4}>
                                                      <Typography variant="body2" color="text.secondary" display="block">Coinsurance</Typography>
                                                      <Typography variant="caption" fontWeight="700">{card.coinsurancePercentage || '0'}%</Typography>
                                                    </Grid>
                                                    <Grid item xs={4} sm={2.4}>
                                                      <Typography variant="body2" color="text.secondary" display="block">Deductible</Typography>
                                                      <Typography variant="caption" fontWeight="700">${card.annualDeductible || '0'}</Typography>
                                                    </Grid>
                                                    <Grid item xs={6} sm={2.4}>
                                                      <Typography variant="body2" color="text.secondary" display="block">Remaining</Typography>
                                                      <Typography variant="caption" fontWeight="700">${card.remainingDeductible || '0'}</Typography>
                                                    </Grid>
                                                    <Grid item xs={6} sm={2.4}>
                                                      <Typography variant="body2" color="text.secondary" display="block">OOP Limit</Typography>
                                                      <Typography variant="caption" fontWeight="700">${card.outOfPocketLimit || '0'}</Typography>
                                                    </Grid>
                                                  </Grid>
                                                </Grid>
                                              </Grid>

                                              {/* Actions Footer inside expansion */}
                                              {!isCaregiver && (
                                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                                                  <Button
                                                    size="small"
                                                    startIcon={<EditIcon />}
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      handleOpenInsuranceDialog(card);
                                                    }}
                                                    variant="contained"
                                                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                                                  >
                                                    Edit Details
                                                  </Button>
                                                  <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      handleDeleteInsurance(card);
                                                    }}
                                                    sx={{ bgcolor: alpha(theme.palette.error.main, 0.1), '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.2) } }}
                                                  >
                                                    <DeleteIcon fontSize="small" />
                                                  </IconButton>
                                                </Box>
                                              )}
                                            </Box>
                                          </Collapse>
                                        </CardContent>
                                      </Card>
                                    </Grid>
                                  );
                                })}
                              </Grid>
                            ) : (
                              <Box sx={{
                                py: 3,
                                textAlign: 'center',
                                bgcolor: 'grey.50',
                                borderRadius: 2,
                                border: '1px dashed',
                                borderColor: 'divider'
                              }}>
                                <Typography variant="body2" color="text.secondary">
                                  No insurance recorded.
                                </Typography>
                              </Box>
                            )
                            }
                          </Grid>
                        </Grid>
                      )}
                    </Paper>
                  )}

                  {/* Prescribe Medication Tab */}
                  {topTab === 1 && (
                    <Paper sx={{ p: 3 }}>
                      {!isOnlyCaregiver && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, mt: 4 }}>
                          <Typography variant="h6" color="text.primary" sx={{ fontWeight: 800 }}>
                            Add Prescribed Medication
                          </Typography>
                        </Box>
                      )}

                      {!selectedResident && !prefilledResident ? (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 2 }}
                        >
                          please select a resident to view Prescribe medications.
                        </Typography>
                      ) : (
                        <>
                          {isLoadingMedications || !fullResidentData ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                              <CircularProgress size={24} />
                              <Typography sx={{ ml: 2 }}>Loading medications...</Typography>
                            </Box>
                          ) : (
                            <>
                              {medications.length === 0 && (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ mb: 2 }}
                                >
                                  No Prescribe Medication Found for this Resident.
                                </Typography>
                              )}
                              {/* Content continues below... */}


                              {isResidentActive && !isOnlyCaregiver && (
                                <Paper
                                  elevation={0}
                                  sx={{
                                    p: { xs: 2.5, md: 3 },
                                    mb: 3,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 2,
                                    bgcolor: 'background.paper'
                                  }}
                                >
                                  <Typography variant="subtitle1" gutterBottom>
                                    {editingIndex !== null && "Edit Prescribed Medication"}
                                  </Typography>
                                  {medicationError && (
                                    <Alert severity="error" sx={{ mb: 2 }}>
                                      {medicationError}
                                    </Alert>
                                  )}
                                  <Grid container spacing={3} alignItems="center">
                                    <Grid item xs={12} md={4}>
                                      <TextField
                                        label="Medication Name"
                                        fullWidth
                                        required
                                        value={medForm.med}
                                        onChange={(e) =>
                                          setMedForm((prev) => ({
                                            ...prev,
                                            med: e.target.value,
                                          }))
                                        }
                                        error={!medForm.med && !!medicationError}
                                        helperText={
                                          !medForm.med && !!medicationError
                                            ? "Medication is required"
                                            : ""
                                        }
                                        sx={{ "& .MuiFormLabel-asterisk": { color: "#d32f2f" } }}
                                      />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                      <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                                        <TextField
                                          label="Dose"
                                          fullWidth
                                          required
                                          value={
                                            medForm.dose
                                              ? `${medForm.dose}${getDoseUnitInputSuffix(medForm.doseUnit)}`
                                              : ""
                                          }
                                          onChange={(e) => {
                                            const rawValue = e.target.value;
                                            const unitSuffix = getDoseUnitInputSuffix(
                                              medForm.doseUnit,
                                            );

                                            let numericValue = rawValue;
                                            if (
                                              unitSuffix &&
                                              rawValue
                                                .toLowerCase()
                                                .endsWith(unitSuffix.toLowerCase())
                                            ) {
                                              numericValue = rawValue.slice(
                                                0,
                                                rawValue.length - unitSuffix.length,
                                              );
                                            }

                                            numericValue = numericValue.replace(/\s+/g, "");

                                            // Allow only dose characters in editable part.
                                            if (/^[0-9./-]*$/.test(numericValue)) {
                                              setMedForm((prev) => ({
                                                ...prev,
                                                dose: numericValue,
                                              }));
                                            }
                                          }}
                                          error={!medForm.dose && !!medicationError}
                                          helperText={
                                            !medForm.dose && !!medicationError
                                              ? "Dose is required"
                                              : ""
                                          }
                                          sx={{ "& .MuiFormLabel-asterisk": { color: "#d32f2f" } }}
                                        />
                                        <TextField
                                          select
                                          label="Unit"
                                          required
                                          sx={{ minWidth: 110, "& .MuiFormLabel-asterisk": { color: "#d32f2f" } }}
                                          value={medForm.doseUnit || ""}
                                          onChange={(e) =>
                                            setMedForm((prev) => ({
                                              ...prev,
                                              doseUnit: e.target.value,
                                            }))
                                          }
                                          error={!medForm.doseUnit && !!medicationError}
                                          helperText={!medForm.doseUnit && !!medicationError ? "Unit is required" : ""}
                                          SelectProps={{
                                            native: false,
                                            MenuProps: {
                                              PaperProps: {
                                                style: {
                                                  maxHeight: 250,
                                                },
                                              },
                                            },
                                          }}
                                        >
                                          <MenuItem value="">
                                            <em>Select</em>
                                          </MenuItem>
                                          {unitOptions.map((opt) => (
                                            <MenuItem key={opt.id} value={opt.valueName}>
                                              {opt.valueName}
                                            </MenuItem>
                                          ))}
                                        </TextField>
                                      </Box>
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                      <FormControl
                                        fullWidth
                                        required
                                        error={!medForm.route && !!medicationError}
                                      >
                                        <InputLabel sx={{ "& .MuiFormLabel-asterisk": { color: "#d32f2f" } }}>Route</InputLabel>
                                        <Select
                                          label="Route"
                                          value={medForm.route}
                                          onChange={(e) =>
                                            setMedForm((prev) => ({
                                              ...prev,
                                              route: e.target.value as string,
                                            }))
                                          }
                                          MenuProps={{
                                            PaperProps: {
                                              sx: { maxHeight: 300 },
                                            },
                                          }}
                                        >
                                          <MenuItem value="">
                                            <em>Select Route</em>
                                          </MenuItem>
                                          {routeOptions.map((opt) => (
                                            <MenuItem key={opt.id} value={opt.valueName}>
                                              {opt.valueName}
                                            </MenuItem>
                                          ))}
                                        </Select>
                                        {!medForm.route && !!medicationError && (
                                          <Typography color="error" variant="caption">
                                            Route is required
                                          </Typography>
                                        )}
                                      </FormControl>
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                                        <DatePicker
                                          label="Medication Start Date"
                                          format="dd/MM/yyyy"
                                          value={medForm.start}
                                          onChange={(v) => {
                                            setMedForm((prev) => ({ ...prev, start: v }));
                                            if (medicationError?.includes("date")) {
                                              setMedicationError(null);
                                            }
                                          }}
                                          slotProps={{
                                            textField: {
                                              fullWidth: true,
                                              error: !medForm.start && !!medicationError,
                                            },
                                          }}
                                        />
                                      </LocalizationProvider>
                                      {!medForm.start && !!medicationError && (
                                        <Typography color="error" variant="caption">
                                          Medication Start Date is required
                                        </Typography>
                                      )}
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                                        <DatePicker
                                          label="Refill Date"
                                          format="dd/MM/yyyy"
                                          value={medForm.end}
                                          onChange={(v) => {
                                            setMedForm((prev) => ({ ...prev, end: v }));
                                            if (medicationError?.includes("Refill Date")) {
                                              setMedicationError(null);
                                            }
                                          }}
                                          minDate={medForm.start || undefined}
                                          slotProps={{
                                            textField: {
                                              fullWidth: true,
                                              error:
                                                !!medicationError &&
                                                medicationError.includes("Refill Date"),
                                              helperText:
                                                !!medicationError &&
                                                  medicationError.includes("Refill Date")
                                                  ? medicationError
                                                  : "",
                                            },
                                          }}
                                        />
                                      </LocalizationProvider>
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                      <FormControl
                                        fullWidth
                                        required
                                        error={!medForm.schedule && !!medicationError}
                                      >
                                        <InputLabel sx={{ "& .MuiFormLabel-asterisk": { color: "#d32f2f" } }}>Schedule</InputLabel>
                                        <Select
                                          value={medForm.schedule}
                                          label="Schedule"
                                          onChange={(e) =>
                                            setMedForm((prev) => ({
                                              ...prev,
                                              schedule: e.target.value as string,
                                              timeSlot: "", // Reset time slot when schedule changes
                                              start: new Date(), // Reset start date to today when schedule changes
                                            }))
                                          }
                                          MenuProps={{
                                            PaperProps: {
                                              style: {
                                                maxHeight: 250,
                                              },
                                            },
                                          }}
                                        >
                                          <MenuItem value="">
                                            <em>Select Schedule</em>
                                          </MenuItem>
                                          {scheduleOptions.map((opt) => (
                                            <MenuItem key={opt.id} value={opt.valueName}>
                                              {opt.valueName}
                                            </MenuItem>
                                          ))}
                                        </Select>
                                      </FormControl>
                                      {!medForm.schedule && !!medicationError && (
                                        <Typography color="error" variant="caption">
                                          Schedule is required
                                        </Typography>
                                      )}
                                    </Grid>
                                    {/* Time Slot Dropdown - Only for Once Daily or STAT Dose */}
                                    {(medForm.schedule === "Once daily" || medForm.schedule === "STAT Dose") && (
                                      <Grid item xs={12} md={4}>
                                        <FormControl
                                          fullWidth
                                          error={!medForm.timeSlot && !!medicationError}
                                        >
                                          <InputLabel>Time of Day</InputLabel>
                                          <Select
                                            value={medForm.timeSlot}
                                            label="Time of Day"
                                            onChange={(e) =>
                                              setMedForm((prev) => ({
                                                ...prev,
                                                timeSlot: e.target.value as string,
                                              }))
                                            }
                                          >
                                            <MenuItem value="">
                                              <em>Select time</em>
                                            </MenuItem>
                                            <MenuItem value="Morning">Morning (6 AM - 9 AM)</MenuItem>
                                            <MenuItem value="Mid-AM">Mid-AM (9 AM - 12 PM)</MenuItem>
                                            <MenuItem value="Noon">Noon (12 PM - 3 PM)</MenuItem>
                                            <MenuItem value="Afternoon">Afternoon (3 PM - 6 PM)</MenuItem>
                                            <MenuItem value="Evening">Evening (6 PM - 9 PM)</MenuItem>
                                            <MenuItem value="Night">Night (9 PM - 12 AM)</MenuItem>
                                          </Select>
                                          {!medForm.timeSlot && !!medicationError && (
                                            <Typography color="error" variant="caption">
                                              Time of day is required for Once Daily schedule
                                            </Typography>
                                          )}
                                        </FormControl>
                                      </Grid>
                                    )}
                                    {medForm.schedule === "Other" && (
                                      <Grid item xs={12} md={4}>
                                        <TextField
                                          label="Note"
                                          fullWidth
                                          value={medForm.note}
                                          onChange={(e) =>
                                            setMedForm((prev) => ({
                                              ...prev,
                                              note: e.target.value,
                                            }))
                                          }
                                        />
                                      </Grid>
                                    )}
                                    <Grid item xs={12} md={4}>
                                      <FormControlLabel
                                        control={
                                          <Checkbox
                                            checked={!!medForm.isNarcotic}
                                            onChange={(e) => {
                                              setMedForm((prev) => ({
                                                ...prev,
                                                isNarcotic: e.target.checked,
                                                narcoticStorageLocation: e.target.checked ? prev.narcoticStorageLocation : "",
                                              }));
                                            }}
                                          />
                                        }
                                        label="Is Narcotic"
                                      />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                      <FormControlLabel
                                        control={
                                          <Checkbox
                                            checked={!!medForm.isCritical}
                                            onChange={(e) => {
                                              setMedForm((prev) => ({
                                                ...prev,
                                                isCritical: e.target.checked,
                                              }));
                                            }}
                                          />
                                        }
                                        label="Is Critical"
                                      />
                                    </Grid>
                                    {medForm.isNarcotic && (
                                      <Grid item xs={12} md={4}>
                                        <TextField
                                          label="Narcotic Storage Location"
                                          fullWidth
                                          required
                                          value={medForm.narcoticStorageLocation}
                                          onChange={(e) =>
                                            setMedForm((prev) => ({
                                              ...prev,
                                              narcoticStorageLocation: e.target.value,
                                            }))
                                          }
                                          sx={{ "& .MuiFormLabel-asterisk": { color: "#d32f2f" } }}
                                        />
                                      </Grid>
                                    )}
                                    {editingIndex !== null && (
                                      <Grid item xs={12} md={4}>
                                        <FormControlLabel
                                          control={
                                            <Checkbox
                                              checked={!!medForm.discontinued}
                                              onChange={(e) => {
                                                setMedForm((prev) => ({
                                                  ...prev,
                                                  discontinued: e.target.checked,
                                                }));
                                              }}
                                            />
                                          }
                                          label="Discontinue"
                                        />
                                      </Grid>
                                    )}
                                    <Grid item xs={12} md={12}>
                                      <Box sx={{ display: "flex", gap: 1 }}>
                                        <Button
                                          variant="contained"
                                          onClick={
                                            editingIndex === null
                                              ? handleAddMedication
                                              : handleUpdateMedication
                                          }
                                          sx={{
                                            mr: 1,
                                            minWidth: 150,
                                            position: 'relative',
                                            bgcolor: '#48CAE4',
                                            color: '#000000',
                                            fontWeight: 'normal',
                                            '&:hover': {
                                              bgcolor: '#3ab3cc',
                                            },
                                          }}
                                          disabled={isSavingMedication}
                                        >
                                          {isSavingMedication ? (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                              <CircularProgress size={16} color="inherit" />
                                              <span>Saving...</span>
                                            </Box>
                                          ) : (
                                            editingIndex === null ? "Add Medication" : "Update Medication"
                                          )}
                                        </Button>
                                        {editingIndex !== null && (
                                          <Button
                                            variant="outlined"
                                            color="inherit"
                                            onClick={handleCancelEditMedication}
                                          >
                                            Cancel
                                          </Button>
                                        )}
                                      </Box>
                                    </Grid>
                                  </Grid>
                                </Paper>
                              )}

                              {/* Medications Table */}
                              <Paper sx={{ p: 2 }}>
                                <Typography variant="subtitle1" gutterBottom>
                                  Prescribe Medication
                                </Typography>
                                <TableContainer sx={{ overflowX: 'auto' }}>
                                  <Table size="small" sx={{ minWidth: 800 }}>
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Medication</TableCell>
                                        <TableCell>Dose</TableCell>
                                        <TableCell>Route</TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>Medication Start Date</TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>Refill Date</TableCell>
                                        <TableCell>Schedule</TableCell>
                                        <TableCell>Narcotic</TableCell>
                                        <TableCell>Critical</TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}> Scheduled Time</TableCell>
                                        <TableCell>Note</TableCell>

                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>Created By</TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>Created On</TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>Updated By</TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>Updated On</TableCell>
                                        {!isOnlyCaregiver && <TableCell align="right">Actions</TableCell>}
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {medications.map((row, idx) => (
                                        <TableRow key={`${row.med}-${idx}`}>
                                          <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                              {row.discontinued ? (
                                                <img
                                                  src="/assets/discontinued_icon.png"
                                                  alt="Discontinued"
                                                  style={{
                                                    width: 24,
                                                    height: 24,
                                                    cursor: "pointer",
                                                  }}
                                                  title="Discontinued"
                                                />
                                              ) : (
                                                <CheckCircleIcon
                                                  color="success"
                                                  titleAccess="Active"
                                                  sx={{ fontSize: 24, cursor: "pointer" }}
                                                />
                                              )}
                                              {(row as any).isNarcotic && (
                                                <Tooltip title="Narcotic Medication" arrow>
                                                  <Box
                                                    component="img"
                                                    src="/assets/narcotic_icon.png"
                                                    alt="Narcotic"
                                                    sx={{
                                                      width: 20,
                                                      height: 20,
                                                      display: "block",
                                                      objectFit: "contain",
                                                    }}
                                                  />
                                                </Tooltip>
                                              )}
                                              {(row as any).isCritical && (
                                                <Tooltip title="Critical Medication" arrow>
                                                  <Box
                                                    component="img"
                                                    src="/assets/critical1.png"
                                                    alt="Critical"
                                                    sx={{
                                                      width: 26,
                                                      height: 26,
                                                      display: "block",
                                                      objectFit: "contain",
                                                    }}
                                                  />
                                                </Tooltip>
                                              )}
                                            </Box>
                                          </TableCell>
                                          <TableCell
                                            sx={{
                                              whiteSpace: "normal",
                                              overflowWrap: "break-word",
                                              minWidth: "180px",
                                              fontWeight: "bold",
                                              verticalAlign: 'middle'
                                            }}
                                          >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                              {row.med}
                                            </Box>
                                          </TableCell>
                                          <TableCell>{formatDoseForDisplay(row.dose)}</TableCell>
                                          <TableCell>{row.route}</TableCell>
                                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                                            {formatToUIDate(row.start)}
                                          </TableCell>
                                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                                            {formatToUIDate(row.end)}
                                          </TableCell>
                                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                                            {row.schedule}
                                          </TableCell>
                                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                                            {(row as any).isNarcotic ? "Yes" : "No"}
                                          </TableCell>
                                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                                            {(row as any).isCritical ? "Yes" : "No"}
                                          </TableCell>
                                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                                            {(row as any).timeSlot || ((row as any).timeOfDay ? getSlotFromTime((row as any).timeOfDay) : "") || "-"}
                                          </TableCell>
                                          <TableCell
                                            sx={{
                                              whiteSpace: "normal",
                                              overflowWrap: "break-word",
                                              minWidth: "250px",
                                            }}
                                          >
                                            {row.note || "-"}
                                          </TableCell>

                                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                                            {row.createdByName || "-"}
                                          </TableCell>
                                          <TableCell
                                            sx={{ whiteSpace: "nowrap", minWidth: 160 }}
                                          >
                                            {formatToUIDateTimeLong(row.createdOn)}
                                          </TableCell>
                                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                                            {row.updatedByName || "-"}
                                          </TableCell>
                                          <TableCell
                                            sx={{ whiteSpace: "nowrap", minWidth: 160 }}
                                          >
                                            {formatToUIDateTimeLong(row.updatedOn)}
                                          </TableCell>
                                          {!isOnlyCaregiver && (
                                            <TableCell align="right">
                                              {isResidentActive ? (
                                                <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
                                                  <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={() => handleEditMedication(idx)}
                                                    title="Edit"
                                                  >
                                                    <EditIcon fontSize="small" />
                                                  </IconButton>
                                                </Box>
                                              ) : "-"}
                                            </TableCell>
                                          )}
                                        </TableRow>
                                      ))}
                                      {medications.length === 0 && (
                                        <TableRow>
                                          <TableCell colSpan={isOnlyCaregiver ? 13 : 14} align="center">
                                            No Prescribe Medication found.
                                          </TableCell>
                                        </TableRow>
                                      )}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              </Paper>
                            </>
                          )}
                        </>
                      )}
                    </Paper>
                  )
                  }



                  {/* MAR Tab */}
                  {topTab === 2 && (
                    <Paper sx={{ p: 2 }}>
                      <Tabs
                        value={emrTab}
                        variant="scrollable"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                        onChange={(_, v) => setEmrTab(v)}
                        sx={{ mb: 2 }}
                      >
                        <Tab label="Vitals" value={0} />
                        <Tab label="Daily Progress Note" value={1} />
                        <Tab label="Daily Medication" value={2} />
                      </Tabs>


                      {/* Vitals Sub-tab */}
                      {emrTab === 0 && (
                        <>
                          {!selectedResident && !prefilledResident ? (
                            <Alert severity="info" sx={{ mb: 2 }}>
                              Please select a resident from Residents list to view and
                              manage vitals.
                            </Alert>
                          ) : (
                            <VitalsManager
                              residentId={String(effectiveResidentId)}
                              readOnly={!isResidentActive}
                              onSaveSuccess={() => {
                                fetchActivityTimeline();
                                fetchVitalsHistory();
                              }}
                            />
                          )}
                        </>
                      )}

                      {/* Progress Note Sub-tab */}
                      {emrTab === 1 && (
                        <>
                          {!selectedResident && !prefilledResident ? (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mb: 2 }}
                            >
                              Please select a resident to view progress notes.
                            </Typography>
                          ) : (
                            <ProgressNotesSection
                              residentId={effectiveResidentId}
                              residentName={
                                residentBanner ? residentBanner.name : undefined
                              }
                              onSaveSuccess={fetchActivityTimeline}
                              readOnly={!isResidentActive}
                            />
                          )}
                        </>
                      )}

                      {/* Nurse Notes Sub-tab */}
                      {/* Daily Medication - showing MAR directly */}
                      {emrTab === 2 && (
                        <Box sx={{ mt: 2 }}>
                          {effectiveResidentId ? (
                            <MedicationAdministrationRecord
                              residentId={String(effectiveResidentId)}
                              showResidentName={false}
                              refreshTrigger={marRefreshTrigger}
                              onSaveSuccess={fetchActivityTimeline}
                              readOnly={!isResidentActive}
                            />
                          ) : (
                            <Alert severity="info">
                              Please select a resident to view their Medication Administration Record.
                            </Alert>
                          )}
                        </Box>
                      )}
                    </Paper>
                  )
                  }

                  {/* Clinical Notes Tab - Keeping for backward compatibility */}
                  {
                    false && (
                      <Paper sx={{ p: 2 }}>
                        <Card>
                          <CardContent>
                            <Typography variant="h6" gutterBottom>
                              Add New Clinical Note
                            </Typography>
                            <form onSubmit={handleSubmit(onSubmit)}>
                              {/* Resident is chosen from patient context / selection - not from form */}
                              <Box sx={{ mb: 2 }}>
                                <Controller
                                  name="noteType"
                                  control={control}
                                  render={({ field }) => (
                                    <FormControl fullWidth error={!!errors.noteType}>
                                      <InputLabel>Note Type</InputLabel>
                                      <Select {...field} label="Note Type">
                                        <MenuItem value="PROGRESS">Progress Note</MenuItem>
                                        <MenuItem value="NURSE">Nurse Note</MenuItem>
                                        <MenuItem value="SOCIAL_WORK">
                                          Social Work Note
                                        </MenuItem>
                                        <MenuItem value="SPIRITUAL_CARE">
                                          Spiritual Care Note
                                        </MenuItem>
                                        <MenuItem value="PHYSICIAN">
                                          Physician Note
                                        </MenuItem>
                                      </Select>
                                    </FormControl>
                                  )}
                                />
                                {errors.noteType && (
                                  <Typography color="error" variant="caption">
                                    {errors.noteType?.message}
                                  </Typography>
                                )}
                              </Box>
                              <Box sx={{ mb: 2 }}>
                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                  <Controller
                                    name="noteDate"
                                    control={control}
                                    render={({ field }) => (
                                      <DateTimePicker
                                        label="Date & Time"
                                        format="dd/MM/yyyy hh:mm a"
                                        value={field.value}
                                        onChange={field.onChange}
                                        slotProps={{
                                          textField: {
                                            fullWidth: true,
                                            error: !!errors.noteDate,
                                          },
                                        }}
                                      />
                                    )}
                                  />
                                </LocalizationProvider>
                                {errors.noteDate && (
                                  <Typography color="error" variant="caption">
                                    {errors.noteDate?.message}
                                  </Typography>
                                )}
                              </Box>
                              <Box sx={{ mb: 2 }}>
                                <Controller
                                  name="content"
                                  control={control}
                                  render={({ field }) => (
                                    <TextField
                                      {...field}
                                      fullWidth
                                      label="Note Content"
                                      multiline
                                      rows={6}
                                      error={!!errors.content}
                                      helperText={errors.content?.message}
                                    />
                                  )}
                                />
                              </Box>
                              <Box
                                sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}
                              >
                                <Button
                                  type="submit"
                                  variant="contained"
                                  sx={{ py: 1.3, px: 4 }}
                                >
                                  {editingNoteId ? "Update Note" : "Save Clinical Note"}
                                </Button>
                              </Box>
                            </form>
                          </CardContent>
                        </Card>

                        {/* Clinical Notes Table */}
                        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                          Clinical Notes List
                          {selectedResident || prefilledResident ? (
                            <Typography
                              component="span"
                              variant="body2"
                              color="text.secondary"
                              sx={{ ml: 2 }}
                            >
                              {residentBanner?.name && `for ${residentBanner.name}`}
                            </Typography>
                          ) : null}
                        </Typography>
                        {!selectedResident && !prefilledResident ? (
                          <Alert severity="info" sx={{ mb: 2 }}>
                            Please select a resident from Residents list to view and manage
                            clinical notes.
                          </Alert>
                        ) : null}
                        <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                          <Table sx={{ minWidth: 800 }}>
                            <TableHead>
                              <TableRow>
                                <TableCell>Resident</TableCell>
                                <TableCell>Note Type</TableCell>
                                <TableCell>Author</TableCell>
                                <TableCell>DateTime</TableCell>
                                <TableCell>Status</TableCell>
                                {!isOnlyCaregiver && <TableCell align="right">Actions</TableCell>}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {!filteredNotes || filteredNotes.length === 0 ? (
                                <TableRow>
                                  <TableCell colSpan={isOnlyCaregiver ? 5 : 6} align="center">
                                    {selectedResident || prefilledResident
                                      ? `No clinical notes found for ${residentBanner?.name || "this resident"}.`
                                      : "No clinical notes found. Please select a resident to view notes."}
                                  </TableCell>
                                </TableRow>
                              ) : (
                                filteredNotes.map((note) => (
                                  <TableRow key={note.id}>
                                    <TableCell>
                                      <Typography variant="body2">
                                        {getResidentNameForNote(note) || "—"}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Chip
                                        label={note.noteType}
                                        size="small"
                                        color="primary"
                                      />
                                    </TableCell>
                                    <TableCell>
                                      {(() => {
                                        if (note.authorName) return note.authorName;
                                        if (note.createdByName) return note.createdByName;
                                        const userId = note.authorId || note.createdBy;
                                        if (!userId) return "—";



                                        if (
                                          user &&
                                          (user.id === userId || user.username === userId)
                                        ) {
                                          return user.name || user.username;
                                        }

                                        return "—";
                                      })()}
                                    </TableCell>
                                    <TableCell>
                                      {formatToUIDateTime(note.noteDate)}
                                    </TableCell>
                                    <TableCell>
                                      <Chip
                                        label={note.isSigned ? "Signed" : "Draft"}
                                        size="small"
                                        color={note.isSigned ? "success" : "warning"}
                                      />
                                    </TableCell>
                                    {!isOnlyCaregiver && (
                                      <TableCell align="right">
                                        <Box
                                          sx={{
                                            display: "flex",
                                            gap: 1,
                                            justifyContent: "flex-end",
                                          }}
                                        >
                                          <IconButton
                                            size="small"
                                            color="primary"
                                            onClick={() => handleEditNote(note)}
                                          >
                                            <EditIcon />
                                          </IconButton>
                                          <Button
                                            size="small"
                                            color="error"
                                            variant="outlined"
                                            onClick={() => handleDeleteNote(note.id)}
                                          >
                                            Delete
                                          </Button>
                                        </Box>
                                      </TableCell>
                                    )}
                                  </TableRow>
                                ))
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Paper>
                    )
                  }

                  {/* Care Plan Tab */}
                  {
                    topTab === 3 && (
                      <Paper sx={{ p: 2 }}>
                        {!selectedResident && !prefilledResident ? (
                          <Alert severity="info" sx={{ mb: 2 }}>
                            Please select a resident from Residents list to view and manage
                            care plans.
                          </Alert>
                        ) : null}
                        <Tabs
                          value={carePlanTab}
                          variant="scrollable"
                          scrollButtons="auto"
                          allowScrollButtonsMobile
                          onChange={(_, v) => setCarePlanTab(v)}
                          sx={{ mb: 2 }}
                        >
                          <Tab label="Dietary Restrictions" />
                          <Tab label="Daily Activities" />
                          <Tab label="Ambulation" />
                          <Tab label="Wound Care/Skin Check" />
                        </Tabs>
                        {carePlanError && (
                          <Alert
                            severity="error"
                            sx={{ mb: 2 }}
                            onClose={() => setCarePlanError(null)}
                          >
                            {carePlanError}
                          </Alert>
                        )}
                        {carePlanSuccess && (
                          <Alert
                            severity="success"
                            sx={{ mb: 2 }}
                            onClose={() => setCarePlanSuccess(null)}
                          >
                            {carePlanSuccess}
                          </Alert>
                        )}
                        {carePlanTab === 0 && (
                          <Box sx={{ pt: 2 }}>
                            <Typography variant="h6">Dietary Restrictions</Typography>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2, justifyContent: "space-between" }}>
                              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                                {!isOnlyCaregiver && (
                                  <DietaryRestrictionsDropdown
                                    onAdd={(tag) => {
                                      const currentVal = carePlanData.diagnosticsPlan || "";
                                      const newVal = currentVal ? `${currentVal}\n- ${tag}` : `- ${tag}`;
                                      setCarePlanData((prev) => ({ ...prev, diagnosticsPlan: newVal }));
                                    }}
                                    onRemove={(tag) => {
                                      let currentVal = carePlanData.diagnosticsPlan || "";
                                      const newVal = currentVal
                                        .replace(new RegExp(`\\n- ${tag.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(?=\\n|$)`, "g"), "")
                                        .replace(new RegExp(`^- ${tag.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\n?`, "g"), "")
                                        .trim();
                                      setCarePlanData((prev) => ({ ...prev, diagnosticsPlan: newVal }));
                                    }}
                                  />
                                )}
                              </Box>
                              {!isOnlyCaregiver && (
                                <Button
                                  variant="outlined"
                                  sx={{ minWidth: "auto", px: 1, py: 0, height: "26px", fontSize: "0.75rem", alignSelf: "center", textTransform: "none", color: "text.secondary", borderColor: "divider" }}
                                  onClick={() => setCarePlanData((prev) => ({ ...prev, diagnosticsPlan: "" }))}
                                >
                                  Clear
                                </Button>
                              )}
                            </Box>
                            <Grid container spacing={2} sx={{ mt: 3 }}>
                              <Grid item xs={12}>
                                <TextField
                                  label="Dietary Restrictions"
                                  fullWidth
                                  multiline
                                  rows={8}
                                  value={carePlanData.diagnosticsPlan || ""}
                                  InputProps={{
                                    readOnly: isStrictlyCaregiver,
                                    endAdornment: (
                                      <InputAdornment position="end" sx={{ alignSelf: "flex-start", mt: 1 }}>
                                        {!isStrictlyCaregiver && (
                                          <Tooltip title={listeningField === "diagnosticsPlan" ? "Stop listening" : "Dictate (Speech to Text)"}>
                                            <IconButton
                                              size="small"
                                              onClick={() => handleMicClick("diagnosticsPlan")}
                                              color={listeningField === "diagnosticsPlan" ? "error" : "default"}
                                              sx={{
                                                animation: listeningField === "diagnosticsPlan" ? "pulse 1.2s infinite" : "none",
                                                "@keyframes pulse": {
                                                  "0%": { boxShadow: "0 0 0 0 rgba(211,47,47,0.4)" },
                                                  "70%": { boxShadow: "0 0 0 8px rgba(211,47,47,0)" },
                                                  "100%": { boxShadow: "0 0 0 0 rgba(211,47,47,0)" },
                                                },
                                              }}
                                            >
                                              <MicIcon />
                                            </IconButton>
                                          </Tooltip>
                                        )}
                                      </InputAdornment>
                                    ),
                                  }}
                                  sx={{
                                    "& .MuiInputBase-input": {
                                      color: "#000000",
                                      WebkitTextFillColor: "#000000"
                                    }
                                  }}
                                  onChange={(e) => {
                                    setCarePlanData((prev) => ({
                                      ...prev,
                                      diagnosticsPlan: e.target.value,
                                    }));
                                  }}
                                />
                              </Grid>
                            </Grid>
                          </Box>
                        )}
                        {carePlanTab === 1 && (
                          <Box sx={{ pt: 2 }}>
                            <Typography variant="h6">Daily Activities</Typography>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2, justifyContent: "space-between" }}>
                              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                                {!isOnlyCaregiver && (
                                  <DailyActivitiesDropdown
                                    onAdd={(tag) => {
                                      const currentVal = carePlanData.dailyActivities || "";
                                      const newVal = currentVal ? `${currentVal}\n- ${tag}` : `- ${tag}`;
                                      setCarePlanData((prev) => ({ ...prev, dailyActivities: newVal }));
                                    }}
                                    onRemove={(tag) => {
                                      let currentVal = carePlanData.dailyActivities || "";
                                      // Find and remove the exact tag line
                                      const newVal = currentVal
                                        .replace(new RegExp(`\\n- ${tag.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(?=\\n|$)`, "g"), "")
                                        .replace(new RegExp(`^- ${tag.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\n?`, "g"), "")
                                        .trim();
                                      setCarePlanData((prev) => ({ ...prev, dailyActivities: newVal }));
                                    }}
                                  />
                                )}
                              </Box>
                              {!isOnlyCaregiver && (
                                <Button
                                  variant="outlined"
                                  sx={{ minWidth: "auto", px: 1, py: 0, height: "26px", fontSize: "0.75rem", alignSelf: "center", textTransform: "none", color: "text.secondary", borderColor: "divider" }}
                                  onClick={() => setCarePlanData((prev) => ({ ...prev, dailyActivities: "" }))}
                                >
                                  Clear
                                </Button>
                              )}
                            </Box>
                            <Grid container spacing={2} sx={{ mt: 3 }}>
                              <Grid item xs={12}>
                                <TextField
                                  label="Daily Activities"
                                  fullWidth
                                  multiline
                                  rows={8}
                                  value={carePlanData.dailyActivities}
                                  InputProps={{
                                    readOnly: isStrictlyCaregiver,
                                    endAdornment: (
                                      <InputAdornment position="end" sx={{ alignSelf: "flex-start", mt: 1 }}>
                                        {!isStrictlyCaregiver && (
                                          <Tooltip title={listeningField === "dailyActivities" ? "Stop listening" : "Dictate (Speech to Text)"}>
                                            <IconButton
                                              size="small"
                                              onClick={() => handleMicClick("dailyActivities")}
                                              color={listeningField === "dailyActivities" ? "error" : "default"}
                                              sx={{
                                                animation: listeningField === "dailyActivities" ? "pulse 1.2s infinite" : "none",
                                                "@keyframes pulse": {
                                                  "0%": { boxShadow: "0 0 0 0 rgba(211,47,47,0.4)" },
                                                  "70%": { boxShadow: "0 0 0 8px rgba(211,47,47,0)" },
                                                  "100%": { boxShadow: "0 0 0 0 rgba(211,47,47,0)" },
                                                },
                                              }}
                                            >
                                              <MicIcon />
                                            </IconButton>
                                          </Tooltip>
                                        )}
                                      </InputAdornment>
                                    ),
                                  }}
                                  sx={{
                                    "& .MuiInputBase-input": {
                                      color: "#000000",
                                      WebkitTextFillColor: "#000000"
                                    }
                                  }}
                                  onChange={(e) => {
                                    setCarePlanData((prev) => ({
                                      ...prev,
                                      dailyActivities: e.target.value,
                                    }));
                                  }}
                                />
                              </Grid>
                            </Grid>
                          </Box>
                        )}
                        {carePlanTab === 2 && (
                          <Box sx={{ pt: 2 }}>
                            <Typography variant="h6">Ambulation</Typography>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2, justifyContent: "space-between" }}>
                              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                                {!isOnlyCaregiver && (
                                  <AmbulationDropdown
                                    onAdd={(tag) => {
                                      const currentVal = carePlanData.ambulation || "";
                                      const newVal = currentVal ? `${currentVal}\n- ${tag}` : `- ${tag}`;
                                      setCarePlanData((prev) => ({ ...prev, ambulation: newVal }));
                                    }}
                                    onRemove={(tag) => {
                                      let currentVal = carePlanData.ambulation || "";
                                      const newVal = currentVal
                                        .replace(new RegExp(`\\n- ${tag.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(?=\\n|$)`, "g"), "")
                                        .replace(new RegExp(`^- ${tag.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\n?`, "g"), "")
                                        .trim();
                                      setCarePlanData((prev) => ({ ...prev, ambulation: newVal }));
                                    }}
                                  />
                                )}
                              </Box>
                              {!isOnlyCaregiver && (
                                <Button
                                  variant="outlined"
                                  sx={{ minWidth: "auto", px: 1, py: 0, height: "26px", fontSize: "0.75rem", alignSelf: "center", textTransform: "none", color: "text.secondary", borderColor: "divider" }}
                                  onClick={() => setCarePlanData((prev) => ({ ...prev, ambulation: "" }))}
                                >
                                  Clear
                                </Button>
                              )}
                            </Box>
                            <Grid container spacing={2} sx={{ mt: 3 }}>
                              <Grid item xs={12}>
                                <TextField
                                  label="Ambulation"
                                  fullWidth
                                  multiline
                                  rows={8}
                                  value={carePlanData.ambulation}
                                  InputProps={{
                                    readOnly: isStrictlyCaregiver,
                                    endAdornment: (
                                      <InputAdornment position="end" sx={{ alignSelf: "flex-start", mt: 1 }}>
                                        {!isStrictlyCaregiver && (
                                          <Tooltip title={listeningField === "ambulation" ? "Stop listening" : "Dictate (Speech to Text)"}>
                                            <IconButton
                                              size="small"
                                              onClick={() => handleMicClick("ambulation")}
                                              color={listeningField === "ambulation" ? "error" : "default"}
                                              sx={{
                                                animation: listeningField === "ambulation" ? "pulse 1.2s infinite" : "none",
                                                "@keyframes pulse": {
                                                  "0%": { boxShadow: "0 0 0 0 rgba(211,47,47,0.4)" },
                                                  "70%": { boxShadow: "0 0 0 8px rgba(211,47,47,0)" },
                                                  "100%": { boxShadow: "0 0 0 0 rgba(211,47,47,0)" },
                                                },
                                              }}
                                            >
                                              <MicIcon />
                                            </IconButton>
                                          </Tooltip>
                                        )}
                                      </InputAdornment>
                                    ),
                                  }}
                                  sx={{
                                    "& .MuiInputBase-input": {
                                      color: "#000000",
                                      WebkitTextFillColor: "#000000"
                                    }
                                  }}
                                  onChange={(e) => {
                                    setCarePlanData((prev) => ({
                                      ...prev,
                                      ambulation: e.target.value,
                                    }));
                                  }}
                                />
                              </Grid>
                            </Grid>
                          </Box>
                        )}
                        {carePlanTab === 3 && (
                          <Box sx={{ pt: 2 }}>
                            <Typography variant="h6">Wound Care / Skin Check</Typography>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2, justifyContent: "space-between" }}>
                              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                                {!isStrictlyCaregiver && (
                                  <WoundCareDropdown
                                    onAdd={(tagValue) => {
                                      const currentVal = carePlanData.woundCare || "";
                                      const newVal = currentVal ? `${currentVal}\n- ${tagValue}` : `- ${tagValue}`;
                                      setCarePlanData((prev) => ({ ...prev, woundCare: newVal }));
                                    }}
                                    onRemove={(tagValue) => {
                                      let text = carePlanData.woundCare || "";
                                      const regex = new RegExp(`(^|\\n)-\\s*${tagValue}(\\n|$)`, 'g');
                                      text = text.replace(regex, '$1').trim();
                                      setCarePlanData((prev) => ({ ...prev, woundCare: text }));
                                    }}
                                  />
                                )}
                              </Box>
                              {!isStrictlyCaregiver && (
                                <Button
                                  variant="outlined"
                                  sx={{ minWidth: "auto", px: 1, py: 0, height: "26px", fontSize: "0.75rem", alignSelf: "center", textTransform: "none", color: "text.secondary", borderColor: "divider" }}
                                  onClick={() => setCarePlanData((prev) => ({ ...prev, woundCare: "" }))}
                                >
                                  Clear
                                </Button>
                              )}
                            </Box>
                            <Grid container spacing={2} sx={{ mt: 3 }}>
                              <Grid item xs={12}>
                                <TextField
                                  label="Wound Care / Skin Check"
                                  fullWidth
                                  multiline
                                  rows={8}
                                  value={carePlanData.woundCare}
                                  InputProps={{
                                    readOnly: isStrictlyCaregiver,
                                    endAdornment: (
                                      <InputAdornment position="end" sx={{ alignSelf: "flex-start", mt: 1 }}>
                                        {!isStrictlyCaregiver && (
                                          <Tooltip title={listeningField === "woundCare" ? "Stop listening" : "Dictate (Speech to Text)"}>
                                            <IconButton
                                              size="small"
                                              onClick={() => handleMicClick("woundCare")}
                                              color={listeningField === "woundCare" ? "error" : "default"}
                                              sx={{
                                                animation: listeningField === "woundCare" ? "pulse 1.2s infinite" : "none",
                                                "@keyframes pulse": {
                                                  "0%": { boxShadow: "0 0 0 0 rgba(211,47,47,0.4)" },
                                                  "70%": { boxShadow: "0 0 0 8px rgba(211,47,47,0)" },
                                                  "100%": { boxShadow: "0 0 0 0 rgba(211,47,47,0)" },
                                                },
                                              }}
                                            >
                                              <MicIcon />
                                            </IconButton>
                                          </Tooltip>
                                        )}
                                      </InputAdornment>
                                    ),
                                  }}
                                  sx={{
                                    "& .MuiInputBase-input": {
                                      color: "#000000",
                                      WebkitTextFillColor: "#000000"
                                    }
                                  }}
                                  onChange={(e) => {
                                    setCarePlanData((prev) => ({
                                      ...prev,
                                      woundCare: e.target.value,
                                    }));
                                  }}
                                />
                              </Grid>
                            </Grid>
                          </Box>
                        )}
                        {!isStrictlyCaregiver && (
                          <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
                            <Button
                              variant="contained"
                              size="medium"
                              sx={{
                                py: 1.25,
                                minWidth: 130,
                                bgcolor: "#48CAE4",
                                color: "#000000",
                                fontWeight: "normal",
                                "&:hover": { bgcolor: "#3ab3cc" },
                                "&.Mui-disabled": {
                                  bgcolor: "rgba(0, 0, 0, 0.12)",
                                  color: "rgba(0, 0, 0, 0.26)"
                                }
                              }}
                              onClick={handleSaveCarePlan}
                              disabled={isSavingCarePlan || !effectiveResidentId || !isResidentActive}
                            >
                              {isSavingCarePlan ? (
                                <>
                                  <CircularProgress size={18} sx={{ mr: 1 }} />
                                  Saving...
                                </>
                              ) : (
                                "Save Plan"
                              )}
                            </Button>
                            {!effectiveResidentId && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ mt: 1, display: "block", textAlign: "center", width: "100%" }}
                              >
                                Please select a resident before saving the care plan.
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Paper>
                    )
                  }

                  {/* Medical Appointment Tab */}
                  {
                    topTab === 4 && (
                      <Paper sx={{ p: 3 }}>
                        {!selectedResident && !prefilledResident ? (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 2 }}
                          >
                            Please select a resident to manage appointments.
                          </Typography>
                        ) : (
                          <Box>
                            <Typography variant="h6" gutterBottom sx={{ mt: 1 }}>
                              Medical Appointment
                            </Typography>

                            {appointmentSuccess && (
                              <Alert
                                severity="success"
                                sx={{ mb: 2 }}
                                onClose={() => setAppointmentSuccess(null)}
                              >
                                {appointmentSuccess}
                              </Alert>
                            )}

                            {appointmentError && (
                              <Alert
                                severity="error"
                                sx={{ mb: 2 }}
                                onClose={() => setAppointmentError(null)}
                              >
                                {appointmentError}
                              </Alert>
                            )}

                            {appointmentInfo && (
                              <Alert
                                severity="info"
                                sx={{ mb: 2 }}
                              >
                                {appointmentInfo}
                              </Alert>
                            )}

                            {isResidentActive && !isCaregiver ? (
                              <Paper sx={{ p: 3, pt: 2 }}>
                                <Grid container spacing={3} sx={{ mt: 1 }}>
                                  {/* Row 1: DateTime & Hospital Name */}
                                  <Grid item xs={12} md={6}>
                                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                                      <DateTimePicker
                                        label="Appointment Date & Time"
                                        value={appointmentForm.dateTime}
                                        disablePast
                                        format="dd/MM/yyyy hh:mm a"
                                        onChange={(v: Date | null) =>
                                          setAppointmentForm((prev) => ({
                                            ...prev,
                                            dateTime: v,
                                          }))
                                        }
                                        slotProps={{
                                          textField: {
                                            fullWidth: true,
                                            required: true,
                                            error: !appointmentForm.dateTime && !!appointmentError,
                                            helperText: !appointmentForm.dateTime && !!appointmentError ? "Date & Time is required" : "",
                                            sx: { "& .MuiFormLabel-asterisk": { color: "#d32f2f" } },
                                          },
                                        }}
                                      />
                                    </LocalizationProvider>
                                  </Grid>

                                  <Grid item xs={12} md={6}>
                                    <TextField
                                      label="Hospital Name"
                                      fullWidth
                                      required
                                      value={appointmentForm.hospitalName}
                                      error={!appointmentForm.hospitalName && !!appointmentError}
                                      helperText={!appointmentForm.hospitalName && !!appointmentError ? "Hospital Name is required" : ""}
                                      onChange={(e) =>
                                        setAppointmentForm((prev) => ({
                                          ...prev,
                                          hospitalName: e.target.value,
                                        }))
                                      }
                                      sx={{ "& .MuiFormLabel-asterisk": { color: "#d32f2f" } }}
                                    />
                                  </Grid>

                                  {/* Row 2: Doctor and Appointment Type */}
                                  <Grid item xs={12} md={6}>
                                    <FormControl fullWidth>
                                      <TextField
                                        label="Doctor"
                                        fullWidth
                                        value={appointmentForm.doctor}
                                        onChange={(e) => {
                                          setAppointmentForm((prev) => ({
                                            ...prev,
                                            doctor: e.target.value,
                                          }));
                                        }}
                                      />
                                    </FormControl>
                                  </Grid>

                                  <Grid item xs={12} md={6}>
                                    <FormControl
                                      fullWidth
                                      required
                                      error={!appointmentForm.type && !!appointmentError}
                                    >
                                      <InputLabel sx={{ "& .MuiFormLabel-asterisk": { color: "#d32f2f" } }}>Appointment Type</InputLabel>
                                      <Select
                                        value={appointmentForm.type}
                                        label="Appointment Type"
                                        onChange={(e) =>
                                          setAppointmentForm((prev) => ({
                                            ...prev,
                                            type: e.target.value as string,
                                          }))
                                        }
                                      >
                                        <MenuItem value=""><em>Select Type</em></MenuItem>
                                        {appointmentTypeOptions.map((opt) => (
                                          <MenuItem key={opt.id} value={opt.valueName}>
                                            {opt.valueName}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                      {!appointmentForm.type && !!appointmentError && (
                                        <FormHelperText>
                                          Appointment Type is required
                                        </FormHelperText>
                                      )}
                                    </FormControl>
                                  </Grid>

                                  {/* Row 3: Appointment Reason */}
                                  <Grid item xs={12}>
                                    <TextField
                                      label="Appointment Reason"
                                      fullWidth
                                      multiline
                                      rows={3}
                                      value={appointmentForm.reason}
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        if (/^[a-zA-Z0-9\s]*$/.test(val)) {
                                          setAppointmentForm((prev) => ({
                                            ...prev,
                                            reason: val,
                                          }));
                                        }
                                      }}
                                    />
                                  </Grid>

                                  {/* Billing & Transportation Checkboxes */}
                                  <Grid item xs={12}>
                                    <Stack direction="row" spacing={3}>
                                      <FormControlLabel
                                        control={
                                          <Checkbox
                                            sx={{ mr: -0.5 }}
                                            checked={appointmentForm.includeInBilling}
                                            onChange={(e) =>
                                              setAppointmentForm((prev) => ({
                                                ...prev,
                                                includeInBilling: e.target.checked,
                                              }))
                                            }
                                          />
                                        }
                                        label="Include In Billing"
                                      />
                                      <FormControlLabel
                                        control={
                                          <Checkbox
                                            sx={{ mr: -0.5 }}
                                            checked={appointmentForm.isTransportationScheduled}
                                            onChange={(e) =>
                                              setAppointmentForm((prev) => ({
                                                ...prev,
                                                isTransportationScheduled: e.target.checked,
                                              }))
                                            }
                                          />
                                        }
                                        label="Is Transportation Scheduled"
                                      />
                                    </Stack>
                                  </Grid>

                                  {/* Conditional Transportation Fields */}
                                  {appointmentForm.isTransportationScheduled && (
                                    <>
                                      <Grid item xs={12} md={4}>
                                        <TextField
                                          label="Vendor"
                                          fullWidth
                                          value={appointmentForm.vendor}
                                          onChange={(e) =>
                                            setAppointmentForm((prev) => ({
                                              ...prev,
                                              vendor: e.target.value,
                                            }))
                                          }
                                        />
                                      </Grid>
                                      <Grid item xs={12} md={4}>
                                        <TextField
                                          label="Transport Contact Number"
                                          fullWidth
                                          value={appointmentForm.transportContactNumber}
                                          error={!!appointmentForm.transportContactNumber && appointmentForm.transportContactNumber.length !== 10}
                                          helperText={!!appointmentForm.transportContactNumber && appointmentForm.transportContactNumber.length !== 10 ? "Contact number must be exactly 10 digits" : ""}
                                          inputProps={{ maxLength: 10 }}
                                          onChange={(e) => {
                                            const val = e.target.value;
                                            if (/^[0-9]*$/.test(val) && val.length <= 10) {
                                              setAppointmentForm((prev) => ({
                                                ...prev,
                                                transportContactNumber: val,
                                              }))
                                            }
                                          }}
                                        />
                                      </Grid>
                                      <Grid item xs={12} md={4}>
                                        <FormControl fullWidth>
                                          <InputLabel>Mobility Type</InputLabel>
                                          <Select
                                            value={appointmentForm.mobilityType}
                                            label="Mobility Type"
                                            onChange={(e) =>
                                              setAppointmentForm((prev) => ({
                                                ...prev,
                                                mobilityType: e.target.value as string,
                                              }))
                                            }
                                          >
                                            <MenuItem value=""><em>Select Mobility</em></MenuItem>
                                            {mobilityTypeOptions.map((opt) => (
                                              <MenuItem key={opt.id} value={opt.valueName}>
                                                {opt.valueName}
                                              </MenuItem>
                                            ))}
                                          </Select>
                                        </FormControl>
                                      </Grid>
                                    </>
                                  )}

                                  {/* Save Button */}
                                  <Grid
                                    item
                                    xs={12}
                                    sx={{ display: "flex", justifyContent: "flex-end" }}
                                  >
                                    <Button
                                      variant="contained"
                                      onClick={handleSaveAppointment}
                                      disabled={
                                        isSavingAppointment ||
                                        (() => {
                                          if (!appointmentForm.dateTime)
                                            return false;
                                          return new Date(appointmentForm.dateTime) < new Date();
                                        })()
                                      }
                                      sx={{
                                        bgcolor: "#48CAE4",
                                        color: "#000000",
                                        fontWeight: "normal",
                                        "&:hover": { bgcolor: "#3ab3cc" },
                                        "&.Mui-disabled": {
                                          bgcolor: "rgba(0, 0, 0, 0.12)",
                                          color: "rgba(0, 0, 0, 0.26)"
                                        }
                                      }}
                                    >
                                      {isSavingAppointment ? (
                                        <CircularProgress size={24} color="inherit" />
                                      ) : editingAppointmentId ? (
                                        "Update Appointment"
                                      ) : (
                                        "Save"
                                      )}
                                    </Button>
                                    {editingAppointmentId && (
                                      <Button
                                        variant="outlined"
                                        onClick={handleCancelAppointmentEdit}
                                        disabled={isSavingAppointment}
                                        sx={{
                                          ml: 2,
                                          width: "fit-content",
                                          minWidth: 100,
                                        }}
                                      >
                                        Cancel
                                      </Button>
                                    )}
                                  </Grid>
                                </Grid>
                              </Paper>
                            ) : null}
                            {!isCaregiver && !isResidentActive && (
                              <Alert severity="info" sx={{ mb: 2 }}>
                                Only Active residents can have new appointments scheduled.
                              </Alert>
                            )}

                            <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                              Appointment List
                            </Typography>
                            <TableContainer component={Paper}>
                              <Table>
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Date & Time</TableCell>
                                    <TableCell>Doctor</TableCell>
                                    <TableCell>Hospital Name</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Reason</TableCell>
                                    <TableCell>Created By</TableCell>
                                    <TableCell>Created On</TableCell>
                                    <TableCell>Updated By</TableCell>
                                    <TableCell>Updated On</TableCell>
                                    {!isCaregiver && (
                                      <TableCell align="right">{"Actions"}</TableCell>
                                    )}
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {appointments.length === 0 ? (
                                    <TableRow>
                                      <TableCell colSpan={isCaregiver ? 9 : 10} align="center">
                                        No appointments found for this resident.
                                      </TableCell>
                                    </TableRow>
                                  ) : (
                                    appointments.map((appt, idx) => {
                                      let displayDate = appt.appointmentDate || "-";
                                      try {
                                        if (appt.appointmentDate) {
                                          // Parse YYYY-MM-DD as local date components to avoid timezone shifts
                                          const [y, m, d] = appt.appointmentDate
                                            .split("-")
                                            .map(Number);
                                          // Month is 0-indexed in Date constructor
                                          const dateObj = new Date(y, m - 1, d);
                                          displayDate = formatToUIDate(dateObj);
                                        }
                                      } catch (e) {
                                        console.error("Date formatting error", e);
                                      }

                                      let displayTime = appt.appointmentTime || "";
                                      try {
                                        if (displayTime) {
                                          // Parse time strings strictly if needed, usually "HH:mm:ss" or "HH:mm" from backend
                                          // Prepend a dummy date to allow parsing
                                          const timeStr =
                                            displayTime.length === 5
                                              ? `${displayTime}:00`
                                              : displayTime;
                                          const timeDate = new Date(
                                            `2000-01-01T${timeStr}`,
                                          );
                                          if (!isNaN(timeDate.getTime())) {
                                            displayTime = format(timeDate, "hh:mm a");
                                          } else {
                                            // Fallback if Date parsing fails but string is usable
                                            if (displayTime.length >= 5)
                                              displayTime = displayTime.substring(0, 5);
                                          }
                                        }
                                      } catch (e) {
                                        console.error("Time formatting error", e);
                                        if (displayTime.length >= 5)
                                          displayTime = displayTime.substring(0, 5);
                                      }

                                      // Doctor mapping update
                                      let doctorName = "-";

                                      if (appt.doctorName) {
                                        doctorName = appt.doctorName;
                                      } else if (appt.userId) {
                                        doctorName = appt.userId;
                                      } else if (appt.doctorId) {
                                        doctorName = appt.doctorId;
                                      }

                                      const hospitalName =
                                        appt.hospitalName ||
                                        appt.hospital_name ||
                                        "-";

                                      return (
                                        <TableRow key={appt.id || idx}>
                                          <TableCell
                                            sx={{ whiteSpace: "nowrap", minWidth: 160 }}
                                          >
                                            {displayDate} {displayTime}
                                          </TableCell>
                                          <TableCell sx={{ wordBreak: "break-word" }}>
                                            {doctorName}
                                          </TableCell>
                                          <TableCell sx={{ wordBreak: "break-word" }}>
                                            {hospitalName}
                                          </TableCell>
                                          <TableCell>
                                            <Chip
                                              label={(() => {
                                                const type = appt.appointmentType;
                                                if (!type) return "";
                                                return type
                                                  .replace(/_/g, " ")
                                                  .toLowerCase()
                                                  .replace(/\b\w/g, (char: string) =>
                                                    char.toUpperCase(),
                                                  );
                                              })()}
                                              size="small"
                                              color="primary"
                                              variant="outlined"
                                            />
                                          </TableCell>
                                          <TableCell
                                            sx={{
                                              whiteSpace: "normal",
                                              wordBreak: "break-word",
                                            }}
                                          >
                                            {appt.reason}
                                          </TableCell>
                                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                                            {(() => {
                                              const name = appt.createdByName;
                                              if (name && name !== "-") return name;

                                              const creatorId =
                                                appt.createdBy || appt.created_by;
                                              if (creatorId) {
                                                const display =
                                                  getUserDisplay(creatorId);
                                                if (display && display !== "-")
                                                  return display;
                                                return creatorId;
                                              }

                                              return "-";
                                            })()}
                                          </TableCell>
                                          <TableCell
                                            sx={{ whiteSpace: "nowrap", minWidth: 160 }}
                                          >
                                            {formatToUIDateTimeLong(appt.createdDate || appt.createdAt || appt.created_at)}
                                          </TableCell>
                                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                                            {(() => {
                                              const name = appt.updatedByName;
                                              if (name && name !== "-") return name;

                                              const updaterId =
                                                appt.updatedBy ||
                                                appt.updated_by ||
                                                appt.lastModifiedBy;
                                              if (updaterId) {
                                                const display =
                                                  getUserDisplay(updaterId);
                                                if (display && display !== "-")
                                                  return display;
                                                return updaterId;
                                              }

                                              return "-";
                                            })()}
                                          </TableCell>
                                          <TableCell
                                            sx={{ whiteSpace: "nowrap", minWidth: 160 }}
                                          >
                                            {formatToUIDateTimeLong(appt.updatedDate || appt.updatedAt || appt.updated_at)}
                                          </TableCell>
                                          {!isCaregiver && (
                                            <TableCell align="right">
                                              <Box
                                                sx={{
                                                  display: "flex",
                                                  justifyContent: "flex-end",
                                                  gap: 1,
                                                }}
                                              >
                                                <IconButton
                                                  size="small"
                                                  color="primary"
                                                  onClick={() =>
                                                    handleEditAppointment(appt)
                                                  }
                                                >
                                                  <EditIcon />
                                                </IconButton>
                                                <Tooltip title="Print Clinic Document" arrow>
                                                  <IconButton
                                                    size="small"
                                                    sx={{
                                                      color: 'primary.main',
                                                      bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                                                      '&:hover': { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.15) }
                                                    }}
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      handlePrintClinicDocument(appt.residentId || String(effectiveResidentId));
                                                    }}
                                                  >
                                                    {printingId === (appt.residentId || String(effectiveResidentId)) ? (
                                                      <CircularProgress size={18} color="inherit" />
                                                    ) : (
                                                      <PrintIcon sx={{ fontSize: 18 }} />
                                                    )}
                                                  </IconButton>
                                                </Tooltip>
                                              </Box>
                                            </TableCell>
                                          )}
                                        </TableRow>
                                      );
                                    })
                                  )}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Box>
                        )}
                      </Paper>
                    )
                  }

                  {/* Documents Tab */}
                  {
                    topTab === 5 && (
                      <Paper sx={{ p: 2 }}>
                        {!selectedResident && !prefilledResident ? (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 2 }}
                          >
                            Please select a resident to view and upload documents.
                          </Typography>
                        ) : (
                          <>
                            {isResidentActive ? (
                              <Box
                                sx={{
                                  mb: 3,
                                  p: 2,
                                  border: "1px dashed #ccc",
                                  borderRadius: 1,
                                }}
                              >
                                {documentsSuccess && (
                                  <Alert
                                    severity="success"
                                    sx={{ mb: 2 }}
                                    onClose={() => setDocumentsSuccess(null)}
                                  >
                                    {documentsSuccess}
                                  </Alert>
                                )}
                                {documentsError && (
                                  <Alert
                                    severity="error"
                                    sx={{ mb: 2 }}
                                    onClose={() => setDocumentsError(null)}
                                  >
                                    {documentsError}
                                  </Alert>
                                )}

                                <Grid container spacing={3} alignItems="flex-start">
                                  <Grid item xs={12} sm={6}>
                                    <TextField
                                      fullWidth
                                      label="Document Name"
                                      value={docsTabName}
                                      onChange={(e) => setDocsTabName(e.target.value)}
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={4}>
                                    <Button
                                      variant="outlined"
                                      component="label"
                                      fullWidth
                                      disabled={isUploading}
                                      sx={{ height: "56px" }}
                                    >
                                      {docsTabFile ? docsTabFile.name : "Select File"}
                                      <input
                                        type="file"
                                        hidden
                                        onChange={handleDocsFileSelect}
                                      />
                                    </Button>
                                  </Grid>
                                  <Grid item xs={12} sm={2}>
                                    <Button
                                      variant="contained"
                                      fullWidth
                                      onClick={handleAddDocsDocument}
                                      disabled={
                                        !docsTabName || !docsTabFile || isUploading
                                      }
                                      sx={{
                                        height: "56px",
                                        bgcolor: "#48CAE4",
                                        color: "#000000",
                                        fontWeight: "normal",
                                        "&:hover": { bgcolor: "#3ab3cc" },
                                        "&.Mui-disabled": {
                                          bgcolor: "rgba(0, 0, 0, 0.12)",
                                          color: "rgba(0, 0, 0, 0.26)"
                                        }
                                      }}
                                    >
                                      {isUploading ? (
                                        <CircularProgress size={24} />
                                      ) : (
                                        "Add"
                                      )}
                                    </Button>
                                  </Grid>
                                </Grid>
                              </Box>
                            ) : (
                              <Alert severity="info" sx={{ mb: 2 }}>
                                Only Active residents can have new documents uploaded.
                              </Alert>
                            )}

                            {isFetchingDocs ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "center",
                                  p: 3,
                                }}
                              >
                                <CircularProgress />
                              </Box>
                            ) : (
                              <Box sx={{ mt: 3 }}>
                                <TableContainer component={Paper} elevation={1} sx={{ overflowX: 'auto' }}>
                                  <Table size="small" sx={{ minWidth: 600 }}>
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>Document Name</TableCell>
                                        <TableCell>Created Date</TableCell>
                                        <TableCell>Created By</TableCell>
                                        <TableCell align="center">
                                          {"Actions"}
                                        </TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {activeDocuments.length === 0 ? (
                                        <TableRow>
                                          <TableCell
                                            colSpan={4}
                                            align="center"
                                            sx={{ py: 4, color: "text.secondary" }}
                                          >
                                            <InsertDriveFileIcon
                                              sx={{
                                                fontSize: 48,
                                                color: "grey.300",
                                                mb: 1,
                                                display: "block",
                                                mx: "auto",
                                              }}
                                            />
                                            No documents found for this resident.
                                          </TableCell>
                                        </TableRow>
                                      ) : (
                                        activeDocuments
                                          .sort((a, b) => {
                                            const dateA = new Date(
                                              a.createdAt || a.uploadedAt || 0,
                                            ).getTime();
                                            const dateB = new Date(
                                              b.createdAt || b.uploadedAt || 0,
                                            ).getTime();
                                            return dateB - dateA;
                                          })
                                          .map((doc) => (
                                            <TableRow key={doc.id}>
                                              <TableCell
                                                sx={{
                                                  whiteSpace: "normal",
                                                  wordBreak: "break-word",
                                                  minWidth: "200px",
                                                }}
                                              >
                                                {doc.fileName}
                                              </TableCell>
                                              <TableCell sx={{ whiteSpace: "nowrap" }}>
                                                {formatToUIDate(doc.createdAt || doc.uploadedAt)}
                                              </TableCell>
                                              <TableCell
                                                sx={{ whiteSpace: "nowrap" }}
                                              >
                                                {(() => {
                                                  if (doc.createdByName)
                                                    return doc.createdByName;
                                                  const userId =
                                                    doc.uploadedBy || doc.createdBy;
                                                  if (!userId) return "-";



                                                  if (
                                                    user &&
                                                    (user.id === userId ||
                                                      user.username === userId)
                                                  ) {
                                                    return user.name || user.username;
                                                  }

                                                  return userId;
                                                })()}
                                              </TableCell>
                                              <TableCell align="center">
                                                <Box
                                                  sx={{
                                                    display: "flex",
                                                    justifyContent: "center",
                                                  }}
                                                >
                                                  <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={() =>
                                                      residentService.openDocument(
                                                        doc.id,
                                                        true,
                                                        doc.fileName,
                                                      )
                                                    }
                                                    title="View"
                                                    sx={{ mr: 1 }}
                                                  >
                                                    <VisibilityIcon fontSize="small" />
                                                  </IconButton>
                                                  {!isOnlyCaregiver && (
                                                    <IconButton
                                                      size="small"
                                                      color="error"
                                                      onClick={() => handleDeleteDocument(doc.id)}
                                                      title="Delete"
                                                    >
                                                      <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                  )}
                                                </Box>
                                              </TableCell>
                                            </TableRow>
                                          ))
                                      )}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              </Box>
                            )}
                          </>
                        )}
                      </Paper>
                    )
                  }

                  {/* Prescribe Pharmacy Tab */}
                  {
                    topTab === 6 && (
                      <Paper sx={{ p: 2 }}>
                        {!selectedResident && !prefilledResident ? (
                          <Alert severity="info" sx={{ mb: 2 }}>
                            Please select a resident from Residents list to view pharmacy
                            orders.
                          </Alert>
                        ) : (
                          <>
                            {orderError && (
                              <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }} onClose={() => setOrderError(null)}>
                                {orderError}
                              </Alert>
                            )}
                            {orderSuccess && (
                              <Alert severity="success" sx={{ mb: 2, borderRadius: '8px' }} onClose={() => setOrderSuccess(null)}>
                                {orderSuccess}
                              </Alert>
                            )}
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                              <Button
                                variant="contained"
                                startIcon={<ShoppingCartIcon />}
                                onClick={handleOpenOrderDialog}
                                disabled={!isResidentActive}
                                sx={{
                                  bgcolor: "#48CAE4",
                                  color: "#000000",
                                  fontWeight: "normal",
                                  "&:hover": { bgcolor: "#3ab3cc" },
                                  "&.Mui-disabled": {
                                    bgcolor: "rgba(0, 0, 0, 0.12)",
                                    color: "rgba(0, 0, 0, 0.26)"
                                  }
                                }}
                              >
                                Refill Medication
                              </Button>
                            </Box>

                            {isPharmacyLoading ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "center",
                                  py: 4,
                                }}
                              >
                                <CircularProgress size={24} />
                              </Box>
                            ) : pharmacyOrders.length > 0 ? (
                              <Box sx={{ width: "100%", overflowX: "auto" }}>
                                <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                                  <Table sx={{ minWidth: 1300 }}>
                                    <TableHead sx={{ backgroundColor: "#f8f9fa" }}>
                                      <TableRow>
                                        <TableCell sx={{ fontWeight: "bold", minWidth: 150 }}>
                                          Prescription Number
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: "bold", minWidth: 200 }}>
                                          Pharmacy Name
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: "bold", minWidth: 200 }}>
                                          Pharmacy Email
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: "bold", minWidth: 200 }}>
                                          Pharmacy Contact Number
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: "bold", minWidth: 150 }}>
                                          Primary Care Provider
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: "bold", minWidth: 150 }}>
                                          Created At
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: "bold", minWidth: 150 }}>
                                          Created By
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: "bold", minWidth: 150 }}>
                                          Updated At
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: "bold", minWidth: 150 }}>
                                          Updated By
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: "bold", minWidth: 150 }}>
                                          Status
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            fontWeight: "bold",
                                            textAlign: "center",
                                            minWidth: 150,
                                          }}
                                        >
                                          {"Actions"}
                                        </TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {pharmacyOrders.map((order) => {
                                        return (
                                          <TableRow key={order.id}>
                                            <TableCell sx={{ wordBreak: "break-word" }}>
                                              {order.prescriptionNumber || "-"}
                                            </TableCell>
                                            <TableCell>
                                              {order.pharmacyName || "-"}
                                            </TableCell>
                                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                                              {order.pharmacyEmail || "-"}
                                            </TableCell>
                                            <TableCell sx={{ color: "#4B5563" }}>
                                              {order.pharmacyContactNumber || "-"}
                                            </TableCell>
                                            <TableCell sx={{ wordBreak: "break-word" }}>
                                              {order.prescribingPhysicianName || "N/A"}
                                            </TableCell>
                                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                                              {formatToUIDateTimeLong(order.createdAt)}
                                            </TableCell>
                                            <TableCell>
                                              {order.createdByName || "-"}
                                            </TableCell>
                                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                                              {formatToUIDateTimeLong(order.updatedAt)}
                                            </TableCell>
                                            <TableCell>
                                              {order.updatedByName || "-"}
                                            </TableCell>
                                            <TableCell>
                                              <Chip
                                                label={(order.status || "PENDING").replace(/_/g, ' ')}
                                                size="small"
                                                variant="outlined"
                                                color={
                                                  order.status === "ACTIVE"
                                                    ? "success"
                                                    : order.status === "PENDING"
                                                      ? "warning"
                                                      : order.status === "CANCELLED"
                                                        ? "error"
                                                        : "primary"
                                                }
                                              />
                                            </TableCell>
                                            <TableCell sx={{ textAlign: "center" }}>
                                              <Box
                                                sx={{
                                                  display: "flex",
                                                  justifyContent: "center",
                                                  flexWrap: "nowrap",
                                                }}
                                              >
                                                <Tooltip title="View Details">
                                                  <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={() =>
                                                      setViewingOrderId(order.id || null)
                                                    }
                                                  >
                                                    <InfoIcon fontSize="small" />
                                                  </IconButton>
                                                </Tooltip>

                                              </Box>
                                            </TableCell>
                                          </TableRow>
                                        );
                                      })}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              </Box>
                            ) : (
                              <Box sx={{ textAlign: "center", py: 4 }}>
                                <Typography variant="body2" color="textSecondary">
                                  No pharmacy orders found for this resident
                                </Typography>
                              </Box>
                            )}
                          </>
                        )}
                      </Paper>
                    )
                  }

                </Paper >
              </Grid >

              {/* Right Sidebar - Activity Timeline */}
              < Grid item xs={12} md={isTimelineExpanded ? 3.5 : 0.5} sx={{ transition: 'all 0.3s ease' }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: isTimelineExpanded ? 2 : 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    height: (isMobile || isTablet) ? 'auto' : (isTimelineExpanded ? 'calc(100vh - 150px)' : 'auto'),
                    maxHeight: (isMobile || isTablet) ? '500px' : 'none',
                    display: 'flex',
                    flexDirection: 'column',
                    bgcolor: 'background.default',
                    borderRadius: 2,
                    position: (isMobile || isTablet) ? 'static' : 'sticky',
                    top: 80,
                    mt: (isMobile || isTablet) ? 3 : 0,
                    overflow: 'hidden'
                  }}
                >
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', mb: 0.5, minHeight: '38px', justifyContent: isTimelineExpanded ? 'space-between' : 'center', flexDirection: isTimelineExpanded ? 'row' : 'column', cursor: 'pointer' }}
                    onClick={() => setIsTimelineExpanded(!isTimelineExpanded)}
                  >
                    {isTimelineExpanded ? (
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.primary' }}>
                        <TimelineIcon fontSize="small" color="primary" /> Recent Updates
                      </Typography>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Tooltip title="Recent Updates" placement="left">
                          <TimelineIcon fontSize="small" color="primary" />
                        </Tooltip>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 800,
                            writingMode: 'vertical-rl',
                            transform: 'rotate(180deg)',
                            color: 'text.primary',
                            fontSize: '0.7rem',
                            letterSpacing: '0.1em',
                            mt: 1,
                            // textTransform: 'uppercase',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          Recent Updates
                        </Typography>
                      </Box>
                    )}
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); setIsTimelineExpanded(!isTimelineExpanded); }}>
                      {isTimelineExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon sx={{ transform: 'rotate(90deg)' }} />}
                    </IconButton>
                  </Box>

                  <Collapse in={isTimelineExpanded} timeout="auto" unmountOnExit sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0, '& .MuiCollapse-wrapper': { flexGrow: 1, display: 'flex', flexDirection: 'column' }, '& .MuiCollapse-wrapperInner': { flexGrow: 1, display: 'flex', flexDirection: 'column' } }}>
                    <Divider sx={{ mb: 2 }} />
                    <Box
                      sx={{
                        flexGrow: 1,
                        maxHeight: (isMobile || isTablet) ? '400px' : 'calc(100vh - 220px)',
                        minHeight: 0,
                        overflowY: 'auto',
                        pr: 1,
                        scrollbarWidth: 'thin',
                        scrollbarColor: 'rgba(0,0,0,0.2) transparent',
                        '&::-webkit-scrollbar': {
                          width: '6px',
                          display: 'block'
                        },
                        '&::-webkit-scrollbar-track': {
                          background: 'rgba(0,0,0,0.02)',
                          borderRadius: '10px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          background: 'rgba(0,0,0,0.2)',
                          borderRadius: '10px',
                          '&:hover': {
                            background: 'rgba(0,0,0,0.3)',
                          }
                        }
                      }}
                    >
                      {renderActivityTimeline()}
                    </Box>
                  </Collapse>
                </Paper>
              </Grid >
            </Grid >

            <Dialog
              open={!!viewingOrderId}
              onClose={() => setViewingOrderId(null)}
              maxWidth="md"
              fullWidth
            >
              <DialogTitle sx={{ fontWeight: "bold" }}>
                Medication Order Details
              </DialogTitle>
              <DialogContent dividers>
                {isFetchingOrderDetails ? (
                  <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  viewingOrder && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      <Paper sx={{ p: 2, backgroundColor: "#f8f9fa" }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={4}>
                            <Typography
                              variant="subtitle2"
                              color="black"
                              component="div"
                            >
                              <Box sx={{ fontWeight: "bold", display: "inline" }}>
                                Resident Name
                              </Box>
                            </Typography>
                            <Typography variant="body1">
                              {viewingOrder.residentName ||
                                (residentBanner ? residentBanner.name : "")}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <Typography
                              variant="subtitle2"
                              color="black"
                              component="div"
                            >
                              <Box sx={{ fontWeight: "bold", display: "inline" }}>
                                Primary Care Provider
                              </Box>
                            </Typography>
                            <Typography variant="body1">
                              {viewingOrder.prescribingPhysicianName}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <Typography
                              variant="subtitle2"
                              color="black"
                              component="div"
                            >
                              <Box sx={{ fontWeight: "bold", display: "inline" }}>
                                Prescription Number
                              </Box>
                            </Typography>
                            <Typography variant="body1">
                              {viewingOrder.prescriptionNumber || "-"}
                            </Typography>
                          </Grid>

                          <Grid item xs={12}>
                            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                              Medications
                            </Typography>
                            <TableContainer component={Paper} variant="outlined">
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Dosage</TableCell>
                                    <TableCell>Route</TableCell>
                                    <TableCell>Frequency</TableCell>
                                    <TableCell>Start Date</TableCell>
                                    <TableCell>Refill Date</TableCell>
                                    <TableCell>Expiration Date</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {viewingOrder.medicationItems?.map(
                                    (item: any, idx: number) => (
                                      <TableRow key={idx}>
                                        <TableCell
                                          sx={{
                                            fontWeight: "bold",
                                            wordBreak: "break-word",
                                          }}
                                        >
                                          {item.medicationName}
                                        </TableCell>
                                        <TableCell sx={{ wordBreak: "break-word" }}>
                                          {item.dosage}
                                        </TableCell>
                                        <TableCell>{item.route}</TableCell>
                                        <TableCell>{item.frequency}</TableCell>
                                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                                          {formatToUIDate(item.startDate)}
                                        </TableCell>
                                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                                          {formatToUIDate(item.endDate)}
                                        </TableCell>
                                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                                          {formatToUIDate(item.medicationExpirationDate)}
                                        </TableCell>
                                      </TableRow>
                                    ),
                                  )}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Grid>
                        </Grid>
                      </Paper>
                    </Box>
                  )
                )}
              </DialogContent>
              <DialogActions sx={{ p: 2 }}>
                <Button onClick={() => setViewingOrderId(null)}>Close</Button>
              </DialogActions>
            </Dialog >

            <Dialog
              open={isInsuranceDialogOpen}
              onClose={() => setIsInsuranceDialogOpen(false)}
              maxWidth="md"
              fullWidth
            >
              <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 3, pt: 3 }}>
                <CreditCardIcon color="primary" />
                <Typography variant="h6" fontWeight="bold">
                  {editingInsurance ? "Edit Insurance Details" : "Add New Insurance"}
                </Typography>
              </DialogTitle>
              <DialogContent sx={{ px: 3, pb: 1 }}>
                <Box sx={{ mt: 1 }}>
                  <Grid container spacing={2}>
                    {/* Insurance Provider Details */}
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="primary" fontWeight="bold" sx={{ mb: 1 }}>
                        INSURANCE PROVIDER DETAILS
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label={<span>Insurance Company Name <span style={{ color: '#d32f2f' }}>*</span></span>}
                        fullWidth
                        value={insuranceForm.insuranceCompanyName}
                        onChange={(e) => setInsuranceForm({ ...insuranceForm, insuranceCompanyName: e.target.value.replace(/[^A-Za-z\s]/g, "") })}
                        placeholder="e.g. Blue Cross Blue Shield"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Insurance Type</InputLabel>
                        <Select
                          label="Insurance Type"
                          value={insuranceForm.insuranceType}
                          onChange={(e) => setInsuranceForm({ ...insuranceForm, insuranceType: e.target.value })}
                        >
                          {/* <MenuItem value="PRIMARY">Primary</MenuItem>
                          <MenuItem value="SECONDARY">Secondary</MenuItem>
                          <MenuItem value="TERTIARY">Tertiary</MenuItem> */}
                          {insuranceTypeOptions.map((opt) => (
                            <MenuItem key={opt.valueName} value={opt.valueName}>{opt.valueName}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Payer ID"
                        fullWidth
                        value={insuranceForm.payerId}
                        onChange={(e) => setInsuranceForm({ ...insuranceForm, payerId: e.target.value.replace(/\D/g, "") })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Insurance Phone"
                        fullWidth
                        value={insuranceForm.insurancePhone}
                        inputProps={{ maxLength: 10 }}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, "");
                          if (val.length <= 10) setInsuranceForm({ ...insuranceForm, insurancePhone: val });
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Insurance Email"
                        fullWidth
                        error={!!insuranceErrors.insuranceEmail}
                        helperText={insuranceErrors.insuranceEmail || ""}
                        value={insuranceForm.insuranceEmail}
                        onChange={(e) => {
                          const value = e.target.value;
                          setInsuranceForm({ ...insuranceForm, insuranceEmail: value });
                          // Validate email format
                          if (value === "") {
                            setInsuranceErrors({ ...insuranceErrors, insuranceEmail: "" });
                          } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                            setInsuranceErrors({ ...insuranceErrors, insuranceEmail: "Invalid email format" });
                          } else {
                            setInsuranceErrors({ ...insuranceErrors, insuranceEmail: "" });
                          }
                        }}
                      />
                    </Grid>

                    {/* Subscriber Details */}
                    <Grid item xs={12} sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" color="primary" fontWeight="bold" sx={{ mb: 1 }}>
                        SUBSCRIBER DETAILS
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label={<span>Subscriber Name <span style={{ color: '#d32f2f' }}>*</span></span>}
                        fullWidth
                        value={insuranceForm.subscriberName}
                        onChange={(e) => setInsuranceForm({ ...insuranceForm, subscriberName: e.target.value.replace(/[^A-Za-z\s]/g, "") })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Subscriber Relationship</InputLabel>
                        <Select
                          label="Subscriber Relationship"
                          value={insuranceForm.subscriberRelationship}
                          onChange={(e) => {
                            const value = e.target.value;
                            let updatedForm = { ...insuranceForm, subscriberRelationship: value };
                            if (value.toLowerCase() === "self" && fullResidentData) {
                              updatedForm = {
                                ...updatedForm,
                                subscriberName: `${fullResidentData.firstName || ""} ${fullResidentData.lastName || ""}`.trim(),
                                subscriberDob: fullResidentData.dateOfBirth || "",
                                subscriberGender: fullResidentData.gender || "",
                                subscriberAddress: fullResidentData.address || "",
                                subscriberPhone: fullResidentData.phone || "",
                              };
                            } else {
                              updatedForm = {
                                ...updatedForm,
                                subscriberName: "",
                                subscriberDob: "",
                                subscriberGender: "",
                                subscriberPhone: "",
                                subscriberAddress: "",
                                subscriberEmployerName: "",
                                subscriberEmployerAddress: "",
                              };
                            }
                            setInsuranceForm(updatedForm);
                          }}
                        >
                          {subscriberRelationshipOptions.map((opt) => (
                            <MenuItem key={opt.valueName} value={opt.valueName}>{opt.valueName}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          label="Subscriber DOB"
                          format="dd/MM/yyyy"
                          disableFuture
                          value={insuranceForm.subscriberDob ? parseISO(insuranceForm.subscriberDob) : null}
                          onChange={(date) => setInsuranceForm({ ...insuranceForm, subscriberDob: date ? format(date, "yyyy-MM-dd") : "" })}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              size: "medium",
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth>
                        <InputLabel>Subscriber Gender</InputLabel>
                        <Select
                          label="Subscriber Gender"
                          value={insuranceForm.subscriberGender}
                          onChange={(e) => setInsuranceForm({ ...insuranceForm, subscriberGender: e.target.value })}
                        >
                          {genderOptions.map((g) => (
                            <MenuItem key={g.valueName} value={g.valueName}>{g.valueName}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Subscriber Phone"
                        fullWidth
                        value={insuranceForm.subscriberPhone}
                        inputProps={{ maxLength: 10 }}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, "");
                          if (val.length <= 10) setInsuranceForm({ ...insuranceForm, subscriberPhone: val });
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Subscriber Address"
                        fullWidth
                        multiline
                        rows={2}
                        value={insuranceForm.subscriberAddress}
                        onChange={(e) => setInsuranceForm({ ...insuranceForm, subscriberAddress: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Subscriber Employer Name"
                        fullWidth
                        value={insuranceForm.subscriberEmployerName}
                        onChange={(e) => setInsuranceForm({ ...insuranceForm, subscriberEmployerName: e.target.value.replace(/[^A-Za-z\s]/g, "") })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Subscriber Employer Address"
                        fullWidth
                        value={insuranceForm.subscriberEmployerAddress}
                        onChange={(e) => setInsuranceForm({ ...insuranceForm, subscriberEmployerAddress: e.target.value })}
                      />
                    </Grid>

                    {/* Policy Details */}
                    <Grid item xs={12} sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" color="primary" fontWeight="bold" sx={{ mb: 1 }}>
                        POLICY DETAILS
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Policy / Member ID"
                        fullWidth
                        required
                        InputLabelProps={{ sx: { '& .MuiFormLabel-asterisk': { color: '#ef4444' } } }}
                        value={insuranceForm.policyMemberId}
                        onChange={(e) => setInsuranceForm({ ...insuranceForm, policyMemberId: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Group Number"
                        fullWidth
                        value={insuranceForm.groupNumber}
                        onChange={(e) => setInsuranceForm({ ...insuranceForm, groupNumber: e.target.value.replace(/\D/g, "") })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Plan Name"
                        fullWidth
                        value={insuranceForm.planName}
                        onChange={(e) => setInsuranceForm({ ...insuranceForm, planName: e.target.value.replace(/[^A-Za-z\s]/g, "") })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth>
                        <InputLabel>Plan Type</InputLabel>
                        <Select
                          label="Plan Type"
                          value={insuranceForm.planType}
                          onChange={(e) => setInsuranceForm({ ...insuranceForm, planType: e.target.value })}
                        >
                          {planTypeOptions.map((opt) => (
                            <MenuItem key={opt.valueName} value={opt.valueName}>{opt.valueName}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth>
                        <InputLabel>Coverage Type</InputLabel>
                        <Select
                          label="Coverage Type"
                          value={insuranceForm.coverageType || ""}
                          onChange={(e) => setInsuranceForm({ ...insuranceForm, coverageType: e.target.value })}
                        >
                          {coverageTypeOptions.map((opt) => (
                            <MenuItem key={opt.valueName} value={opt.valueName}>{opt.valueName}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Authorization Number"
                        fullWidth
                        value={insuranceForm.authorizationNumber}
                        onChange={(e) => setInsuranceForm({ ...insuranceForm, authorizationNumber: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          label="Effective Date"
                          format="dd/MM/yyyy"
                          disablePast
                          value={insuranceForm.effectiveDate ? parseISO(insuranceForm.effectiveDate) : null}
                          onChange={(date) => setInsuranceForm({ ...insuranceForm, effectiveDate: date ? format(date, "yyyy-MM-dd") : "" })}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              size: "medium",
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          label="Termination Date"
                          format="dd/MM/yyyy"
                          disablePast
                          minDate={insuranceForm.effectiveDate ? parseISO(insuranceForm.effectiveDate) : undefined}
                          value={insuranceForm.terminationDate ? parseISO(insuranceForm.terminationDate) : null}
                          onChange={(date) => setInsuranceForm({ ...insuranceForm, terminationDate: date ? format(date, "yyyy-MM-dd") : "" })}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              size: "medium",
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </Grid>

                    {/* Coverage Limits */}
                    <Grid item xs={12} sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" color="primary" fontWeight="bold" sx={{ mb: 1 }}>
                        COVERAGE LIMITS
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Copay Amount"
                        fullWidth
                        error={!!insuranceErrors.copayAmount}
                        helperText={insuranceErrors.copayAmount || ""}
                        value={insuranceForm.copayAmount}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === "" || /^\d*\.?\d{0,2}$/.test(val)) {
                            setInsuranceForm({ ...insuranceForm, copayAmount: val });
                            if (val && !/^\d+(\.\d{1,2})?$/.test(val)) {
                              setInsuranceErrors((prev) => ({ ...prev, copayAmount: "Invalid format (e.g. 25.50)" }));
                            } else {
                              setInsuranceErrors((prev) => ({ ...prev, copayAmount: "" }));
                            }
                          }
                        }}
                        InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Coinsurance %"
                        fullWidth
                        error={!!insuranceErrors.coinsurancePercentage}
                        helperText={insuranceErrors.coinsurancePercentage || ""}
                        value={insuranceForm.coinsurancePercentage}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === "" || /^\d*\.?\d{0,2}$/.test(val)) {
                            setInsuranceForm({ ...insuranceForm, coinsurancePercentage: val });
                            if (val && !/^\d+(\.\d{1,2})?$/.test(val)) {
                              setInsuranceErrors((prev) => ({ ...prev, coinsurancePercentage: "Invalid format (e.g. 25.50)" }));
                            } else {
                              setInsuranceErrors((prev) => ({ ...prev, coinsurancePercentage: "" }));
                            }
                          }
                        }}
                        InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Annual Deductible"
                        fullWidth
                        error={!!insuranceErrors.annualDeductible}
                        helperText={insuranceErrors.annualDeductible || ""}
                        value={insuranceForm.annualDeductible}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === "" || /^\d*\.?\d{0,2}$/.test(val)) {
                            setInsuranceForm({ ...insuranceForm, annualDeductible: val });
                            if (val && !/^\d+(\.\d{1,2})?$/.test(val)) {
                              setInsuranceErrors((prev) => ({ ...prev, annualDeductible: "Invalid format (e.g. 25.50)" }));
                            } else {
                              setInsuranceErrors((prev) => ({ ...prev, annualDeductible: "" }));
                            }
                          }
                        }}
                        InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Remaining Deductible"
                        fullWidth
                        error={!!insuranceErrors.remainingDeductible}
                        helperText={insuranceErrors.remainingDeductible || ""}
                        value={insuranceForm.remainingDeductible}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === "" || /^\d*\.?\d{0,2}$/.test(val)) {
                            setInsuranceForm({ ...insuranceForm, remainingDeductible: val });
                            if (val && !/^\d+(\.\d{1,2})?$/.test(val)) {
                              setInsuranceErrors((prev) => ({ ...prev, remainingDeductible: "Invalid format (e.g. 25.50)" }));
                            } else {
                              setInsuranceErrors((prev) => ({ ...prev, remainingDeductible: "" }));
                            }
                          }
                        }}
                        InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Out of Pocket Limit"
                        fullWidth
                        error={!!insuranceErrors.outOfPocketLimit}
                        helperText={insuranceErrors.outOfPocketLimit || ""}
                        value={insuranceForm.outOfPocketLimit}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === "" || /^\d*\.?\d{0,2}$/.test(val)) {
                            setInsuranceForm({ ...insuranceForm, outOfPocketLimit: val });
                            if (val && !/^\d+(\.\d{1,2})?$/.test(val)) {
                              setInsuranceErrors((prev) => ({ ...prev, outOfPocketLimit: "Invalid format (e.g. 25.50)" }));
                            } else {
                              setInsuranceErrors((prev) => ({ ...prev, outOfPocketLimit: "" }));
                            }
                          }
                        }}
                        InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
                <Button onClick={() => setIsInsuranceDialogOpen(false)} color="inherit">
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSaveInsurance}
                  disabled={!insuranceForm.insuranceCompanyName || !insuranceForm.policyMemberId || !insuranceForm.subscriberName}
                  sx={{
                    bgcolor: "#48CAE4",
                    color: "#000000",
                    textTransform: 'none',
                    fontWeight: 600,
                    "&:hover": { bgcolor: "#3ab3cc" }
                  }}
                >
                  {editingInsurance ? "Update Insurance" : "Save Insurance Card"}
                </Button>
              </DialogActions>
            </Dialog>

            <Dialog
              open={openDiscontinueDialog}
              onClose={() => setOpenDiscontinueDialog(false)}
            >
              <DialogTitle sx={{ m: 0, p: 2 }}>
                {discontinueDialogMode === "DISCONTINUE"
                  ? "Discontinue Medication"
                  : "Undo Discontinue Medication"}
                <IconButton
                  aria-label="close"
                  onClick={() => setOpenDiscontinueDialog(false)}
                  sx={{
                    position: "absolute",
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent>
                <DialogContentText>
                  {discontinueDialogMode === "DISCONTINUE"
                    ? "Are you sure you want to discontinue this medication?"
                    : "Are you sure you want to undo the discontinuation of this medication?"}
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => setOpenDiscontinueDialog(false)}
                  color="primary"
                >
                  No
                </Button>
                <Button
                  onClick={() => {
                    submitUpdateMedication();
                  }}
                  color="primary"
                  autoFocus
                >
                  Yes
                </Button>
              </DialogActions>
            </Dialog>
            <Dialog
              open={nurseConfirmationOpen}
              onClose={() => setNurseConfirmationOpen(false)}
            >
              <DialogTitle>Dose Limit Confirmation</DialogTitle>
              <DialogContent>
                <DialogContentText>
                  The prescribed frequency for <strong>{nurseForm.medication}</strong>{" "}
                  is <strong>{nurseForm.schedule}</strong>. Administration has already
                  occurred{" "}
                  {
                    nurseEntries.filter(
                      (e) =>
                        (e.noteDate
                          ? format(new Date(e.noteDate), "yyyy-MM-dd")
                          : "") ===
                        (nurseForm.noteDate
                          ? format(nurseForm.noteDate, "yyyy-MM-dd")
                          : "") &&
                        (e.medication || "").toLowerCase() ===
                        nurseForm.medication.toLowerCase(),
                    ).length
                  }{" "}
                  time today.
                  <br />
                  <br />
                  Additional dosing may violate the prescribed limit.
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => setNurseConfirmationOpen(false)}
                  color="primary"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => executeAddNurseEntry()}
                  color="primary"
                  autoFocus
                >
                  Yes, continue
                </Button>
              </DialogActions>
            </Dialog>

            {/* Create Medication Order Dialog */}
            <Dialog
              open={openOrderDialog}
              onClose={handleCloseOrderDialog}
              maxWidth="lg"
              fullWidth
            >
              <DialogTitle sx={{ fontWeight: "bold", display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                Create New Medication Order
                <IconButton onClick={handleCloseOrderDialog} size="small">
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent dividers>
                <form onSubmit={handleOrderSubmit(onSubmitOrder)}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        label={<span>Selected Resident <span style={{ color: "#d32f2f" }}>*</span></span>}
                        fullWidth
                        value={
                          residentBanner?.name ||
                            fullResidentData
                            ? `${fullResidentData?.firstName || ""} ${fullResidentData?.lastName || ""}`.trim()
                            : ""
                        }
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="prescribingPhysician"
                        control={orderControl}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label={<span>Primary Care Provider <span style={{ color: "#d32f2f" }}>*</span></span>}
                            fullWidth
                            error={!!orderErrors.prescribingPhysician}
                            helperText={orderErrors.prescribingPhysician?.message}
                            InputLabelProps={{ shrink: !!field.value }}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="prescriptionNumber"
                        control={orderControl}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Prescription Number"
                            fullWidth
                            error={!!orderErrors.prescriptionNumber}
                            helperText={orderErrors.prescriptionNumber?.message}
                            inputProps={{ maxLength: 10 }}
                            InputLabelProps={{ shrink: !!field.value }}
                            onChange={(e) => {
                              const value = e.target.value.replace(/\D/g, "");
                              if (value.length <= 10) field.onChange(value);
                            }}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <Controller
                        name="pharmacyName"
                        control={orderControl}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Pharmacy Name"
                            fullWidth
                            error={!!orderErrors.pharmacyName}
                            helperText={orderErrors.pharmacyName?.message}
                            InputLabelProps={{ shrink: !!field.value }}
                            onChange={(e) => {
                              const value = e.target.value.replace(/[^A-Za-z\s]/g, "");
                              field.onChange(value);
                            }}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <Controller
                        name="pharmacyEmail"
                        control={orderControl}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label={<span>Pharmacy Email <span style={{ color: "#d32f2f" }}>*</span></span>}
                            fullWidth
                            error={!!orderErrors.pharmacyEmail}
                            helperText={orderErrors.pharmacyEmail?.message}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Controller
                        name="pharmacyContactNumber"
                        control={orderControl}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label={<span>Pharmacy Contact Number <span style={{ color: "#d32f2f" }}>*</span></span>}
                            fullWidth
                            error={!!orderErrors.pharmacyContactNumber}
                            helperText={orderErrors.pharmacyContactNumber?.message}
                            InputLabelProps={{ shrink: !!field.value }}
                            onChange={(e) => {
                              const value = e.target.value.replace(/\D/g, "");
                              if (value.length <= 10) {
                                field.onChange(value);
                              }
                            }}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" sx={{ mb: 0.5, fontWeight: "bold", color: "rgba(0, 0, 0, 0.6)" }}>
                        Upload Prescription
                      </Typography>
                      <Box sx={{ position: "relative", width: "100%" }}>
                        <Button
                          variant="outlined"
                          component="label"
                          fullWidth
                          startIcon={<CloudUploadIcon />}
                          sx={{
                            justifyContent: "start",
                            textTransform: "none",
                            height: "56px",
                            borderColor: "rgba(0, 0, 0, 0.23)",
                            color: watchOrder("prescriptionFile") ? "text.primary" : "text.secondary",
                            "&:hover": { borderColor: "text.primary" },
                            pr: watchOrder("prescriptionFile") ? 6 : 2,
                          }}
                        >
                          <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "100%" }}>
                            {watchOrder("prescriptionFile")?.name || "Choose File"}
                          </Box>
                          <input
                            type="file"
                            hidden
                            accept="image/*,.pdf"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) setOrderValue("prescriptionFile", file, { shouldValidate: true });
                              e.target.value = "";
                            }}
                          />
                        </Button>
                        {watchOrder("prescriptionFile") && (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setOrderValue("prescriptionFile", null, { shouldValidate: true });
                            }}
                            sx={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", zIndex: 2, color: "error.main" }}
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                      {orderErrors.prescriptionFile && (
                        <FormHelperText error sx={{ mx: 0, mt: 1 }}>
                          {orderErrors.prescriptionFile.message}
                        </FormHelperText>
                      )}
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" sx={{ mb: 0.5, fontWeight: "bold", color: "rgba(0, 0, 0, 0.6)" }}>
                        Next Order / Refill Date
                      </Typography>
                      <Controller
                        name="nextRefillDate"
                        control={orderControl}
                        render={({ field }) => (
                          <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                              {...field}
                              format="dd/MM/yyyy"
                              disablePast
                              minDate={(newMedItem.startDate && !isNaN(new Date(newMedItem.startDate).getTime())) ? new Date(newMedItem.startDate) : undefined}
                              onChange={(date) => {
                                field.onChange(date);
                                if (date) {
                                  setNewMedItem((prev) => ({ ...prev, endDate: date }));
                                }
                              }}
                              sx={{ width: "100%" }}
                              slotProps={{
                                textField: {
                                  size: "medium",
                                  fullWidth: true,
                                  error: !!orderErrors.nextRefillDate,
                                  helperText: orderErrors.nextRefillDate?.message,
                                },
                              }}
                            />
                          </LocalizationProvider>
                        )}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }}>Medications</Divider>
                    </Grid>

                    <Grid item xs={12}>
                      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, overflowX: "auto", border: "1px solid #e0e0e0" }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow sx={{ "& th": { backgroundColor: "#fdfdfd", fontWeight: "bold", color: "#546E7A", fontSize: "0.7rem", textTransform: "uppercase", whiteSpace: "nowrap", letterSpacing: "0.05em" } }}>
                              <TableCell sx={{ minWidth: 180 }}>Medication Name <span style={{ color: "#d32f2f" }}>*</span></TableCell>
                              <TableCell sx={{ minWidth: 150 }}>Dosage <span style={{ color: "#d32f2f" }}>*</span></TableCell>
                              <TableCell sx={{ minWidth: 200 }}>Frequency <span style={{ color: "#d32f2f" }}>*</span></TableCell>
                              <TableCell sx={{ minWidth: 200 }}>Route <span style={{ color: "#d32f2f" }}>*</span></TableCell>
                              <TableCell sx={{ minWidth: 180 }}>Start Date <span style={{ color: "#d32f2f" }}>*</span></TableCell>
                              <TableCell sx={{ minWidth: 180 }}>Refill Date</TableCell>
                              <TableCell sx={{ minWidth: 200 }}>Medication Expiration Date</TableCell>
                              <TableCell align="center" sx={{ minWidth: 100 }}>Action</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {orderFields.map((field, index) => (
                              <TableRow key={field.id}>
                                <TableCell sx={{ fontWeight: 600 }}>{watchOrder(`medicationItems.${index}.medicationName`)}</TableCell>
                                <TableCell sx={{ color: "#607D8B" }}>{watchOrder(`medicationItems.${index}.dosage`)}</TableCell>
                                <TableCell sx={{ color: "#607D8B" }}>{watchOrder(`medicationItems.${index}.frequency`) || "-"}</TableCell>
                                <TableCell sx={{ color: "#607D8B" }}>{watchOrder(`medicationItems.${index}.route`) || ""}</TableCell>
                                <TableCell sx={{ color: "#607D8B" }}>{formatToUIDate(watchOrder(`medicationItems.${index}.startDate`))}</TableCell>
                                <TableCell sx={{ color: "#607D8B" }}>{formatToUIDate(watchOrder(`medicationItems.${index}.endDate`))}</TableCell>
                                <TableCell sx={{ color: "#607D8B" }}>{formatToUIDate(watchOrder(`medicationItems.${index}.medicationExpirationDate`))}</TableCell>
                                <TableCell align="center">
                                  <IconButton size="small" color="error" onClick={() => removeOrder(index)}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))}
                            <TableRow sx={{ backgroundColor: "#fafafa" }}>
                              <TableCell sx={{ py: 1.5 }}>
                                <Autocomplete
                                  size="small"
                                  options={availableMedicationOptions}
                                  value={newMedItem.medicationName}
                                  onChange={(_, value) => {
                                    const selectedVal = value as string;
                                    setNewMedItem((prev) => ({ ...prev, medicationName: selectedVal || "" }));
                                    if (selectedVal) {
                                      const masterMed = residentMasterMedications.find((m) => m.medicationName === selectedVal);
                                      if (masterMed) {
                                        setNewMedItem((prev) => ({
                                          ...prev,
                                          dosage: masterMed.dosage || "",
                                          frequency: masterMed.schedule || masterMed.frequency || "",
                                          route: normalizeMedicationRoute(masterMed.route) as MedicationRoute,
                                        }));
                                      }
                                    }
                                    if (newMedItemErrors.medicationName) {
                                      setNewMedItemErrors(prev => ({ ...prev, medicationName: undefined }));
                                    }
                                  }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      placeholder="Medication *"
                                      variant="outlined"
                                      error={!!newMedItemErrors.medicationName}
                                      helperText={newMedItemErrors.medicationName}
                                    />
                                  )}
                                />

                              </TableCell>
                              <TableCell>
                                <TextField
                                  size="small"
                                  placeholder="Dose *"
                                  value={newMedItem.dosage}
                                  error={!!newMedItemErrors.dosage}
                                  helperText={newMedItemErrors.dosage}
                                  onChange={(e) => {
                                    setNewMedItem((prev) => ({ ...prev, dosage: e.target.value }));
                                    if (newMedItemErrors.dosage) {
                                      setNewMedItemErrors(prev => ({ ...prev, dosage: undefined }));
                                    }
                                  }}
                                />

                              </TableCell>
                              <TableCell>
                                <Autocomplete
                                  freeSolo
                                  size="small"
                                  options={scheduleOptions.length > 0 ? scheduleOptions.map(opt => opt.valueName) : standardFrequencies}
                                  value={newMedItem.frequency}
                                  onInputChange={(_, newValue) => {
                                    setNewMedItem((prev) => ({ ...prev, frequency: newValue }));
                                    if (newMedItemErrors.frequency) {
                                      setNewMedItemErrors(prev => ({ ...prev, frequency: undefined }));
                                    }
                                  }}
                                  popupIcon={<ArrowDropDownIcon />}
                                  forcePopupIcon={true}
                                  slotProps={{
                                    popper: {
                                      placement: "bottom-start",
                                      modifiers: [{ name: "flip", enabled: false }],
                                      sx: {
                                        "& .MuiAutocomplete-listbox": {
                                          maxHeight: "150px",
                                          backgroundColor: "white",
                                          padding: 0,
                                          "& .MuiAutocomplete-option": {
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                          },
                                        },
                                      },
                                    }
                                  }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      placeholder="Frequency *"
                                      error={!!newMedItemErrors.frequency}
                                      helperText={newMedItemErrors.frequency}
                                      sx={{
                                        "& .MuiOutlinedInput-root": {
                                          borderRadius: "6px",
                                          backgroundColor: "white",
                                          "& .MuiOutlinedInput-notchedOutline": {
                                            borderColor: newMedItemErrors.frequency ? "#d32f2f" : "#0066cc",
                                          },
                                          "&:hover .MuiOutlinedInput-notchedOutline": {
                                            borderColor: newMedItemErrors.frequency ? "#d32f2f" : "#0066cc",
                                          },
                                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                            borderColor: newMedItemErrors.frequency ? "#d32f2f" : "#0066cc",
                                          },
                                        },
                                      }}
                                    />
                                  )}
                                />

                              </TableCell>
                              <TableCell>
                                <FormControl fullWidth size="small" error={!!newMedItemErrors.route}>
                                  {(() => {
                                    const routeAutocompleteOptions = routeOptions.length > 0
                                      ? routeOptions.map(opt => ({ label: opt.valueName, value: normalizeMedicationRoute(opt.valueName) || opt.valueName }))
                                      : Object.values(MedicationRoute).map(r => ({ label: r, value: r }));

                                    return (
                                      <Autocomplete
                                        size="small"
                                        fullWidth
                                        disableClearable
                                        options={routeAutocompleteOptions}
                                        getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
                                        isOptionEqualToValue={(option, val) => {
                                          const valStr = typeof val === 'string' ? val : val?.value;
                                          return option.value === valStr;
                                        }}
                                        value={routeAutocompleteOptions.find(o => o.value === newMedItem.route) || undefined}
                                        onChange={(event, newValue) => {
                                          const selectedValue = typeof newValue === 'string' ? newValue : newValue?.value;
                                          if (selectedValue) {
                                            setNewMedItem((prev) => ({ ...prev, route: selectedValue as MedicationRoute }));
                                            if (newMedItemErrors.route) {
                                              setNewMedItemErrors(prev => ({ ...prev, route: undefined }));
                                            }
                                          }
                                        }}
                                        popupIcon={<ArrowDropDownIcon />}
                                        forcePopupIcon={true}
                                        slotProps={{
                                          popper: {
                                            placement: "bottom-start",
                                            modifiers: [{ name: "flip", enabled: false }],
                                            sx: {
                                              "& .MuiAutocomplete-listbox": {
                                                maxHeight: "150px",
                                                backgroundColor: "white",
                                                padding: 0,
                                                "& .MuiAutocomplete-option": {
                                                  whiteSpace: "nowrap",
                                                  overflow: "hidden",
                                                  textOverflow: "ellipsis",
                                                },
                                              },
                                            },
                                          }
                                        }}
                                        renderInput={(params) => (
                                          <TextField
                                            {...params}
                                            placeholder="Select Route *"
                                            error={!!newMedItemErrors.route}
                                            sx={{
                                              "& .MuiOutlinedInput-root": {
                                                borderRadius: "6px",
                                                backgroundColor: "white",
                                                "& .MuiOutlinedInput-notchedOutline": {
                                                  borderColor: newMedItemErrors.route ? "#d32f2f" : "rgba(0, 0, 0, 0.23)",
                                                },
                                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                                  borderColor: newMedItemErrors.route ? "#d32f2f" : "rgba(0, 0, 0, 0.87)",
                                                },
                                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                                  borderColor: newMedItemErrors.route ? "#d32f2f" : "#0066cc",
                                                },
                                                pr: "35px !important",
                                              },
                                            }}
                                          />
                                        )}
                                      />
                                    );
                                  })()}
                                  {newMedItemErrors.route && (
                                    <FormHelperText>{newMedItemErrors.route}</FormHelperText>
                                  )}
                                </FormControl>

                              </TableCell>
                              <TableCell>
                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                  <DatePicker
                                    format="dd/MM/yyyy"
                                    value={newMedItem.startDate}
                                    onChange={(v) => setNewMedItem((prev) => ({ ...prev, startDate: v }))}
                                    slotProps={{ textField: { size: "small", fullWidth: true } }}
                                  />
                                </LocalizationProvider>
                              </TableCell>
                              <TableCell>
                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                  <DatePicker
                                    format="dd/MM/yyyy"
                                    disablePast
                                    minDate={(newMedItem.startDate && !isNaN(new Date(newMedItem.startDate).getTime())) ? new Date(newMedItem.startDate) : undefined}
                                    value={newMedItem.endDate}
                                    onChange={(v) => {
                                      setNewMedItem((prev) => ({
                                        ...prev,
                                        endDate: v,
                                        // Clear expiration date when refill date changes so user picks fresh
                                        medicationExpirationDate: null,
                                      }));
                                    }}
                                    slotProps={{ textField: { size: "small", fullWidth: true } }}
                                  />
                                </LocalizationProvider>
                              </TableCell>
                              <TableCell>
                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                  <DatePicker
                                    format="dd/MM/yyyy"
                                    value={newMedItem.medicationExpirationDate}
                                    referenceDate={newMedItem.endDate || undefined}
                                    minDate={newMedItem.endDate || undefined}
                                    disablePast
                                    onChange={(v) => setNewMedItem((prev) => ({ ...prev, medicationExpirationDate: v }))}
                                    slotProps={{ textField: { size: "small", fullWidth: true, placeholder: "Medication Expiration Date" } }}
                                  />
                                </LocalizationProvider>
                              </TableCell>
                              <TableCell align="center">
                                <Button
                                  variant="contained"
                                  size="small"
                                  startIcon={<AddIcon />}
                                  onClick={handleAddOrderItem}
                                  sx={{
                                    borderRadius: '8px',
                                    bgcolor: '#48CAE4',
                                    color: '#000000',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                    '&:hover': {
                                      bgcolor: '#3ab3cc',
                                      boxShadow: '0 6px 16px rgba(0,0,0,0.15)'
                                    }
                                  }}
                                >
                                  Add
                                </Button>
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                      {orderErrors.medicationItems && (
                        <FormHelperText error sx={{ mt: 1, ml: 1 }}>{orderErrors.medicationItems.message}</FormHelperText>
                      )}
                    </Grid>
                  </Grid>
                </form>
              </DialogContent>
              <DialogActions sx={{ p: 2 }}>
                <Button onClick={handleCloseOrderDialog} color="inherit">Cancel</Button>
                <Button
                  onClick={handleOrderSubmit(onSubmitOrder, (errors: any) => {
                    console.error("Form validation errors:", errors);
                    const errorList: string[] = [];
                    if (errors.residentId) errorList.push(`Resident: ${errors.residentId.message}`);
                    if (errors.prescribingPhysician) errorList.push(`Physician: ${errors.prescribingPhysician.message}`);
                    if (errors.pharmacyName) errorList.push(`Pharmacy Name: ${errors.pharmacyName.message}`);
                    if (errors.pharmacyEmail) errorList.push(`Pharmacy Email: ${errors.pharmacyEmail.message}`);
                    if (errors.prescriptionNumber) errorList.push(`Prescription No: ${errors.prescriptionNumber.message}`);
                    if (errors.prescriptionFile) errorList.push(`Prescription File: ${errors.prescriptionFile.message}`);
                    if (errors.medicationItems) {
                      if (Array.isArray(errors.medicationItems)) {
                        errors.medicationItems.forEach((itemErr: any, i: number) => {
                          if (itemErr) {
                            Object.values(itemErr).forEach((e: any) => {
                              if (e && e.message) errorList.push(`Medication ${i + 1}: ${e.message}`);
                            });
                          }
                        });
                      } else if (errors.medicationItems.message) {
                        errorList.push(`Medications: ${errors.medicationItems.message}`);
                      }
                    }
                    setOrderError(`Please fix the following errors: ${errorList.join(", ")}`);
                  })}
                  variant="contained"
                  disabled={false}
                  sx={{
                    borderRadius: '12px',
                    px: 3,
                    py: 1,
                    fontWeight: 'bold',
                    bgcolor: '#48CAE4',
                    color: '#000000',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    '&:hover': {
                      bgcolor: '#3ab3cc',
                      boxShadow: '0 6px 16px rgba(0,0,0,0.15)'
                    }
                  }}
                >
                  {isSavingOrder ? <CircularProgress size={24} color="inherit" /> : "Submit Order"}
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}
      </Box>
      <SignatureInput
        open={signatureModalOpen}
        onClose={() => {
          setSignatureModalOpen(false);
          setSignatureOrderId(null);
        }}
        onSave={handleSignatureSave}
        loading={signingLoading}
      />

      {/* Delete Document Confirmation Dialog */}
      <Dialog
        open={Boolean(deleteDocId)}
        onClose={() => !isDeletingDoc && setDeleteDocId(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "20px" } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, pb: 1, pt: 3, px: 3 }}>
          <Avatar sx={{ bgcolor: "#FEE2E2", color: "#DC2626", borderRadius: "10px" }}><DeleteIcon /></Avatar>
          <Typography fontWeight={800} fontSize="1rem">Confirm Delete</Typography>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 3, pt: 1 }}>
          <Typography variant="body2" sx={{ color: "#475569", lineHeight: 1.6 }}>
            Are you sure you want to delete this document?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 0 }}>
          <Button
            onClick={() => setDeleteDocId(null)}
            disabled={isDeletingDoc}
            sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600, color: "#475569" }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteDocument}
            variant="contained"
            disabled={isDeletingDoc}
            sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700, bgcolor: "#DC2626", "&:hover": { bgcolor: "#B91C1C" }, px: 3 }}
            startIcon={isDeletingDoc ? <CircularProgress size={20} color="inherit" /> : undefined}
          >
            {isDeletingDoc ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Confirmation Dialog */}
      <Dialog open={statusConfirmOpen} onClose={() => {
        setStatusConfirmOpen(false);
        setStatusReason("");
      }}>
        <DialogTitle>Confirm Status Change</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "#334155", mb: 1.5 }}>
            Are you sure you want to change the resident status from{" "}
            <strong>{formatStatusDisplay(residentBanner?.status || fullResidentData?.status || "Active")}</strong> to{" "}
            <strong>{formatStatusDisplay(pendingStatusUpdate)}</strong>?
          </Typography>
          {isStrictlyCaregiver && (
            <TextField
              fullWidth
              margin="dense"
              label="Reason for status change"
              required
              multiline
              rows={2}
              value={statusReason}
              onChange={(e) => setStatusReason(e.target.value)}
              sx={{
                '& .MuiFormLabel-asterisk': { color: '#DC2626 !important' },
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setStatusConfirmOpen(false);
            setStatusReason("");
          }}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            disabled={isStrictlyCaregiver && !statusReason.trim()}
            onClick={() => {
              setStatusConfirmOpen(false);
              if (pendingStatusUpdate) {
                handleStatusChange(pendingStatusUpdate, statusReason);
              }
              setStatusReason("");
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Medication Update Choice Dialog */}
      <Dialog
        open={openMedicationUpdateChoice}
        onClose={() => setOpenMedicationUpdateChoice(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            width: "100%",
            maxWidth: 450,
            overflow: "hidden"
          }
        }}
      >
        <DialogTitle sx={{
          pb: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'grey.50',
          position: 'relative'
        }}>
          <MedicationIcon color="primary" />
          <Typography variant="h6" fontWeight="700">Update Medication</Typography>
          <IconButton
            aria-label="close"
            onClick={() => setOpenMedicationUpdateChoice(false)}
            sx={{
              position: 'absolute',
              right: 12,
              top: 8,
              color: 'text.secondary',
            }}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ mt: 3, pb: 2 }}>
          <Typography variant="body1" color="text.primary" fontWeight="600">
            Do you want to discontinue and add a new record for this medication?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1, flexDirection: 'row', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            variant="contained"
            onClick={handleDiscontinueAndAddNew}
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1,
              textTransform: 'none',
              fontWeight: 600,
              minWidth: 100,
              boxShadow: theme.shadows[2]
            }}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
      {/* Delete Insurance Confirm Dialog */}
      <Dialog open={!!deleteInsurance} onClose={() => setDeleteInsurance(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "20px" } }}>
        <DialogTitle sx={{ pb: 1, pt: 3, px: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Avatar sx={{ bgcolor: "#FEE2E2", color: "#DC2626", borderRadius: "10px" }}><DeleteIcon /></Avatar>
            <Typography fontWeight={800} sx={{ color: "text.primary" }}>Delete Insurance</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ px: 3 }}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Are you sure you want to delete this insurance?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5, gap: 1 }}>
          <Button onClick={() => setDeleteInsurance(null)} sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600, color: "text.secondary" }}>Cancel</Button>
          <Button variant="contained" onClick={confirmDeleteInsurance} sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700, bgcolor: "#DC2626", "&:hover": { bgcolor: "#B91C1C" }, px: 3 }}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClinicalNotesPage;
